/**
 * Orquestador de Geocodificación Especializada v2.3 (P1.05)
 * 
 * Integra clasificación tipológica con geocodificadores especializados WFS,
 * datos locales DERA (offline), NGA (topónimos),
 * Catastro R2 V2 (Flatbush + uFuzzy + refcatMap), Overpass (OSM) y fallbacks genéricos 
 * (CDAU, CartoCiudad) para máxima precisión y cobertura en infraestructuras 
 * PTEL Andalucía.
 * 
 * ARCH-01 (2026-01-16): SINGLETON FIRST
 * - SingletonDetector ahora se ejecuta ANTES de TSFGeocoder
 * - Rationale: 65% municipios tienen solo 1 infraestructura por tipo
 * - Impacto esperado: +4-6 infraestructuras geocodificadas en Colomera
 * 
 * Cascada de geocodificación (10 niveles - SINGLETON FIRST · IAID-DECISION 17-may-2026):
 * L0. SINGLETON - Si count=1 en municipio → retorno directo 95%
 * L1. TSF - Fuzzy matching para múltiples candidatos
 * L2. LOCAL_DERA - Datos pre-descargados (10.653 features, offline)
 * L3. Geocodificador especializado WFS según tipología (DERA online)
 * L4. catastro_ptel - Referencias catastrales Pol/Parcela y RC explícita (CAT-12)
 * L5. NGA - Nomenclátor Geográfico Andalucía (topónimos)
 * L5b. CATASTRO_R2_V2 - 2.4M parcelas offline (Flatbush + uFuzzy + refcatMap)
 * L6. (retirado) IAID Instalaciones Deportivas — capa upstream inexistente, cobertura asumida por L3 WFSSports
 * L7. Overpass/OSM - OpenStreetMap
 * L8. CDAU - Callejero Digital Andalucía
 * L9. CartoCiudad - IGN (fallback universal)
 * L10. Nominatim - OSM (último recurso)
 * 
 * F021 Fase 2: Sistema offline-first con datos DERA locales
 * - health.geojson:     1,700 centros (CAP + hospitales)
 * - security.geojson:   1,282 (policía, bomberos, GC, emergencias)
 * - education.geojson:  6,725 centros educativos
 * - municipal.geojson:    785 ayuntamientos (100% municipios)
 * - energy.geojson:       161 parques eólicos
 * 
 * Cobertura esperada: ~90-95% con SINGLETON + cascada completa
 * 
 * @module services/geocoding
 * @version 2.4.0 - F1.07 Catastro V2 Integration
 */

import { InfrastructureClassifier } from '../classification/InfrastructureClassifier';
import { classifyLadder } from './orchestrator/cascadeTelemetry'; // T1: telemetría por nivel
import type { 
  GeocodingResult,
  ClassificationResult} from '../../types/infrastructure';
import { 
  InfrastructureType,
  ClassificationConfidence
} from '../../types/infrastructure';
import type { GeocodingResult as TypedResult } from '../../types/errors';
import type { GeocodedCoordinates } from './generic/CartoCiudadGeocoder';

import {
  type OrchestrationOptions,
  type OrchestrationResult,
  TYPOLOGY_FALLBACKS,
  FALLBACK_CONFIDENCE_PENALTY,
  // F.2: Spatial validation
  applyTRF06SpatialValidation,
  validateGeocodingResult,
  isValidAndaluciaCoordinate,
  VALIDATION_CONFIGS as _VALIDATION_CONFIGS,
  // F.3: Type mapping
  mapTypeToTipologia,
  mapTypeToLocalCategories,
  looksLikeToponym,
  isSportsFacility,
  isTelecomInfrastructure,
  isWatercourse,
  extractOperator,
  findMatchingTower,
  // PostGIS-01: validación espacial con polígono municipal real
  applyPostGISValidation,
  isPostGISAvailable,
} from './orchestrator';

// F3.07: Clasificador de geocodificabilidad pre-cascada
import { classifyForGeocoding } from './orchestrator/AddressClassifier';

// T1a: Umbral dual 90/85 según criticidad de tipología
import { isCriticalInfrastructure } from './conditionalValidation';

// SC.07: Scoring v2 bridge
import { enrichWithScoringV2 } from '../scoring/scoringBridge';

// F3.09: DERA Viaria R2 — centroide representativo para carreteras LINEAL_NOMBRADO
import { getDERAViariaR2Service } from './specialized/DERAViariaR2Service';
import { ROAD_CODE_IN_TEXT_REGEX } from '../../types/deraViaria';

// B3: DERA G10 R2 — infraestructura energética/medioambiental (L1.4)
import { getDERAG10R2Service } from './specialized/DERAG10R2Service';

// B4: DERA G3 R2 — infraestructura hidráulica (L1.5)
import { getDERAG3R2Service } from './specialized/DERAG3R2Service';

// B6: IAPH R2 — patrimonio cultural (L1.6)
import { getIAPHR2Service } from './specialized/IAPHR2Service';

// A4: CGPHA R2 — patrimonio histórico (L1.10)
import { getCGPHAR2Service } from './specialized/CGPHAR2Service';

// A4b: OBPH R2 — otros bienes patrimonio histórico (L1.11)
import { getOBPHR2Service } from './specialized/OBPHR2Service';

// A5: OSM Security R2 — seguridad desde OpenStreetMap (L1.12)
import { getOSMSecurityR2Service } from './specialized/OSMSecurityR2Service';

// A7: GC Facilities R2 — Guardia Civil datos abiertos (L1.14, T3.2)
import { getGCFacilitiesR2Service } from './specialized/GCFacilitiesR2Service';

// W2RF-1: DERA G12_37 R2 — puestos socorro playa (cluster F2 SEG ampliado oleada-2)
import { getDERAG12_37R2Service } from './specialized/DERAG12_37R2Service';

// W2RF-2: DERA G12_36 R2 — organizaciones humanitarias (cluster F2 SEG ampliado oleada-2)
import { getDERAG12_36R2Service } from './specialized/DERAG12_36R2Service';

// M2RF-1: DERA G12_38 R2 — puntos encuentro maremotos costa Andalucía (cluster F2 SEG ampliado oleada-2)
import { getDERAG12_38R2Service } from './specialized/DERAG12_38R2Service';

// A6: Edu Centers R2 — centros educativos Junta Andalucía (L1.13)
import { getEduCentersR2Service } from './specialized/EduCentersR2Service';

// Issue #14 A.NEW: Centroide municipal fallback (INER2Service)
import { getINER2Service } from './specialized/INER2Service';

// PROP-C (CENTROID-REFINE-01): mapeo tipología → usos catastrales para rescate pre-centroide
// TUN3 (7-may-2026): migrado a adaptador formal `tipologiaACodigoCatastro`.
// FUS1 (02-ago-2026): `infraTipologiaAPTEL` traduce el vocabulario DERA al PTEL;
// con ella, `TipologiaPTEL`/`TIPOLOGIAS_VALUES` dejan de usarse aquí.
import { tipologiaACodigoCatastro, infraTipologiaAPTEL } from '../../types/catastroR2';

// LocalDataService - L0 Geocoding offline (F021 Fase 2)
// B.4: SingletonDetector usa IndexedDB, pero LocalDataService se mantiene como fallback
import {
  searchLocal,
  isDataLoaded,
  loadLocalData,
  toGeocodingResult,
  // Fallback para cuando IndexedDB no está lista (tests, primera carga)
  countByType,
  getUniqueByType,
  getFeaturesByMunicipio,
  type InfrastructureCategory as _InfrastructureCategory,
} from '../../lib/LocalDataService';

// F023 Fase 1.4 - Desambiguación multi-campo
import {
  disambiguate,
  type GeocodingCandidate,
  type PTELRecord,
} from '../../lib/multiFieldStrategy';

// TSF-04: TSFGeocoder como L1 del cascade (después de Singleton - ARCH-01)
import {
  TSFGeocoder,
  type TSFGeocodingRequest,
  type TSFGeocodingResponse,
} from '../../lib/tsf';

// Geocodificadores especializados WFS
import type {
  // T1: NGA Local para búsqueda offline antes de WFS
  NGALocalGeocoder,
  // F1.07: Catastro V2 para refcat/dirección/fuzzy (2.4M parcelas - Flatbush + uFuzzy)
  CatastroGeocoderV2} from './specialized';
import {
  WFSHealthGeocoder,
  WFSCulturalGeocoder,
  WFSSecurityGeocoder,
  WFSHydraulicGeocoder,
  WFSEnergyGeocoder,
  WFSSportsGeocoder,
  WFSIndustrialGeocoder,
  // WAVE-1 DERA G12 — Maremotos (g12_38 · 715 features litorales · HX-CAPTURA-MAXIMAL)
  WFSMaremotosGeocoder,
  type MaremotosSearchOptions,
  // WAVE-2 DERA G12 — Socorro + Humanitarias (g12_37 + g12_36 · HX-CAPTURA-MAXIMAL · cluster F2 SEG ampliado oleada-2 rumbo 2)
  WFSPuestoSocorroGeocoder,
  type PuestoSocorroSearchOptions,
  WFSOrganizacionesHumanitariasGeocoder,
  type OrganizacionesHumanitariasSearchOptions,
  // Nuevos geocodificadores Fase B
  NGAGeocoder,
  getNGALocalGeocoder,
  OverpassGeocoder,
  // F3.TELECOM - Torres Comunicación Ene 2026
  TorresComGeocoder,
  // F3-HYDRAULIC - Hidráulicas Municipales Ene 2026
  HydraulicMunicipalGeocoder,
  // F3-CAUCES - Cauces hidrográficos lineales Ene 2026
  DERAWaterwaysGeocoder,
  // F3.T3.3: Tipos para distribución inteligente
  type TelecomInfrastructure,
  type DistributedGeocodingResult,
  // F2.T8 - Gasolineras MITECO Ene 2026
  GasolinerasGeocoder,
  // CAT-12: Catastro PTEL para referencias Pol/Parcela
  type WFSSearchOptions,
  getCatastroGeocoderV2,
  // P2.02: DERA R2 Geocoder v2 - carga por municipio (más eficiente)
  getDERAR2Geocoder,
  // P2.03: NGA R2 Geocoder (233K topónimos desde Cloudflare R2)
  geocodeWithNgaR2,
  // P3.04: CDAU R2 Geocoder (2.69M portales desde Cloudflare R2)
  geocodeWithCdauR2,
  // FOPT-02a: AGOL R2 Geocoder (36K+ features AGOL desde Cloudflare R2)
  geocodeWithAgolR2,
} from './specialized';

// F3.04: Normalización L1 para CDAU R2 (mejora match rate pre-geocodificación)
import { normalizeAddressL1 } from '../normalization/AddressNormalizerL1';
import { stripMunicipioSuffix } from '../normalization/AddressNormalizerL2';

// Geocodificadores genéricos (fallback)
import { 
  CartoCiudadGeocoder,
  CDAUGeocoder,
  CatastroService,
  IDEEGeocoder,
  NominatimGeocoder,
  // B-03: filtro morfología de dirección — evita falsos positivos en CartoCiudad
  hasAddressPattern,
  // NGA-PED: detección de pedanías en municipios plurinucleares
  looksLikePedania,
} from './generic';

// Validación por código INE (previene errores Colomera/Colomers)
import { 
  validarResultadoCartoCiudad,
  acceptCartoCiudadResult,
} from './ineValidator';
import { getCodigoINE } from '../../utils/codigosINEDerivados';
import { esCentroideMunicipal } from '../../utils/procedenciaVia'; // CTRN-G
import { hasCatastralPatternInFields } from './utils/extractCatastralPattern';

// Constantes geográficas centralizadas
// isWithinAndalucia: usado internamente por orchestrator/spatialValidation

// Validación Cruzada Multi-Fuente (v3.0)
import type {
  GeocodingSource,
  SourceResult,
  ValidationStatus,
} from './CrossValidator';
import {
  getCrossValidator,
} from './CrossValidator';

// F025: Extractor de direcciones normalizadas
import {
  extractStreetAddress,
  type AddressExtractionResult,
} from '../../utils/addressExtractor';

// F023 Fase 1 / B.4: SingletonDetector con BBDD local IndexedDB
// NOTA: getSingletonFeature, SingletonResult preparados para uso futuro
import {
  detectSingleton,
  getCandidatesByNombre,
} from '../../lib/localData/singletonDetector';
import { type InfraTipologia as _InfraTipologia } from '../../lib/localData/schemas';
import { isDatabaseReady } from '../../lib/localData/schemas';

// F023 Fase 2.1: Errores tipados para validación cruzada
// NOTA: CrossValidationError, DiscrepancyError, NetworkError, TimeoutError preparados para F023 Fase 2
import {
  wrapError,
  type GeocodingError,
} from '../../types/errors';

// B05: Tipos de validación (funciones importadas de ./orchestrator)
import type { INEValidationResult as _INEValidationResult } from '../validation/INEValidator';
import type { BoundaryValidationResult as _BoundaryValidationResult } from '../validation/MunicipalBoundaryValidator';
// B1b: mapper enum→unión lib (B1a) para construir el ctx supramunicipal en la costura.
import { mapInfraEnumToSupramunicipalType } from '../validation/MunicipalBoundaryValidator';

// F4.01: Motor de validación cruzada multi-fuente (lib/crossValidation)
import {
  crossValidateInfrastructure,
  type PrecomputedSourceResult,   // OPT-01
} from '../crossValidation/CrossValidationSourceFactory';
import { applyWfsFallbackValidation } from './wfsFallbackValidator';
import { CircuitBreakerManager } from './orchestrator/CircuitBreakerManager';
import { CIRCUIT_BREAKER_COOLDOWN_MS } from './orchestrator/types';
import { ServiceHealthChecker, type HealthCheckResult } from './orchestrator/ServiceHealthChecker';

// CB-A: Desambiguador espacial municipal
import {
  getMuniSpatialDisambiguator,
  type MuniSpatialDisambiguator,
  OUTSIDE_CONFIDENCE_PENALTY,
} from './spatial/MuniSpatialDisambiguator';

// MJ-04: Acceso directo al servicio CDAU R2 para consultar conteo de portales en caché
import { getCDAUR2Service } from '../cdau/CDAUR2Service';
import {
  getAddressVerifier,
  type AddressVerificationResult as _AddressVerificationResult,
} from './address/AddressVerifier';

// CB-E / CB-G: TypologyVoter — post-procesador de oleada2 (sin fetch adicional)
import { vote as typologyVote, extractNgaVote } from './classifier/TypologyVoter';
import { getProvinciaPorCodigoINE } from '@/lib/codigosINEUnificado';

// TRF-06: applySpatialValidation y VALIDATION_CONFIGS importados de ./orchestrator

// TRF-06: fromUtm usado internamente en orchestrator/spatialValidation

// TYPOLOGY_FALLBACKS y FALLBACK_CONFIDENCE_PENALTY importados de ./orchestrator

// Re-exportamos tipos para compatibilidad con imports existentes
export type { OrchestrationOptions, OrchestrationResult } from './orchestrator';

// ─────────────────────────────────────────────────────────────────────────────
// D.03 — CascadeStats: telemetría en memoria (O(1), sin persistencia R2)
// ─────────────────────────────────────────────────────────────────────────────
export interface CascadeStats {
  byLevel: Record<string, { hits: number; misses: number; skips: number; avgMs: number; lastXY: { x: number; y: number } | null }>;
  totalRequests: number;
  cacheHitRate: number; // NOTA T1: tasa de acierto agregada (hits/llamadas), NO solo caché — nombre histórico
  sessionStart: string; // ISO 8601
}

interface _LevelStats {
  hits: number;
  misses: number;
  skips: number;     // T1: nivel saltado (circuit breaker/guard) — NO es fallo
  totalMs: number;
  lastXY: { x: number; y: number } | null; // T1/DOP-T1-3: geometría del último acierto del nivel
}

const _cascadeStore = {
  byLevel: {} as Record<string, _LevelStats>,
  totalRequests: 0,
  cacheHits: 0,
  sessionStart: new Date().toISOString(),
};

/** Inicializa (si falta) y devuelve el acumulador de un nivel. @internal */
function _ensureLevel(level: string): _LevelStats {
  if (!_cascadeStore.byLevel[level]) {
    _cascadeStore.byLevel[level] = { hits: 0, misses: 0, skips: 0, totalMs: 0, lastXY: null };
  }
  return _cascadeStore.byLevel[level];
}

/**
 * T1: registra un evento por nivel (hit/miss/skip) + geometría opcional del acierto.
 * NO toca los contadores globales (totalRequests/cacheHits) — eso lo hace el llamador
 * una vez por llamada (recordCascadeLadder) o el wrapper _recordLevel (compat D.03).
 * @internal
 */
export function _recordLevelDetailed(
  level: string,
  status: 'hit' | 'miss' | 'skip',
  ms: number,
  xy?: { x: number; y: number } | null,
): void {
  const s = _ensureLevel(level);
  if (status === 'skip') { s.skips++; return; }  // skip: ni ms ni acierto
  if (status === 'hit') { s.hits++; if (xy) s.lastXY = xy; }
  else { s.misses++; }
  s.totalMs += ms;
}

/**
 * Registra un intento en un nivel de la cascada (firma histórica D.03).
 * Incrementa totalRequests con cada llamada. Wrapper de _recordLevelDetailed.
 * @internal Expuesto para tests unitarios D.03.
 */
export function _recordLevel(level: string, hit: boolean, ms: number): void {
  _recordLevelDetailed(level, hit ? 'hit' : 'miss', ms);
  if (hit) _cascadeStore.cacheHits++;
  _cascadeStore.totalRequests++;
}

/**
 * T1/DOP-T1-1 (opción B): deriva la escalera por nivel de una llamada a geocode()
 * a partir de la traza ya producida (attempts[] + geocoderUsed + geocoding.source),
 * sin tocar la lógica de la cascada. Registra ≤1 hit (nivel ganador) + misses + skips.
 * Concesión DoD: el ms-por-nivel no existe en el código (sin timing por fase); se
 * atribuye el ms TOTAL de la llamada al nivel ganador (los demás eventos con ms=0).
 * Incrementa los contadores globales UNA vez por llamada.
 */
export function recordCascadeLadder(
  result: { attempts?: string[]; geocoderUsed?: string | null; geocoding?: { x?: number; y?: number; source?: string } | null },
  totalMs: number,
): void {
  const attempts = result.attempts ?? [];
  const source = result.geocoding?.source ?? null;
  const { winnerLevel, ladder } = classifyLadder(attempts, result.geocoderUsed, source);
  const xy = (result.geocoding && typeof result.geocoding.x === 'number' && typeof result.geocoding.y === 'number')
    ? { x: result.geocoding.x, y: result.geocoding.y }
    : null;
  for (const e of ladder) {
    if (e.status === 'hit') _recordLevelDetailed(e.level, 'hit', totalMs, xy);
    else _recordLevelDetailed(e.level, e.status, 0);
  }
  if (winnerLevel !== 'none') _cascadeStore.cacheHits++;
  _cascadeStore.totalRequests++;
}

/** Expone estadísticas de cascada por nivel — D.03 */
export function getCascadeStats(): CascadeStats {
  const byLevel: CascadeStats['byLevel'] = {};
  for (const [lvl, s] of Object.entries(_cascadeStore.byLevel)) {
    const count = s.hits + s.misses;
    byLevel[lvl] = {
      hits: s.hits,
      misses: s.misses,
      skips: s.skips,
      avgMs: count > 0 ? Math.round(s.totalMs / count) : 0,
      lastXY: s.lastXY,
    };
  }
  const rate = _cascadeStore.totalRequests > 0
    ? _cascadeStore.cacheHits / _cascadeStore.totalRequests
    : 0;
  return {
    byLevel,
    totalRequests: _cascadeStore.totalRequests,
    cacheHitRate: Math.round(rate * 1000) / 1000,
    sessionStart: _cascadeStore.sessionStart,
  };
}

/** Reinicia todos los contadores — para tests y nueva sesión */
export function resetCascadeStats(): void {
  _cascadeStore.byLevel = {};
  _cascadeStore.totalRequests = 0;
  _cascadeStore.cacheHits = 0;
  _cascadeStore.sessionStart = new Date().toISOString();
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Orquestador principal de geocodificación v2.2 (ARCH-01)
 * 
 * Gestiona flujo completo con 10 niveles de cascada
 * SINGLETON FIRST: SingletonDetector se evalúa antes de TSFGeocoder
 * 
 * @example
 * ```typescript
 * const orchestrator = new GeocodingOrchestrator();
 * 
 * // Geocodificar una piscina municipal (cascada Sports + CartoCiudad)
 * const result = await orchestrator.geocode({
 *   name: 'Piscina Municipal',
 *   municipality: 'Colomera',
 *   province: 'Granada'
 * });
 * 
 * // Geocodificar antena en un cerro
 * const antena = await orchestrator.geocode({
 *   name: 'Antena Movistar',
 *   municipality: 'Colomera',
 *   province: 'Granada',
 *   address: 'Cerro Cementerio',  // Topónimo como referencia
 *   useNGA: true,    // Buscar topónimo
 *   useOverpass: true // Buscar antena en OSM
 * });
 * ```
 */
export class GeocodingOrchestrator {
  // Clasificador tipológico
  private classifier: InfrastructureClassifier;
  
  // Geocodificadores especializados WFS (Fase 1)
  private healthGeocoder: WFSHealthGeocoder;
  private culturalGeocoder: WFSCulturalGeocoder;
  private securityGeocoder: WFSSecurityGeocoder;
  private hydraulicGeocoder: WFSHydraulicGeocoder;
  
  // F3-HYDRAULIC: Fallback para hidráulicas municipales (NGA→OSM→Catastro)
  private hydraulicMunicipalGeocoder: HydraulicMunicipalGeocoder;
  
  // F3-CAUCES: Cauces hidrográficos lineales (ríos, ramblas, arroyos, barrancos)
  private waterwaysGeocoder: DERAWaterwaysGeocoder;
  private energyGeocoder: WFSEnergyGeocoder;
  private sportsGeocoder: WFSSportsGeocoder;
  private industrialGeocoder: WFSIndustrialGeocoder;
  // WAVE-1 DERA G12 — Maremotos (g12_38 · puntos encuentro litorales)
  private maremotosGeocoder: WFSMaremotosGeocoder;
  // WAVE-2 DERA G12 — Socorro + Humanitarias (g12_37 + g12_36 · cluster F2 SEG ampliado oleada-2 rumbo 2)
  private puestoSocorroGeocoder: WFSPuestoSocorroGeocoder;
  private organizacionesHumanitariasGeocoder: WFSOrganizacionesHumanitariasGeocoder;
  
  // Nuevos geocodificadores (Fase B)
  private ngaGeocoder: NGAGeocoder;
  // T1: NGA Local para búsqueda offline (233K+ topónimos)
  private ngaLocalGeocoder: NGALocalGeocoder;
  private overpassGeocoder: OverpassGeocoder;
  
  // F3.TELECOM - Torres Comunicación (Ene 2026)
  private torresComGeocoder: TorresComGeocoder;
  
  // F2.T8 - Gasolineras MITECO (Ene 2026)
  private gasolinerasGeocoder: GasolinerasGeocoder;
  
  // F1.07: Catastro V2 - singleton con Flatbush + uFuzzy + refcatMap (2.4M parcelas)
  private catastroGeocoder: CatastroGeocoderV2;
  
  // Geocodificadores genéricos (fallback)
  private cdauGeocoder: CDAUGeocoder;
  private cartoCiudadGeocoder: CartoCiudadGeocoder;
  private ideeGeocoder: IDEEGeocoder;
  private nominatimGeocoder: NominatimGeocoder;
  private catastroService: CatastroService;
  
  // Estado de datos locales (L0)
  private localDataPreloaded = false;
  
  // TSF-04: TSFGeocoder como nivel L1 (después de Singleton - ARCH-01)
  private tsfGeocoder: TSFGeocoder;

  // F9.01a-fix: Circuit breaker para servicios online (failover resiliente)
  private circuitBreaker: CircuitBreakerManager;
  // F9.01a-fix.2: Health check pre-batch
  private healthChecker: ServiceHealthChecker;

  // CB-A: Desambiguador espacial — rechaza candidatos fuera del municipio
  private muniSpatial: MuniSpatialDisambiguator;

  constructor() {
    // Inicializar clasificador
    this.classifier = new InfrastructureClassifier({
      strictMode: false,
      caseSensitive: false
    });

    // Inicializar geocodificadores especializados WFS
    this.healthGeocoder = new WFSHealthGeocoder();
    this.culturalGeocoder = new WFSCulturalGeocoder();
    this.securityGeocoder = new WFSSecurityGeocoder();
    this.hydraulicGeocoder = new WFSHydraulicGeocoder();
    // F3-HYDRAULIC: Fallback para depósitos, pozos, manantiales municipales
    this.hydraulicMunicipalGeocoder = new HydraulicMunicipalGeocoder();
    // F3-CAUCES: Cauces hidrográficos lineales (ríos, ramblas, arroyos, barrancos)
    this.waterwaysGeocoder = new DERAWaterwaysGeocoder();
    this.energyGeocoder = new WFSEnergyGeocoder();
    this.sportsGeocoder = new WFSSportsGeocoder();
    this.industrialGeocoder = new WFSIndustrialGeocoder();
    // WAVE-1 DERA G12 — Maremotos
    this.maremotosGeocoder = new WFSMaremotosGeocoder();
    this.puestoSocorroGeocoder = new WFSPuestoSocorroGeocoder();
    this.organizacionesHumanitariasGeocoder = new WFSOrganizacionesHumanitariasGeocoder();
    
    // Inicializar nuevos geocodificadores (Fase B)
    this.ngaGeocoder = new NGAGeocoder();
    // T1: NGA Local usa singleton de NGADataService
    this.ngaLocalGeocoder = getNGALocalGeocoder();
    this.overpassGeocoder = new OverpassGeocoder();
    
    // F3.TELECOM - Torres Comunicación (Ene 2026)
    this.torresComGeocoder = new TorresComGeocoder();
    
    // F2.T8 - Gasolineras MITECO (Ene 2026)
    this.gasolinerasGeocoder = new GasolinerasGeocoder();
    
    // F1.07: Catastro V2 - inicializado más abajo, tras circuitBreaker y
    // catastroService, para inyectar deps de fallback OVC (F1-R2-FIRST chat 1/2 V2).
    
    // Inicializar geocodificadores genéricos
    this.cdauGeocoder = new CDAUGeocoder();
    this.cartoCiudadGeocoder = new CartoCiudadGeocoder();
    this.ideeGeocoder = new IDEEGeocoder();
    this.nominatimGeocoder = new NominatimGeocoder();
    this.catastroService = new CatastroService();
    
    // TSF-04: Inicializar TSFGeocoder (L1 después de Singleton - ARCH-01)
    this.tsfGeocoder = new TSFGeocoder();

    // F9.01a-fix: Circuit breaker para servicios online (umbral 2 fallos, cooldown 5 min)
    // QA-03: halfOpenAfterMs 30 s → 300 s — Nominatim 429 dura varios minutos en producción
    // CB-FIX: failureThreshold 2 → 4 — evita corte prematuro en batch con fallos transitorios
    this.circuitBreaker = new CircuitBreakerManager({
      failureThreshold: 4,
      halfOpenAfterMs: CIRCUIT_BREAKER_COOLDOWN_MS,   // 300_000 ms (5 min) — types.ts
    });
    // F9.01a-fix.2: Health checker pre-batch
    this.healthChecker = new ServiceHealthChecker();

    // CB-A: Desambiguador espacial municipal (singleton compartido)
    this.muniSpatial = getMuniSpatialDisambiguator();

    // F1.07 + F1-R2-FIRST chat 1/2 V2: Catastro V2 — singleton con Flatbush +
    // uFuzzy + refcatMap (2,4 M parcelas). Inyectamos deps de fallback OVC
    // online aquí, después de que circuitBreaker y catastroService estén
    // construidos. La inyección es idempotente: si el singleton ya existe sin
    // deps (otros módulos lo crean al importar), `getCatastroGeocoderV2(deps)`
    // las fija sólo si aún no estaban; si ya estaban, no se sobreescriben.
    this.catastroGeocoder = getCatastroGeocoderV2({
      ovcService: this.catastroService,
      circuitBreaker: this.circuitBreaker,
    });
  }

  // mapTypeToTipologia: importado de ./orchestrator

  /**
   * DISAMV-REFINE-01 M1: Escalada WFS para tipologías críticas cuando
   * la desambiguación multi-campo devuelve confianza MEDIUM o LOW.
   * Solo actúa sobre HEALTH, POLICE y FIRE.
   * Devuelve null si la tipología no está en la lista o el geocodificador no resuelve.
   *
   * WECB 26-may: EDUCATION retirada de la escalada. La ruta principal
   * (`case EDUCATION` de `trySpecializedGeocoder`) ya usa `EduCentersR2Service`
   * (no WFS) tras deprecar `WFSEducationGeocoder` (CKAN muerto); una escalada
   * «WFS» para EDU ya no tiene sentido y reintroduciría el endpoint roto.
   */
  private async _tryWFSRescue(
    options: OrchestrationOptions,
    type: InfrastructureType,
  ): Promise<GeocodingResult | null> {
    const TIPOLOGIAS_WFS_RESCUE = new Set([
      InfrastructureType.HEALTH,
      InfrastructureType.POLICE,
      InfrastructureType.FIRE,
    ]);
    if (!TIPOLOGIAS_WFS_RESCUE.has(type)) return null;

    try {
      const searchOpts = {
        name: options.name,
        municipality: options.municipality,
        province: options.province,
        maxResults: 3,
      };
      if (type === InfrastructureType.HEALTH) {
        return await this.healthGeocoder.geocodeLegacy(searchOpts);
      } else {
        // POLICE y FIRE → securityGeocoder
        return await this.securityGeocoder.geocodeLegacy(searchOpts);
      }
    } catch {
      return null;
    }
  }

  /**
   * Precarga datos locales DERA para geocodificación offline (L0)
   * Llamar al inicio de la app para evitar latencia en primera búsqueda
   * 
   * @returns Stats de carga (total features, por categoría, tiempo)
   */
  public async preloadLocalData(): Promise<{
    totalFeatures: number;
    byCategory: Record<string, number>;
    loadTimeMs: number;
    municipiosIndexados: number;
  }> {
    if (this.localDataPreloaded && isDataLoaded()) {
      console.log('[GeocodingOrchestrator] Datos locales ya cargados');
      return loadLocalData();
    }
    
    console.log('[GeocodingOrchestrator] Precargando datos DERA locales...');
    const stats = await loadLocalData();
    this.localDataPreloaded = true;
    
    console.log(
      `[GeocodingOrchestrator] L0 ready: ${stats.totalFeatures} features ` +
      `(${stats.municipiosIndexados} municipios) en ${stats.loadTimeMs}ms`
    );
    
    return stats;
  }

  /**
   * Verifica si datos locales están disponibles
   */
  public isLocalDataReady(): boolean {
    return isDataLoaded();
  }

  /**
   * Helper: Extrae GeocodingResult legacy de TypedResult
   * Compatibilidad TD-009: Permite transición gradual a errores tipados
   */
  private extractLegacyResult(
    typedResult: TypedResult<GeocodedCoordinates>
  ): GeocodingResult | null {
    if (typedResult.success) {
      // Cast seguro: GeocodedCoordinates es compatible con GeocodingResult
      return typedResult.data as unknown as GeocodingResult;
    }
    return null;
  }

  /**
   * M4: Extrae una referencia catastral completa (14 o 20 chars DGC) de texto libre.
   * Detecta tanto la referencia sin subparcela (14 chars: 5N+1L+3N+5AN) como la
   * referencia completa con subparcela (20 chars: base + 4AN + 2L).
   * Usado para detectar RCs embebidas en el campo nombre antes de la cascada L0.
   *
   * @example
   * extractRefcatFromText("18087A008003980000AB Depósito Municipal") → "18087A008003980000AB"
   * extractRefcatFromText("RC: 18087A00300045 Balsa Riego")          → "18087A00300045"
   * extractRefcatFromText("Ayuntamiento de Colomera")                 → null
   */
  private extractRefcatFromText(text: string): string | null {
    if (!text) return null;
    const RC_FULL_PATTERN = /\b([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5}(?:[0-9A-Z]{4}[A-Z]{2})?)\b/i;
    const m = text.toUpperCase().match(RC_FULL_PATTERN);
    return m ? m[1] : null;
  }

  /**
   * SC.07: Finaliza un OrchestrationResult aplicando validación espacial + scoring v2.
   * Issue #14 A.NEW: Inyecta centroide municipal cuando sourcesConfirming === 0.
   *
   * Pipeline: [CB-B TruncSpatialValidator] → applyTRF06SpatialValidation → enrichWithScoringV2 → centroidFallback
   * Punto único de salida para todos los caminos exitosos del orquestador.
   *
   * CTRN (09-ago-2026): el núcleo se separó del envoltorio. `finalizeResult`
   * es ahora el envoltorio que, tras el núcleo, adjunta la parcela catastral
   * (`_adjuntarParcelaCatastral`). Se separó porque el núcleo tiene tres salidas
   * —centroide de Issue #14, PostGIS y la normal— y adjuntar en cada una las
   * multiplicaba por tres; envolver lo hace una sola vez y en un solo sitio.
   */
  private async finalizeResult(
    result: OrchestrationResult,
    municipality: string,
    codMun?: string,
    options?: OrchestrationOptions,
  ): Promise<OrchestrationResult> {
    const finalizado = await this._finalizeResultCore(result, municipality, codMun);
    return this._adjuntarParcelaCatastral(finalizado, options);
  }

  /**
   * CTRN · adjunta al resultado la parcela catastral que le corresponde, **gane
   * quien gane la cascada**, sin tocar la coordenada ganadora.
   *
   * Es el paso (2b) que `PAR1` (`cd74cdbe`, 31-jul) dejó a medias: aquel hizo
   * que la parcela dejara de tirarse a la basura; este hace que se pida. Hasta
   * hoy Catastro solo entraba si nadie había ganado antes —el gate
   * `_skipCatastroR2` del NIVEL L5 y el `!geocodingResult` del NIVEL L4—, de
   * modo que la parcela solo se ataba a los elementos que ya traían referencia
   * catastral escrita a mano en el documento: 7 de 220 medidos por `CATRV`,
   * cuando la sonda directa encontraba parcela para 126.
   *
   * Invariantes deliberadas:
   * - **No mueve la coordenada** (`DOP-CTRN-COORD`=(a)). Solo escribe
   *   `geocoding.catastroParcel`; `x`, `y`, `confidence`, `source` y
   *   `geocoderUsed` quedan intactos. No es un nivel más de la cascada.
   * - **No reordena la cascada** ni relaja los gates (`DOP-CTRN-SALTO`=(c)).
   *   Los NIVELES L4 y L5 siguen exactamente igual; esto ocurre después.
   * - **No ata por proximidad cruda.** Replica literalmente los criterios de
   *   búsqueda del NIVEL L5 (refcat, vía/número, nombre, polígono/parcela), que
   *   son las cuatro vías que `src/types/infrastructure.ts` declara fiables.
   *   `CCB1` midió que el vecino más próximo se equivoca en 6 de 8.
   * - **No baja el umbral.** 55, el mismo de L5: `CATRV` no halló ni una parcela
   *   por debajo (rango real 70-85), así que el umbral no descarta nada.
   */
  private async _adjuntarParcelaCatastral(
    res: OrchestrationResult,
    options?: OrchestrationOptions,
  ): Promise<OrchestrationResult> {
    // Sin opciones no hay criterios de búsqueda; con parcela ya atada, nada que
    // hacer (la trajo la cascada por vía catastral y es mejor que la de aquí).
    if (!options || !res.geocoding || res.geocoding.catastroParcel) return res;

    // CTRN-G (`OBS-GEO-CTRN-ATA-PARCELA-SOBRE-CENTROIDE`): si la coordenada es el
    // centroide del municipio, no hay ubicación a la que atar una parcela. Buscarla
    // por calle y pegarla al centro del pueblo sería un dato inventado que verían
    // P2 (`CatastroInlinePanel`) y `MatchingEngine.scoreCatastral`.
    // `DOP-CTRNG-2`=(a): solo la familia municipal; `DOP-CTRNG-3`=(b): una traza.
    if (esCentroideMunicipal(res.geocoderUsed, res.geocoding.source)) {
      console.log(
        `[CTRN] ⏭ centroide municipal (${res.geocoderUsed ?? '—'}): ` +
        `sin parcela para "${options.name ?? ''}"`
      );
      return res;
    }

    const codINE = options.codMun || getCodigoINE(options.municipality) || '';
    if (codINE.length !== 5) return res;
    if (!this.circuitBreaker.isAvailable('CATASTRO_R2')) return res;

    // Criterios de búsqueda: réplica literal del NIVEL L5, para que lo que se
    // adjunta sea exactamente lo que la sonda de `CATRV` midió disponible.
    const refcat = (options.address || options.name || '')
      .match(/\b([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5}(?:[0-9A-Z]{4}[A-Z]{2})?)\b/i)?.[1];
    const addressText = options.address || '';
    const via = addressText
      .match(/(?:c\/|calle|avda|av\.|plaza|pza|paseo)\s*([^,\d]+)/i)?.[1]?.trim();
    const numero = addressText.match(/,?\s*(?:n[°º]?\s*)?(\d+)/)?.[1];
    const hasPolParc = Boolean(options.poligonoCatastral || options.parcelaCatastral);
    if (!refcat && !via && !options.name && !hasPolParc) return res;

    const attempts = [...(res.attempts ?? []), 'catastro_r2:adjunta'];
    try {
      const hit = await this.catastroGeocoder.geocode({
        codINE,
        refcat,
        via,
        numero,
        name: options.name,
        polygon: options.poligonoCatastral,
        parcel: options.parcelaCatastral,
        minConfidence: 55,
        maxResults: 3,
      });
      if (hit?.catastroParcel) {
        console.log(
          `[CTRN] ✅ Parcela adjunta a "${options.name ?? ''}" ` +
          `(${hit.matchType ?? '—'}, conf=${hit.confidence}) sin mover la coordenada ` +
          `de ${res.geocoderUsed ?? '—'}`
        );
        return {
          ...res,
          attempts,
          geocoding: { ...res.geocoding, catastroParcel: hit.catastroParcel },
        };
      }
    } catch (err) {
      // Non-blocking: adjuntar la parcela es enriquecimiento, nunca bloquea.
      console.warn('[CTRN] Adjuntar parcela falló (non-blocking):', err);
    }
    return { ...res, attempts };
  }

  private async _finalizeResultCore(
    result: OrchestrationResult,
    municipality: string,
    codMun?: string,
  ): Promise<OrchestrationResult> {
    // CB-B TRUNC-SPATIAL: si la Y fue corregida por truncación (6 dígitos → 7 dígitos),
    // confirmar con el polígono municipal real antes de continuar el pipeline.
    // Se invoca sólo cuando result.truncatedY === true y hay codMun y geocoding.
    if (result.truncatedY && codMun && result.geocoding) {
      try {
        const { TruncSpatialValidator } = await import('./spatial/TruncSpatialValidator');
        const tsv = new TruncSpatialValidator();
        // Recuperar la parte truncada original: los últimos 6 dígitos del Y corregido
        // son equivalentes al valor que llegó al normalizador antes del prefijo.
        const correctedY = result.geocoding.y;
        const prefixDigits = Math.floor(correctedY / 1_000_000); // p.ej. 4
        const truncY = correctedY - prefixDigits * 1_000_000;    // p.ej. 091234
        const tsResult = await tsv.validate(truncY, result.geocoding.x, codMun);
        if (tsResult.best.correctedY !== correctedY) {
          console.log(
            `[CB-B] ✅ TruncY revalidada: ${correctedY} → ${tsResult.best.correctedY} ` +
            `(prefix=${tsResult.best.prefix}, mode=${tsResult.best.spatialMode}, ` +
            `confirmedByPolygon=${tsResult.confirmedByPolygon})`,
          );
          result = {
            ...result,
            geocoding: { ...result.geocoding, y: tsResult.best.correctedY },
          };
        } else {
          console.log(
            `[CB-B] ✅ TruncY confirmada sin cambio: ${correctedY} ` +
            `(mode=${tsResult.best.spatialMode}, confirmedByPolygon=${tsResult.confirmedByPolygon})`,
          );
        }
      } catch (err) {
        // Non-blocking: la validación espacial de Y truncada no debe bloquear el pipeline
        console.warn('[CB-B] TruncSpatialValidator error (non-blocking):', err);
      }
    }

    const validated = applyTRF06SpatialValidation(result, municipality);
    // B1b: cablear el scoring supramunicipal. Si la coord quedó fuera del polígono
    // municipal (isValid === false) y hay codMun (INE) + coords + clasificación,
    // construir el ctx supra (enum→unión lib vía mapInfraEnumToSupramunicipalType)
    // para que D3 aplique la banda [70-95] y finalScore emita SUPRAMUNICIPAL_CONFIRMADO.
    // isValid === undefined (PostGIS no disponible) ⇒ supra off (seguro).
    const outOfBounds = validated.spatialValidation?.isValid === false;
    const infraEnum = validated.classification?.type;
    const supra =
      outOfBounds && codMun && validated.geocoding && infraEnum !== undefined
        ? {
            x: validated.geocoding.x,
            y: validated.geocoding.y,
            codigoINE: codMun,
            infrastructureType: mapInfraEnumToSupramunicipalType(infraEnum),
            outOfBounds,
          }
        : undefined;
    const scored = enrichWithScoringV2(validated, supra);

    // F4.01: Validación cruzada multi-fuente post-geocodificación
    // Invoca crossValidateInfrastructure() con fuentes DERA/CDAU/NGA/CartoCiudad
    // para obtener scoring independiente y coordenada Huber robusta.
    if (scored.geocoding && codMun && scored.geocoding.matchedName) {
      try {
        const tipologia = mapTypeToTipologia(scored.classification?.type);

        // OPT-01: si CDAU ya geocodificó en la cascada (L8), reutilizar ese resultado
        // como fuente precomputada para evitar una segunda llamada HTTP en CrossValidation.
        const precomputed: PrecomputedSourceResult[] = [];
        if (scored.geocoderUsed === 'generic:cdau') {
          precomputed.push({
            sourceId: 'cdau_r2',
            coordinates: { x: scored.geocoding.x, y: scored.geocoding.y },
            matchedName: scored.geocoding.matchedName,
            confidence: scored.geocoding.confidence,
          });
        }

        const cvResult = await crossValidateInfrastructure({
          nombre: scored.geocoding.matchedName,
          codMun,
          tipologia: tipologia || undefined,
          municipio: municipality,
          // FIX M5: pasar dirección para que CDAU R2 la use en CrossValidation
          // (sin esto createCdauR2Source usa el nombre como fallback, no la dirección real)
          direccion: scored.geocoding.address || undefined,
          sourceOptions: { includeOnline: false, maxSources: 3 },
          timeoutMs: 8_000,
          precomputed: precomputed.length > 0 ? precomputed : undefined, // OPT-01
        });

        scored.libCrossValidation = cvResult;

        // Enriquecer D3 con datos del motor lib/crossValidation si mejora
        const confirmedCount = cvResult.sourceResults.filter(
          (sr) => sr.result !== null,
        ).length;
        if (confirmedCount > 0 && scored.scoreDetail) {
          const currentD3 = scored.scoreDetail.d3.sourcesConfirming;
          if (confirmedCount > currentD3) {
            scored.scoreDetail.d3.sourcesConfirming = confirmedCount;
          }
        }

        console.log(
          `[F4.01] ✅ CrossValidation: ${cvResult.recommendation} ` +
          `(score=${cvResult.compositeScore}, sources=${confirmedCount}, ` +
          `radius=${cvResult.clusterAnalysis?.radiusMeters?.toFixed(1) ?? '?'}m)`
        );
      } catch (err) {
        // Non-blocking: validación cruzada es enriquecimiento, no debe bloquear
        console.warn('[F4.01] CrossValidation error (non-blocking):', err);
      }
    }

    // PROP-C (CENTROID-REFINE-01): Catastro/CDAU scoped antes del centroide grosero
    // Intenta dos fuentes offline de mayor precisión (±5–50 m) antes de inyectar
    // el centroide municipal (±1–5 km, conf=22).
    //
    // PROP-C-FIX: prioridad invertida en infraName — originalName primero.
    // Antes: scored.geocoding?.matchedName tomaba prioridad, pero en infras con centroide M3
    // matchedName contiene el topónimo del municipio (ej. "Guadix"), no el nombre de la infra.
    // Evidencia BM.03 corpus v4: 116 N/O de Guadix con infraName="Guadix" → 0 resoluciones.
    // Fix: result.originalName (del documento original) es siempre la fuente más fiable.
    {
      const _propcSourcConf = scored.scoreDetail?.d3.sourcesConfirming;
      const _propcConf = scored.geocoding?.confidence ?? null;
      const _propcInfraName =
        result.originalName ??
        result.geocoding?.matchedName ??
        scored.geocoding?.matchedName ??
        '';
      const _propcEnters =
        _propcSourcConf === 0 && !!codMun &&
        (!scored.geocoding || (_propcConf !== null && _propcConf <= 22));
      console.log(
        `[PROP-C] audit: sourcesConfirming=${_propcSourcConf}, codMun=${codMun ?? 'null'}, ` +
        `geocoding.conf=${_propcConf ?? 'null'}, infraName="${_propcInfraName}", enters=${_propcEnters}`
      );
    }
    if (scored.scoreDetail?.d3.sourcesConfirming === 0 && codMun
        && (!scored.geocoding || scored.geocoding.confidence <= 22)) {
      // PROP-C-FIX: originalName tiene prioridad sobre matchedName.
      // matchedName en infras con centroide M3 contiene el municipio, no el nombre de la infra.
      const infraName =
        result.originalName ??
        result.geocoding?.matchedName ??
        scored.geocoding?.matchedName ??
        '';
      if (!infraName) {
        console.warn('[PROP-C] ⚠️ infraName vacío — skip Catastro R2 + CDAU R2 (sin nombre para buscar)');
      }
      const tipologia = mapTypeToTipologia(scored.classification?.type);

      // Intento 1 — Catastro R2 V2 (Flatbush, offline, ±5–30 m)
      if (infraName && this.circuitBreaker.isAvailable('CATASTRO_R2')) {
        try {
          const catResult = await this.catastroGeocoder.geocode({
            codINE: codMun,
            name: infraName,
            // FUS1 (02-ago-2026): la guarda anterior era
            // `TIPOLOGIAS_VALUES.includes(tipologia)` sobre el valor que devuelve
            // `mapTypeToTipologia`, que habla `InfraTipologia` y no `TipologiaPTEL`.
            // Seis de trece valores —SEGURIDAD, EMERGENCIA, PATRIMONIO, TRANSPORTE,
            // RESIDUOS, OTRO— no pasaban la guarda y caían a `usos: undefined`, o
            // sea, al filtro por uso apagado sin decirlo. Medido sobre Colomera:
            // la Policía Local se ataba a una vivienda a 16 m (`AUDIT_FUS1` §3-§4).
            usos: (() => {
              const ptel = infraTipologiaAPTEL(tipologia);
              return ptel ? tipologiaACodigoCatastro(ptel) : undefined;
            })(),
            minConfidence: 50,
            maxResults: 3,
          });
          if (catResult && catResult.confidence >= 50) {
            scored.geocoding = {
              x: catResult.x,
              y: catResult.y,
              confidence: catResult.confidence,
              source: 'catastro_r2',
              matchedName: infraName,
              address: catResult.address ?? undefined,
              // CTRN: PROP-C gana con Catastro y hasta hoy tiraba su propia
              // parcela al reconstruir el objeto. Misma especie que la OBS que
              // cierra esta sesión, un eslabón más abajo.
              ...(catResult.catastroParcel
                ? { catastroParcel: catResult.catastroParcel }
                : {}),
            };
            scored.geocoderUsed = 'catastro_r2:pre_centroide';
            console.log(`[PROP-C] ✅ Catastro pre-centroide: conf=${catResult.confidence}`);
          }
        } catch (err) {
          console.warn('[PROP-C] Catastro scoped error (non-blocking):', err);
        }
      }

      // Intento 2 — CDAU R2 si Catastro no resolvió (2,69M portales, ±10–50 m)
      if (!scored.geocoding && infraName && this.circuitBreaker.isAvailable('CDAU_R2')) {
        try {
          const cdauResult = await geocodeWithCdauR2(infraName, codMun);
          if (cdauResult && cdauResult.confidence >= 45) {
            scored.geocoding = {
              x: cdauResult.x,
              y: cdauResult.y,
              confidence: cdauResult.confidence,
              source: 'cdau_r2',
              matchedName: infraName,
              address: cdauResult.matchedAddress ?? undefined,
            };
            scored.geocoderUsed = 'cdau_r2:pre_centroide';
            console.log(`[PROP-C] ✅ CDAU pre-centroide: conf=${cdauResult.confidence}`);
          }
        } catch (err) {
          console.warn('[PROP-C] CDAU scoped error (non-blocking):', err);
        }
      }
    }
    // → continúa Issue #14 A.NEW sin modificar

    // Issue #14 A.NEW — Fallback centroide cuando ninguna fuente confirma
    if (scored.scoreDetail?.d3.sourcesConfirming === 0 && codMun) {
      try {
        const ineService = getINER2Service();
        // initialize() es no-op si ya está cargado
        await ineService.initialize();
        const centroide = ineService.getCentroide(codMun);
        if (centroide) {
          const municipioData = ineService.getByCodMun(codMun);
          const municipioName = municipioData?.nombre ?? municipality;
          console.log(
            `[Issue14-A] Centroide fallback ${codMun}: X=${centroide.x} Y=${centroide.y}`
          );
          return {
            ...scored,
            centroidSuggestion: {
              x: centroide.x,
              y: centroide.y,
              codMun,
              municipio: municipioName,
            },
          };
        }
      } catch (err) {
        // Silenciar: centroide es opcional, no debe bloquear el resultado principal
        console.warn('[Issue14-A] Error obteniendo centroide:', err);
      }
    }

    // PostGIS-01: validación espacial exacta con polígono municipal real.
    // Sustituye la validación centroide+radio (~92 % precisión) por ST_Contains (~99,9 %).
    // Requisito: VITE_POSTGIS_VALIDATION_ENABLED=true en .env.local + Neon configurado.
    // Non-blocking: cualquier error devuelve scored sin modificar.
    if (
      import.meta.env.VITE_POSTGIS_VALIDATION_ENABLED === 'true' &&
      isPostGISAvailable() &&
      scored.geocoding &&
      Number.isFinite(scored.geocoding.x) &&
      Number.isFinite(scored.geocoding.y) &&
      codMun
    ) {
      try {
        return await applyPostGISValidation(scored, {
          expectedCodIne: codMun,
          severity: 'standard',
        });
      } catch (err) {
        console.warn('[PostGIS-01] Error en validación espacial (non-blocking):', err);
      }
    }

    return scored;
  }

  /**
   * D.03: Punto de entrada público con telemetría de cascada.
   * Registra nivel, hit/miss y duración en _cascadeStore (O(1), en memoria).
   */
  public async geocode(options: OrchestrationOptions): Promise<OrchestrationResult> {
    const t0 = performance.now();

    // Respetar options.timeout si está definido (evita que tests / llamadas
    // con timeout corto queden bloqueados más allá del límite indicado)
    let result: OrchestrationResult;
    if (options.timeout && options.timeout > 0) {
      const timeoutResult = new Promise<OrchestrationResult>((resolve) =>
        setTimeout(() => resolve(enrichWithScoringV2({
          geocoding: null,
          classification: {
            type: InfrastructureType.GENERIC,
            confidence: ClassificationConfidence.NONE,
            keywords: [] as string[],
          },
          geocoderUsed: 'timeout',
          processingTime: options.timeout!,
          originalName: options.name,
          errors: [`Timeout: geocodificación cancelada tras ${options.timeout}ms`],
          attempts: [],
        })), options.timeout)
      );
      result = await Promise.race([this._geocodeCore(options), timeoutResult]);
    } else {
      result = await this._geocodeCore(options);
    }

    const ms = performance.now() - t0;
    // T1/DOP-T1-1(B): registro per-nivel de la escalera (reemplaza el registro único del verdicto)
    recordCascadeLadder(result, ms);
    return result;
  }

  /**
   * Geocodifica una infraestructura usando clasificación + cascada completa
   * 
   * ARCH-01: SINGLETON FIRST
   * Orden de evaluación: Singleton → TSF → LOCAL_DERA → WFS → Fallbacks
   */
  private async _geocodeCore(options: OrchestrationOptions): Promise<OrchestrationResult> {
    const startTime = Date.now();
    const errors: string[] = [];
    const attempts: string[] = [];
    const servicesSkipped: string[] = [];

    // Opciones por defecto para nuevos geocodificadores
    // IEP-06: addressType ajusta flags antes de entrar en cascada.
    // Regla: addressType añade activaciones; nunca sobreescribe un override
    // explícito del llamador (ej. useNGA=false explícito se respeta siempre).
    const useNGA = options.addressType === 'toponym'
      ? options.useNGA !== false   // toponym: NGA activo salvo override explícito false
      : options.useNGA !== false;  // resto: comportamiento sin cambio
    // useIAID: flag legacy preservado en API pública (types.ts:108) por compatibilidad
    // con callers (useAutoGeocoding, e2e, benchmarks, iep06). Tras IAID-DECISION 17-may-2026
    // NIVEL 3 IAID desaparece y este flag queda como no-op interno.
    // OBS-FT-IAID-FLAG-DEPRECATED-CLEANUP sev D ~30 min difiere la limpieza completa.
    const _useIAID = options.addressType === 'sports'
      ? true
      : options.useIAID !== false;
    const useOverpass = options.useOverpass !== false;
    // IEP-06: hint para CartoCiudad cuando la dirección es tipo carretera
    const _iep06RoadHint = options.addressType === 'road';

    try {
      // Paso 1: Clasificar tipología (o usar tipo forzado)
      // FIX-GEO-002: Usar declaredType del documento como hint para clasificación
      // El nombre puede ser de persona/contacto, pero tipo del documento es confiable
      let classification: ClassificationResult;
      if (options.forceType) {
        classification = { 
          type: options.forceType, 
          confidence: ClassificationConfidence.HIGH,
          keywords: [] as string[]
        };
      } else {
        // Clasificar usando nombre Y tipología declarada del documento
        const classificationInput = options.declaredType 
          ? `${options.declaredType} ${options.name}` 
          : options.name;
        classification = this.classifier.classify(classificationInput);
      }

      // ===== PASO 1.5: PRE-PROCESAR DIRECCIÓN CON F025 AddressExtractor =====
      // Normalizar dirección antes de geocodificar para mejorar match rate
      let addressExtractionResult: AddressExtractionResult | null = null;
      const originalAddress = options.address;
      
      if (options.address && options.address.trim().length > 0) {
        attempts.push('f025_address_extraction');
        try {
          addressExtractionResult = extractStreetAddress(
            options.address,
            options.municipality
          );
          
          if (addressExtractionResult.address) {
            // Usar dirección normalizada para el resto del flujo
            options.address = addressExtractionResult.address;
            console.log(
              `[F025] ✅ Dirección normalizada: "${originalAddress}" → "${addressExtractionResult.address}" ` +
              `(confianza: ${addressExtractionResult.confidence}%)`
            );
          } else {
            console.log(
              `[F025] ⚠️ Dirección no geocodificable: "${originalAddress}" ` +
              `(razón: ${addressExtractionResult.reason})`
            );
          }
        } catch (err) {
          errors.push(`F025_EXTRACT: ${err}`);
          console.warn('[F025] Error en extracción de dirección:', err);
        }
      }

      // ===== PASO 1.7: F3.07 CLASIFICACIÓN DE GEOCODIFICABILIDAD =====
      // Determina si la infraestructura merece entrar en la cascada L0-L5.
      // Placeholders y redes genéricas se descartan aquí → 0 llamadas API.
      const geocodabilityResult = classifyForGeocoding(
        options.address,
        options.name,
        options.declaredType,
      );

      if (geocodabilityResult.skipCascade) {
        console.log(
          `[F3.07] ⏭️ Cascada omitida: ${geocodabilityResult.category} — ${geocodabilityResult.reason}`
        );
        return enrichWithScoringV2({
          geocoding: null,
          classification,
          geocoderUsed: 'skipped:' + geocodabilityResult.category.toLowerCase(),
          processingTime: Date.now() - startTime,
          originalName: options.name,
          errors: [],
          attempts: ['f307_classify'],
          geocodabilityCategory: geocodabilityResult.category,
          geocodabilityReason: geocodabilityResult.reason,
        });
      }

      // ===== PASO 1.75: L3-3 SOCIAL_SIN_DATOS =====
      // AddressClassifier clasificó la infra como social sin dirección.
      // No se hace skip: continúa a la cascada normal con case SOCIAL (L3-1).
      if (geocodabilityResult.category === 'SOCIAL_SIN_DATOS') {
        console.log(
          `[L3-3] 🏠 Infra social sin dirección: "${options.name}" → cascada SOCIAL`
        );
        // Sin modificación de flujo — case SOCIAL gestiona AGOL + centroide.
      }

      // Paso 2: Preparar opciones de búsqueda
      const _searchOptions: WFSSearchOptions = {
        name: options.name,
        municipality: options.municipality,
        province: options.province,
        maxResults: 10
      };

      let geocodingResult: GeocodingResult | null = null;
      let geocoderUsed = 'none';

      // ===== M4: RC CATASTRAL EN CAMPO NOMBRE → R2-FIRST + OVC FALLBACK =====
      // V2 (F1-R2-FIRST chat 1/2, 8-may-2026): R2 primero, OVC sólo si miss +
      // RC explícita + circuit-breaker cerrado. Conserva conf≥70 para OVC
      // (DOP1-B). Impacto histórico del bloque [M4]: 5–15 infras N/O →
      // GEOCODED (depósitos, balsas, fincas rurales). El R2-first reduce las
      // llamadas online a un mínimo: sólo RCs creadas tras el snapshot
      // 12-mar-2026 que aún no figuran en R2.
      if (!geocodingResult && options.name) {
        const rcInName = this.extractRefcatFromText(options.name);
        if (rcInName) {
          attempts.push('catastro_r2_first:name_rc');
          try {
            const codMun = options.codMun ?? '';
            if (codMun) {
              const r2firstRes = await this.catastroGeocoder.geocodeWithOVCFallback(
                {
                  codINE: codMun,
                  refcat: rcInName,
                  name: options.name,
                  municipality: options.municipality,
                  province: options.province,
                },
                codMun,
              );
              if (r2firstRes) {
                geocodingResult = r2firstRes;
                // El source distingue R2 ('catastro_r2_v2') vs OVC ('catastro_api')
                geocoderUsed = r2firstRes.source === 'catastro_api'
                  ? 'catastro_api:name_rc_r2_fallback'
                  : 'catastro_r2_v2:name_rc';
                console.log(
                  `[M4] ✅ RC en nombre resuelta vía ${geocoderUsed}: ` +
                  `${rcInName} → conf=${r2firstRes.confidence}`,
                );
              }
            }
          } catch (err) {
            errors.push(`M4_RC_NAME: ${err}`);
          }
        }
      }

      // ===== CATANT: POLÍGONO/PARCELA DEL PARSER → CATASTRO R2 ANTES DE LA CASCADA =====
      // OBS-GEO-FILA-HIDRAULICA-AGOTA-LOS-10-S-EN-OVERPASS-ANTES-DE-LLEGAR-A-CATASTRO
      // (B, 24-sep-2026). pol/parc solo los puebla el parser (IEP-08) ante un patrón
      // catastral explícito («Pol. 8 P-159»): son la misma clase de evidencia que la
      // RC del bloque M4 de arriba, y la parcela DGC es la fuente de manual para una
      // rústica. Hasta hoy esa evidencia esperaba al NIVEL L5 detrás de la oleada
      // paralela y de L3, y en la tipología hidráulica eso son dos consultas a
      // Overpass por fila (`HydraulicMunicipalGeocoder.searchOSM`): con Overpass a
      // 8-12 s el tope de 10 s de `useAutoGeocoding` vencía antes de que Catastro
      // (2 ms, sin frío) hablara [ejecutado, `.ptel/p0_depagua_20260924/AUDIT_DEPAGUA.md` §3].
      // DOP-1 (a-i): retorno directo, misma plantilla que F3.09 y la oleada 1.
      // DOP-2 (a): solo `matchType === 'polygon_parcel'` cierra aquí; null, un fuzzy
      // por nombre o un error dejan la cascada intacta (L5 y CATPP-BC siguen aguas abajo).
      if (!geocodingResult && (options.poligonoCatastral || options.parcelaCatastral)) {
        const codINE = options.codMun || getCodigoINE(options.municipality) || '';
        if (codINE.length === 5 && this.circuitBreaker.isAvailable('CATASTRO_R2')) {
          attempts.push('catastro_r2_first:pol_parc');
          try {
            const hit = await this.catastroGeocoder.geocode({
              codINE,
              name: options.name,
              polygon: options.poligonoCatastral,
              parcel: options.parcelaCatastral,
              minConfidence: 55,
              maxResults: 3,
            });
            if (hit && hit.matchType === 'polygon_parcel' && hit.confidence >= 55) {
              geocodingResult = hit;
              geocoderUsed = 'catastro_r2:pol_parc';
              console.log(
                `[CATANT] ✅ Parcela DGC por polígono/parcela antes de la cascada: ` +
                `pol ${options.poligonoCatastral ?? '—'} / parc ${options.parcelaCatastral ?? '—'} ` +
                `→ conf=${hit.confidence}`,
              );
              const polParcResult: OrchestrationResult = {
                geocoding: geocodingResult,
                classification,
                geocoderUsed,
                processingTime: Date.now() - startTime,
                errors,
                attempts,
                originalName: options.name,
                crossValidationScore: hit.confidence,
                validationStatus: 'CADASTRAL_AUTHORITATIVE',
                discrepancyMeters: null,
                requiresManualReview: false,
                reviewReason: 'Parcela catastral DGC autoritativa (polígono/parcela); resuelta antes de la cascada',
                sourcesConsulted: [{
                  source: hit.source,
                  x: hit.x,
                  y: hit.y,
                  confidence: hit.confidence,
                  responseTimeMs: Date.now() - startTime,
                }],
                geocodabilityCategory: geocodabilityResult.category,
                geocodabilityReason: geocodabilityResult.reason,
              };
              return this.finalizeResult(polParcResult, options.municipality, options.codMun, options);
            }
          } catch (err) {
            errors.push(`CATANT_POL_PARC: ${err}`);
          }
        }
      }

      // ===== PASO 1.8: F3.09 DERA VIARIA — LINEAL_NOMBRADO vial =====
      // Si F3.07 clasificó como LINEAL_NOMBRADO y el nombre contiene
      // un código de carretera (A-348, AL-3400, N-340…), resolver
      // con DERAViariaR2Service antes de entrar en la cascada general.
      // Cauces hidrográficos (ríos, ramblas) se delegan a la cascada
      // normal que ya usa waterwaysGeocoder/NGA.
      if (geocodabilityResult.category === 'LINEAL_NOMBRADO') {
        const queryText = options.name ?? options.address ?? '';
        const hasRoadCode = ROAD_CODE_IN_TEXT_REGEX.test(queryText);

        if (hasRoadCode) {
          attempts.push('dera_viaria_r2');
          try {
            const viariaService = getDERAViariaR2Service();
            await viariaService.ensureLoaded();

            // Intentar por código primero, luego por nombre
            let viariaResults = await viariaService.searchByCode({
              code: queryText.match(ROAD_CODE_IN_TEXT_REGEX)?.[1] ?? '',
              codINE: options.codMun,
              maxResults: 3,
            });

            // Fallback a búsqueda por nombre si código no resolvió
            if (viariaResults.length === 0) {
              viariaResults = await viariaService.searchByName({
                name: queryText,
                codINE: options.codMun,
                minConfidence: 50,
                maxResults: 3,
              });
            }

            if (viariaResults.length > 0) {
              const best = viariaResults[0];
              geocodingResult = viariaService.toGeocodingResult(best, queryText);
              geocoderUsed = `dera_viaria:${best.matchType}`;

              console.log(
                `[F3.09] ✅ Carretera resuelta: "${geocodingResult.matchedName}" ` +
                `(${geocodingResult.confidence}% conf, ${best.matchType})`
              );

              const processingTime = Date.now() - startTime;
              const viariaOrchResult: OrchestrationResult = {
                geocoding: geocodingResult,
                classification,
                geocoderUsed,
                processingTime,
                errors,
                attempts,
                originalName: options.name,
                geocodabilityCategory: 'LINEAL_NOMBRADO',
                geocodabilityReason: geocodabilityResult.reason,
              };
              return this.finalizeResult(viariaOrchResult, options.municipality, options.codMun, options);
            }

            // AL51-FIX: si viaria no resolvió porque el dataset no cargó
            // (fallo transitorio R2 con reintento agotado), hacerlo visible
            // en la columna L del export en vez de caer en silencio.
            const viariaLoadError = viariaService.getLoadError();
            if (viariaLoadError) {
              errors.push(`DERA_VIARIA_R2: dataset no cargado (${viariaLoadError})`);
            }

            console.log(`[F3.09] ⚠️ Carretera no resuelta por DERA viaria, cascada normal`);
          } catch (err) {
            errors.push(`DERA_VIARIA_R2: ${err}`);
            console.warn('[F3.09] Error en DERA Viaria R2:', err);
          }
        }
        // Si no es carretera (río, gasoducto…) o no resolvió → continuar cascada normal
      }

      // ===== ARCH-01: SINGLETON FIRST - Evaluación al inicio de cascada =====
      // CRÍTICO: SingletonDetector se ejecuta PRIMERO (antes de TSFGeocoder)
      // Rationale: 65% municipios tienen solo 1 infraestructura por tipo → match directo 95%
      // Diagnóstico Colomera reveló pérdida de +4-6 geocodificaciones por orden incorrecto
      // ===== B.4: SINGLETON DETECTOR CON BBDD LOCAL (IndexedDB) =====
      // Nivel L0_LOCAL: Consulta BBDD local antes de cualquier llamada WFS
      // El 65% de municipios tiene solo 1 infraestructura por tipo → match directo
      const useLocalData = options.useLocalData !== false;

      // ===== FOPT-01: EJECUCIÓN ESPECULATIVA OLEADA 1 =====
      // Lanzar TSF y LOCAL_DERA en paralelo ANTES de evaluar Singleton.
      // Si Singleton resuelve temprano (65% de los casos), las promesas se
      // descartan sin coste (garbage collected). Si no resuelve, TSF y
      // LOCAL_DERA ya tienen resultados listos → ahorro 100-300ms.
      const oleada1UseTSF = useLocalData && !!options.codMun;

      const speculativeTSF: Promise<TSFGeocodingResponse | null> = oleada1UseTSF
        ? this.tsfGeocoder.geocode({
            nombre: options.name,
            tipo: classification.type as unknown as TSFGeocodingRequest['tipo'],
            codMunicipio: options.codMun!,
          }).catch((err) => {
            console.warn('[FOPT-01] TSF speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      const oleada1LocalCategories = mapTypeToLocalCategories(classification.type);
      const speculativeLocalDERA = (useLocalData && !!options.codMun)
        ? searchLocal({
            nombre: options.name,
            codMun: options.codMun,
            municipio: options.municipality,
            provincia: options.province,
            categorias: oleada1LocalCategories,
            threshold: 0.35,
          }).catch((err) => {
            console.warn('[FOPT-01] LOCAL_DERA speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== FOPT-02: EJECUCIÓN ESPECULATIVA OLEADA 2 (R2) =====
      // Lanzar DERA_R2, NGA_R2 y CDAU_R2 en paralelo ANTES de evaluar Singleton.
      // Si Oleada 1 resuelve (65-70% de los casos), las promesas se descartan.
      // Si no resuelve, los datos R2 ya están listos → ahorro 300-800ms.
      const oleada2HasCodMun = !!options.codMun;
      const oleada2Tipologia = mapTypeToTipologia(classification.type);

      // M5: prefiltro subtipo ELECTRICO para transformadores — evita 20+ candidatos con gap≈0
      const esTransformadorElectrico =
        oleada2Tipologia === 'ENERGIA' &&
        /transform/i.test(options.name ?? '');

      // OPT-1: guards de tipología para 8 fuentes especulativas (bloque FOPT-02)
      const oleada2IsEnergy    = oleada2Tipologia === 'ENERGIA';
      const oleada2IsResiduos  = oleada2Tipologia === 'RESIDUOS';
      const oleada2IsHydraulic = oleada2Tipologia === 'HIDRAULICO';
      const oleada2IsHeritage  = oleada2Tipologia === 'PATRIMONIO';
      const oleada2IsSecurity  = oleada2Tipologia === 'SEGURIDAD' || oleada2Tipologia === 'EMERGENCIA';
      const oleada2IsEducation = oleada2Tipologia === 'EDUCATIVO';

      const speculativeDeraR2 = (oleada2HasCodMun && this.circuitBreaker.isAvailable('DERA_R2'))
        ? getDERAR2Geocoder().geocode({
            codINE: options.codMun!,
            name: options.name,
            tipologia: oleada2Tipologia || undefined,
            subtipo: esTransformadorElectrico ? 'ELECTRICO' : undefined, // M5
          }).catch((err) => {
            this.circuitBreaker.recordFailure('DERA_R2');
            console.warn('[FOPT-02] DERA_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      const speculativeNgaR2 = (oleada2HasCodMun && useNGA && this.circuitBreaker.isAvailable('NGA_R2'))
        ? geocodeWithNgaR2(options.name, { codINE: options.codMun! })
          .catch((err) => {
            this.circuitBreaker.recordFailure('NGA_R2');
            console.warn('[FOPT-02] NGA_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // CDAU_R2: pre-computar inputs síncronos para lanzamiento especulativo
      const oleada2CdauINE = options.codMun || getCodigoINE(options.municipality);
      const oleada2CdauRaw = options.address || options.name || '';
      const oleada2CdauL1 = normalizeAddressL1(oleada2CdauRaw);
      const oleada2CdauL1Str = oleada2CdauL1.normalized || oleada2CdauRaw;
      // GEO-STR-01: strip municipio/provincia del sufijo antes de la query CDAU-WFS
      const oleada2CdauNormalized = stripMunicipioSuffix(
        oleada2CdauL1Str,
        options.municipality || ''
      );
      const oleada2CdauNumMatch = oleada2CdauRaw.match(
        /(?:n[°º]?\s*)?(\d+)\s*(?:[-/]?\s*\d*\s*)?(?:BIS|BAJO|DCHA?|IZDA?|[A-Z])?\s*(?:,|$)/i
      );
      const oleada2CdauNumero = oleada2CdauNumMatch ? parseInt(oleada2CdauNumMatch[1], 10) : undefined;

      const speculativeCdauR2 = (oleada2CdauINE && this.circuitBreaker.isAvailable('CDAU_R2'))
        ? geocodeWithCdauR2(
            oleada2CdauNormalized,
            oleada2CdauINE,
            oleada2CdauNumero ? { numero: oleada2CdauNumero } : undefined
          ).catch((err) => {
            this.circuitBreaker.recordFailure('CDAU_R2');
            console.warn('[FOPT-02] CDAU_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== FOPT-02a: EJECUCIÓN ESPECULATIVA AGOL R2 =====
      // AGOL contiene 36K+ features complementarios (patrimonio, servicios,
      // transporte, hidrografía, energía, tejido económico, sistema urbano).
      // Lanzar en paralelo con los demás R2 para máxima cobertura.
      const speculativeAgolR2 = (oleada2HasCodMun && this.circuitBreaker.isAvailable('AGOL_R2'))
        ? geocodeWithAgolR2(options.name, { codINE: options.codMun! })
          .catch((err) => {
            this.circuitBreaker.recordFailure('AGOL_R2');
            console.warn('[FOPT-02a] AGOL_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== B3: EJECUCIÓN ESPECULATIVA DERA G10 R2 =====
      // DERA G10: ~8.300 features energía/medioambiente (EDAR, ETAP,
      // subestaciones, plantas solares, eólicos, RSU, puntos limpios…).
      // Fuzzy match por nombre sin filtro subtipo (datos por municipio < 20).
      const speculativeDeraG10R2 = (oleada2HasCodMun && (oleada2IsEnergy || oleada2IsResiduos) && this.circuitBreaker.isAvailable('DERA_G10_R2'))
        ? getDERAG10R2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('DERA_G10_R2');
            console.warn('[B3] DERA_G10_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== B4: EJECUCIÓN ESPECULATIVA DERA G3 R2 =====
      // DERA G3: ~3.500 features hidráulicas (manantiales, captaciones,
      // depósitos, estaciones de aforo).
      // Fuzzy match por nombre sin filtro subtipo.
      const speculativeDeraG3R2 = (oleada2HasCodMun && oleada2IsHydraulic && this.circuitBreaker.isAvailable('DERA_G3_R2'))
        ? getDERAG3R2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('DERA_G3_R2');
            console.warn('[B4] DERA_G3_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== B6: EJECUCIÓN ESPECULATIVA IAPH R2 =====
      // IAPH: ~25.000 features patrimonio cultural (706 municipios).
      // Fuzzy match por nombre usando tokens pre-normalizados.
      const speculativeIaphR2 = (oleada2HasCodMun && oleada2IsHeritage && this.circuitBreaker.isAvailable('IAPH_R2'))
        ? getIAPHR2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('IAPH_R2');
            console.warn('[B6] IAPH_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== A4: EJECUCIÓN ESPECULATIVA CGPHA R2 =====
      // CGPHA: ~18.478 features patrimonio histórico (730 municipios).
      // Fuzzy match por nombre usando tokens pre-normalizados.
      const speculativeCgphaR2 = (oleada2HasCodMun && oleada2IsHeritage && this.circuitBreaker.isAvailable('CGPHA_R2'))
        ? getCGPHAR2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('CGPHA_R2');
            console.warn('[A4] CGPHA_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== A4b: EJECUCIÓN ESPECULATIVA OBPH R2 =====
      // OBPH: ~8.305 features patrimonio histórico no catalogado (672 municipios).
      // Complementa a CGPHA con bienes no protegidos.
      const speculativeObphR2 = (oleada2HasCodMun && oleada2IsHeritage && this.circuitBreaker.isAvailable('OBPH_R2'))
        ? getOBPHR2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('OBPH_R2');
            console.warn('[A4b] OBPH_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== A5: EJECUCIÓN ESPECULATIVA OSM Security R2 =====
      // OSM Security: ~791 features seguridad (420 municipios).
      // Fuzzy match por tokens pre-computados. Solo para tipología SEGURIDAD.
      const speculativeOsmSecurityR2 = (oleada2HasCodMun && oleada2IsSecurity && this.circuitBreaker.isAvailable('OSM_SECURITY_R2'))
        ? getOSMSecurityR2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('OSM_SECURITY_R2');
            console.warn('[A5] OSM_SECURITY_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== A7: EJECUCIÓN ESPECULATIVA GC Facilities R2 (T3.2) =====
      // Guardia Civil: 629 instalaciones en 413 municipios — datos abiertos.
      // Fuzzy match por tokens pre-computados. Solo para tipología SEGURIDAD.
      const speculativeGcFacilitiesR2 = (oleada2HasCodMun && oleada2IsSecurity && this.circuitBreaker.isAvailable('GC_FACILITIES_R2'))
        ? getGCFacilitiesR2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('GC_FACILITIES_R2');
            console.warn('[A7] GC_FACILITIES_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== W2RF-1: EJECUCIÓN ESPECULATIVA DERA G12_37 Puestos Socorro R2 =====
      // DERA G12_37: 533 puestos socorro playa Andalucía (47 INE litorales).
      // Decreto 197/2024 — Planes Seguridad y Salvamento Playas.
      // Solo para tipología SEGURIDAD/EMERGENCIA. Cluster F2 SEG ampliado oleada-2.
      const speculativeDeraG12_37R2 = (oleada2HasCodMun && oleada2IsSecurity && this.circuitBreaker.isAvailable('DERA_G12_37_R2'))
        ? getDERAG12_37R2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('DERA_G12_37_R2');
            console.warn('[W2RF-1] DERA_G12_37_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== W2RF-2: EJECUCIÓN ESPECULATIVA DERA G12_36 Organizaciones Humanitarias R2 =====
      // DERA G12_36: 107 organizaciones humanitarias Andalucía (Cruz Roja, ONG, PC).
      // Solo para tipología SEGURIDAD/EMERGENCIA. Cluster F2 SEG ampliado oleada-2.
      const speculativeDeraG12_36R2 = (oleada2HasCodMun && oleada2IsSecurity && this.circuitBreaker.isAvailable('DERA_G12_36_R2'))
        ? getDERAG12_36R2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('DERA_G12_36_R2');
            console.warn('[W2RF-2] DERA_G12_36_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== M2RF-1: EJECUCIÓN ESPECULATIVA DERA G12_38 Puntos Encuentro Maremotos R2 =====
      // DERA G12_38: 715 puntos encuentro maremotos costa Andalucía (47 INE litorales).
      // Decreto 197/2024 — Planes Riesgo Maremoto Costa Andalucía.
      // Solo para tipología SEGURIDAD/EMERGENCIA. Cluster F2 SEG ampliado oleada-2.
      const speculativeDeraG12_38R2 = (oleada2HasCodMun && oleada2IsSecurity && this.circuitBreaker.isAvailable('DERA_G12_38_R2'))
        ? getDERAG12_38R2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('DERA_G12_38_R2');
            console.warn('[M2RF-1] DERA_G12_38_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      // ===== A6: EJECUCIÓN ESPECULATIVA Edu Centers R2 =====
      // Edu Centers: ~6000+ centros educativos Junta Andalucía.
      // Fuzzy match por tokens pre-computados. Solo para tipología EDUCACIÓN.
      const speculativeEduCentersR2 = (oleada2HasCodMun && oleada2IsEducation && this.circuitBreaker.isAvailable('EDU_CENTERS_R2'))
        ? getEduCentersR2Service().geocode({
            codINE: options.codMun!,
            name: options.name,
          }).catch((err) => {
            this.circuitBreaker.recordFailure('EDU_CENTERS_R2');
            console.warn('[A6] EDU_CENTERS_R2 speculative error (handled):', err);
            return null;
          })
        : Promise.resolve(null);

      
      if (useLocalData && options.codMun) {
        attempts.push('singleton_indexeddb');
        try {
          // Verificar si IndexedDB está lista (datos cargados)
          const dbReady = await isDatabaseReady();
          
          if (dbReady) {
            // Mapear tipo a tipología DERA
            const tipologia = mapTypeToTipologia(classification.type);
            
            // Consulta rápida: ¿es singleton?
            const singletonResult = await detectSingleton(options.codMun, tipologia);
            
            console.log(
              `[ARCH-01] SingletonDetector: ${tipologia} en ${options.codMun} → ` +
              `count=${singletonResult.count}, singleton=${singletonResult.isSingleton} (${singletonResult.queryTimeMs.toFixed(1)}ms)`
            );
            
            if (singletonResult.isSingleton && singletonResult.feature) {
              // ===== SINGLETON DETECTADO → RETORNO DIRECTO 95% =====
              const feature = singletonResult.feature;
              
              console.log(
                `[ARCH-01] ✅ SINGLETON: "${feature.nombre}" (95% confianza, skip TSF+WFS)`
              );
              
              geocodingResult = {
                x: feature.x,
                y: feature.y,
                confidence: 95,
                source: `SINGLETON_${tipologia}`,
                matchedName: feature.nombre,
                municipality: options.municipality,
                province: options.province
              };
              geocoderUsed = `singleton:${tipologia.toLowerCase()}`;
              
              // P2.10c: Validación cruzada REAL para singleton
              // Principio: Singleton ≠ fuente única → validar contra NGA/Catastro/CartoCiudad
              const validationResult = await this.validateWithSecondarySources(
                geocodingResult,
                'cache' as GeocodingSource,
                options,
                classification
              );
              
              const processingTime = Date.now() - startTime;
              const singletonResult_TRF06: OrchestrationResult = {
                geocoding: geocodingResult,
                classification,
                geocoderUsed,
                processingTime,
                errors,
                attempts,
                originalName: options.name,
                crossValidationScore: validationResult.crossValidationScore,
                validationStatus: validationResult.validationStatus,
                discrepancyMeters: validationResult.discrepancyMeters,
                requiresManualReview: validationResult.requiresManualReview,
                reviewReason: validationResult.reviewReason,
                sourcesConsulted: validationResult.sourcesConsulted,
              };
              return this.finalizeResult(singletonResult_TRF06, options.municipality, options.codMun, options);
              
            } else if (singletonResult.count >= 2) {
              // ===== MÚLTIPLES CANDIDATOS → DESAMBIGUACIÓN =====
              console.log(
                `[ARCH-01] 🔄 Múltiples (${singletonResult.count}): ${tipologia} en ${options.codMun} → desambiguación`
              );
              
              // Obtener candidatos ordenados por similitud con nombre buscado
              const candidates = await getCandidatesByNombre(
                options.codMun,
                tipologia,
                options.name,
                10
              );
              
              if (candidates.length >= 1) {
                // Convertir DERAFeature a GeocodingCandidate para desambiguación
                const geocodingCandidates: GeocodingCandidate[] = candidates.map(f => ({
                  id: f.id,
                  nombre: f.nombre,
                  direccion: f.direccion || '',
                  municipio: f.municipio,
                  codMunicipio: f.codMun,
                  utmX: f.x,
                  utmY: f.y,
                  tipologia: classification.type,
                  subtipo: f.subtipo || '',
                }));
                
                // Crear registro PTEL para desambiguación
                const ptelRecord: PTELRecord = {
                  nombre: options.name,
                  direccion: options.address,
                  localidad: options.municipality,
                  codMunicipio: options.codMun,
                };
                
                // Ejecutar desambiguación multi-campo (F023)
                // MJ-05: bonus de proximidad → resolver speculativeCdauR2
                let _dirCoordsForDisambig: { x: number; y: number } | undefined;
                try {
                  const _cdauForDisambig = await speculativeCdauR2;
                  if (_cdauForDisambig && (_cdauForDisambig.confidence ?? 0) >= 60
                      && _cdauForDisambig.x && _cdauForDisambig.y) {
                    _dirCoordsForDisambig = { x: _cdauForDisambig.x, y: _cdauForDisambig.y };
                    console.log(`[MJ-05] 📍 Dir coords para disambig: X=${_cdauForDisambig.x} Y=${_cdauForDisambig.y}`);
                  }
                } catch { /* non-blocking */ }
                const disambResult = disambiguate(
                  geocodingCandidates, 
                  ptelRecord, 
                  classification.type,
                  _dirCoordsForDisambig
                );
                
                if (disambResult.selected && disambResult.confidence !== 'NONE') {
                  console.log(
                    `[ARCH-01] 📊 Desambiguación: "${disambResult.selected.nombre}" ` +
                    `(score=${disambResult.score}, conf=${disambResult.confidence})`
                  );
                  
                  const disambConfidence = 
                    disambResult.confidence === 'HIGH' ? 90 :
                    disambResult.confidence === 'MEDIUM' ? 75 : 60;
                  
                  geocodingResult = {
                    x: disambResult.selected.utmX,
                    y: disambResult.selected.utmY,
                    confidence: disambConfidence,
                    source: 'cache',
                    matchedName: disambResult.selected.nombre,
                    municipality: options.municipality,
                    province: options.province
                  };
                  geocoderUsed = 'disambiguated:indexeddb';
                  
                  // Si HIGH, retornar directamente
                  if (disambResult.confidence === 'HIGH') {
                    const processingTime = Date.now() - startTime;
                    const disambResult_TRF06: OrchestrationResult = {
                      geocoding: geocodingResult,
                      classification,
                      geocoderUsed,
                      processingTime,
                      errors,
                      attempts,
                      originalName: options.name,
                      crossValidationScore: disambConfidence,
                      validationStatus: 'CONFIRMED',
                      discrepancyMeters: null,
                      requiresManualReview: false,
                      disambiguationConfidence: 'HIGH', // DISAMV-REFINE-01 M5
                    };
                    return this.finalizeResult(disambResult_TRF06, options.municipality, options.codMun, options);
                  }
                  // MEDIUM/LOW: continuar cascada para validación cruzada
                }
              }
            }
            // count === 0: No hay datos locales → fallback a TSF + cascada WFS
          } else {
            // ===== FALLBACK A LOCALDATA SERVICE (para tests y compatibilidad) =====
            console.log('[ARCH-01] ⚠️ IndexedDB no lista, intentando LocalDataService...');
            
            // Usar el viejo sistema para mantener compatibilidad con tests
            const localCategories = mapTypeToLocalCategories(classification.type);
            
            if (localCategories.length > 0 && localCategories.length <= 2) {
              const count = await countByType(classification.type, options.codMun);
              
              if (count === 1) {
                const singletonFeature = await getUniqueByType(classification.type, options.codMun);
                
                if (singletonFeature) {
                  console.log(
                    `[ARCH-01] ✅ SINGLETON (LocalDataService): "${singletonFeature.nombre}" (95% confianza)`
                  );
                  
                  geocodingResult = {
                    x: singletonFeature.x,
                    y: singletonFeature.y,
                    confidence: 95,
                    source: `SINGLETON_${singletonFeature.categoria.toUpperCase()}`,
                    matchedName: singletonFeature.nombre,
                    municipality: options.municipality,
                    province: options.province
                  };
                  geocoderUsed = `singleton:${singletonFeature.categoria}`;
                  
                  // P2.10d: Validación cruzada REAL para singleton (LocalDataService)
                  const validationResult = await this.validateWithSecondarySources(
                    geocodingResult,
                    'local_dera' as GeocodingSource,
                    options,
                    classification
                  );
                  
                  const processingTime = Date.now() - startTime;
                  const singletonLocal_TRF06: OrchestrationResult = {
                    geocoding: geocodingResult,
                    classification,
                    geocoderUsed,
                    processingTime,
                    errors,
                    attempts,
                    originalName: options.name,
                    crossValidationScore: validationResult.crossValidationScore,
                    validationStatus: validationResult.validationStatus,
                    discrepancyMeters: validationResult.discrepancyMeters,
                    requiresManualReview: validationResult.requiresManualReview,
                    reviewReason: validationResult.reviewReason,
                    sourcesConsulted: validationResult.sourcesConsulted,
                  };
                  return this.finalizeResult(singletonLocal_TRF06, options.municipality, options.codMun, options);
                }
              } else if (count >= 2) {
                // Múltiples candidatos: usar desambiguación
                const allFeatures = await getFeaturesByMunicipio(
                  options.codMun,
                  localCategories
                );
                
                if (allFeatures.length >= 1) {
                  const geocodingCandidates: GeocodingCandidate[] = allFeatures.map(f => ({
                    id: f.id,
                    nombre: f.nombre,
                    direccion: f.direccion || '',
                    municipio: f.municipio,
                    codMunicipio: f.codMun,
                    utmX: f.x,
                    utmY: f.y,
                    tipologia: classification.type,
                    subtipo: f.tipo || '',
                  }));
                  
                  const ptelRecord: PTELRecord = {
                    nombre: options.name,
                    direccion: options.address,
                    localidad: options.municipality,
                    codMunicipio: options.codMun,
                  };
                  
                  // MJ-05: bonus de proximidad → resolver speculativeCdauR2
                  let _dirCoordsForDisambig: { x: number; y: number } | undefined;
                  try {
                    const _cdauForDisambig = await speculativeCdauR2;
                    if (_cdauForDisambig && (_cdauForDisambig.confidence ?? 0) >= 60
                        && _cdauForDisambig.x && _cdauForDisambig.y) {
                      _dirCoordsForDisambig = { x: _cdauForDisambig.x, y: _cdauForDisambig.y };
                      console.log(`[MJ-05] 📍 Dir coords para disambig: X=${_cdauForDisambig.x} Y=${_cdauForDisambig.y}`);
                    }
                  } catch { /* non-blocking */ }
                  const disambResult = disambiguate(
                    geocodingCandidates,
                    ptelRecord,
                    classification.type,
                    _dirCoordsForDisambig
                  );
                  
                  if (disambResult.selected && disambResult.confidence !== 'NONE') {
                    const disambConfidence =
                      disambResult.confidence === 'HIGH' ? 90 :
                      disambResult.confidence === 'MEDIUM' ? 75 : 60;
                    
                    geocodingResult = {
                      x: disambResult.selected.utmX,
                      y: disambResult.selected.utmY,
                      confidence: disambConfidence,
                      source: 'DISAMBIGUATED_LOCAL',
                      matchedName: disambResult.selected.nombre,
                      municipality: options.municipality,
                      province: options.province
                    };
                    geocoderUsed = 'disambiguated:local';
                    
                    if (disambResult.confidence === 'HIGH') {
                      const processingTime = Date.now() - startTime;
                      const disambLocal_TRF06: OrchestrationResult = {
                        geocoding: geocodingResult,
                        classification,
                        geocoderUsed,
                        processingTime,
                        errors,
                        attempts,
                        originalName: options.name,
                        crossValidationScore: disambConfidence,
                        validationStatus: 'CONFIRMED',
                        discrepancyMeters: null,
                        requiresManualReview: false,
                        disambiguationConfidence: 'HIGH', // DISAMV-REFINE-01 M5
                      };
                      return this.finalizeResult(disambLocal_TRF06, options.municipality, options.codMun, options);
                    }

                    // DISAMV-REFINE-01 M2: cap honestidad — desambiguación ambigua
                    // MEDIUM (gap 8–14 pts) o LOW (gap <8 pts) → confianza máx 55
                    // Sin cap, un MEDIUM puede llegar a PASS sin confirmación externa.
                    if (disambResult.confidence === 'MEDIUM' || disambResult.confidence === 'LOW') {
                      geocodingResult.confidence = Math.min(geocodingResult.confidence, 55);
                    }

                    // DISAMV-REFINE-01 M1: escalada WFS para tipologías críticas
                    // Si MEDIUM/LOW en HEALTH, EDUCATION, POLICE o FIRE → intentar WFS rescue
                    // antes de dejar el resultado en oleada1Candidates.
                    if (disambResult.confidence === 'MEDIUM' || disambResult.confidence === 'LOW') {
                      const wfsRescue = await this._tryWFSRescue(options, classification.type);
                      if (wfsRescue && wfsRescue.confidence > 60) {
                        geocodingResult = wfsRescue;
                        geocoderUsed = `wfs_rescue:${classification.type.toLowerCase()}`;
                        console.log(
                          `[DISAMV-M1] ✅ WFS rescue: "${geocodingResult.matchedName}" ` +
                          `(${geocodingResult.confidence}% conf, tipo=${classification.type})`
                        );
                      }
                    }
                  }
                }
              }
            }
          }
        } catch (err) {
          errors.push(`SINGLETON_INDEXEDDB: ${err}`);
          console.warn('[ARCH-01] Error en SingletonDetector:', err);
        }
      }

      // ===== FOPT-01: OLEADA 1 PARALELA (TSF + LOCAL_DERA) =====
      // Singleton ya se evaluó arriba (con early return para ≥85%).
      // TSF y LOCAL_DERA se lanzaron especulativamente al inicio de geocode().
      // Ahora resolvemos ambos en paralelo con Promise.allSettled.
      const oleada1Settled = await Promise.allSettled([speculativeTSF, speculativeLocalDERA]);

      interface Oleada1Candidate {
        source: string;
        x: number;
        y: number;
        confidence: number;
        matchedName: string;
        geocoderUsed: string;
        responseTimeMs: number;
      }
      const oleada1Candidates: Oleada1Candidate[] = [];

      // Incluir resultado previo de Singleton/Disambiguación (si existe con confianza < 85%)
      if (geocodingResult) {
        oleada1Candidates.push({
          source: geocodingResult.source,
          x: geocodingResult.x,
          y: geocodingResult.y,
          confidence: geocodingResult.confidence,
          matchedName: geocodingResult.matchedName || options.name,
          geocoderUsed,
          responseTimeMs: Date.now() - startTime,
        });
      }

      // Procesar resultado TSF
      const tsfSettled = oleada1Settled[0];
      if (tsfSettled.status === 'fulfilled') {
        const tsfResponse = tsfSettled.value;
        if (tsfResponse?.resolved && tsfResponse.coordenadas) {
          attempts.push('tsf_geocoder');
          oleada1Candidates.push({
            source: `TSF_${tsfResponse.status}`,
            x: tsfResponse.coordenadas.x,
            y: tsfResponse.coordenadas.y,
            confidence: tsfResponse.confidence,
            matchedName: tsfResponse.nombreNormalizado || options.name,
            geocoderUsed: `tsf:${tsfResponse.status.toLowerCase()}`,
            responseTimeMs: tsfResponse.tiempoMs,
          });
          console.log(
            `[FOPT-01] TSF HIT: ${tsfResponse.status} ` +
            `(${tsfResponse.candidateCount} cand, ${tsfResponse.confidence}%, ${tsfResponse.tiempoMs}ms)`
          );
        } else if (tsfResponse) {
          console.log(`[FOPT-01] TSF MISS: ${tsfResponse.status} (${tsfResponse.confidence}%)`);
        }
      } else if (tsfSettled.status === 'rejected') {
        errors.push(`TSF_GEOCODER: ${tsfSettled.reason}`);
        console.warn('[FOPT-01] TSF error (handled):', tsfSettled.reason);
      }

      // Procesar resultado LOCAL_DERA
      const localSettled = oleada1Settled[1];
      if (localSettled.status === 'fulfilled') {
        const localResult = localSettled.value;
        if (localResult?.success && localResult.bestMatch && localResult.matchScore >= 70) {
          const converted = toGeocodingResult(localResult.bestMatch, localResult.matchScore);
          attempts.push('local_dera');
          oleada1Candidates.push({
            source: converted.source,
            x: converted.x,
            y: converted.y,
            confidence: localResult.matchScore,
            matchedName: converted.matchedName,
            geocoderUsed: 'local_dera',
            responseTimeMs: Date.now() - startTime,
          });
          console.log(
            `[FOPT-01] LOCAL_DERA HIT: "${converted.matchedName}" (${localResult.matchScore}%)`
          );
        }
      } else if (localSettled.status === 'rejected') {
        errors.push(`LOCAL_DERA: ${localSettled.reason}`);
        console.warn('[FOPT-01] LOCAL_DERA error (handled):', localSettled.reason);
      }

      // Seleccionar mejor candidato de oleada 1 (mayor confianza)
      // CB-A: Aplicar filtro espacial municipal antes de seleccionar el mejor
      if (options.codMun && oleada1Candidates.length > 0) {
        const checks = await Promise.all(
          oleada1Candidates.map(c => this.muniSpatial.check(c.x, c.y, options.codMun!))
        );
        const inside = oleada1Candidates.filter((_, i) => checks[i].isInside);
        if (inside.length > 0) {
          // Al menos un candidato está dentro → descartar los de fuera
          oleada1Candidates.splice(0, oleada1Candidates.length, ...inside);
        } else {
          // Todos fuera → modo degradado: penalizar −20 pp pero no descartar
          console.warn(
            `[MUNI-SPATIAL] Todos los candidatos oleada1 fuera del municipio INE ${options.codMun} → penalización aplicada`
          );
          oleada1Candidates.forEach((c, i) => {
            if (checks[i].mode !== 'degraded') {
              c.confidence = Math.max(c.confidence - OUTSIDE_CONFIDENCE_PENALTY, 10);
            }
          });
        }
      }

      oleada1Candidates.sort((a, b) => b.confidence - a.confidence);

      if (oleada1Candidates.length > 0) {
        const best = oleada1Candidates[0];
        geocodingResult = {
          x: best.x,
          y: best.y,
          confidence: best.confidence,
          source: best.source,
          matchedName: best.matchedName,
          municipality: options.municipality,
          province: options.province,
        };
        geocoderUsed = best.geocoderUsed;
        console.log(
          `[FOPT-01] Best oleada 1: ${best.geocoderUsed} → "${best.matchedName}" (${best.confidence}%)`
        );
      }

      // T1a: Early return con umbral dual — 90% para tipologías críticas, 85% para el resto
      // isCriticalInfrastructure('') → false, ?? '' garantiza que null/undefined → umbral 85
      const _tipologiaStr = mapTypeToTipologia(classification.type) ?? '';
      const _stopThreshold = isCriticalInfrastructure(_tipologiaStr) ? 90 : 85;
      if (geocodingResult && geocodingResult.confidence >= _stopThreshold) {
        const processingTime = Date.now() - startTime;
        const oleada1Result: OrchestrationResult = {
          geocoding: geocodingResult,
          classification,
          geocoderUsed,
          processingTime,
          errors,
          attempts,
          originalName: options.name,
          crossValidationScore: geocodingResult.confidence,
          validationStatus: 'CONFIRMED',
          discrepancyMeters: null,
          requiresManualReview: false,
          sourcesConsulted: oleada1Candidates.map(c => ({
            source: c.source as GeocodingSource,
            x: c.x,
            y: c.y,
            confidence: c.confidence,
            matchedName: c.matchedName,
            responseTimeMs: c.responseTimeMs,
          })),
          ...(addressExtractionResult && originalAddress ? {
            addressExtraction: {
              originalAddress,
              normalizedAddress: addressExtractionResult.address,
              confidence: addressExtractionResult.confidence,
              transformations: addressExtractionResult.transformations || [],
            }
          } : {}),
        };
        console.log(
          `[FOPT-01] ✅ Early return oleada 1: ${geocoderUsed} (${geocodingResult.confidence}%)`
        );
        return this.finalizeResult(oleada1Result, options.municipality, options.codMun, options);
      }

      // Recopilar sourceResults de oleada 1 para validación cruzada (reutilizados en oleada 2)
      const sourceResults: SourceResult[] = oleada1Candidates.map(c => ({
        source: c.source as GeocodingSource,
        x: c.x,
        y: c.y,
        confidence: c.confidence,
        matchedName: c.matchedName,
        responseTimeMs: c.responseTimeMs,
      }));


      // ===== FOPT-02b: CONSUMO PARALELO OLEADA 2 (Promise.allSettled) =====
      // Recoger DERA_R2 + NGA_R2 + AGOL_R2 + CDAU_R2 en paralelo.
      // TODAS las fuentes que superen umbral se añaden a sourceResults para
      // validación cruzada. Se selecciona la de mayor confianza como resultado.
      // MJ-04: hoisted fuera del bloque if para calcular skipCdauOnline después
      let hasCdauR2InOleada2 = false;
      if (!geocodingResult) {
        attempts.push('oleada2_parallel');
        const oleada2Settled = await Promise.allSettled([
          speculativeDeraR2,
          speculativeNgaR2,
          speculativeAgolR2,
          speculativeCdauR2,
          speculativeDeraG10R2,
          speculativeDeraG3R2,
          speculativeIaphR2,
          speculativeCgphaR2,
          speculativeObphR2,
          speculativeOsmSecurityR2,
          speculativeGcFacilitiesR2,
          speculativeDeraG12_37R2,
          speculativeDeraG12_36R2,
          speculativeDeraG12_38R2,
          speculativeEduCentersR2,
        ]);

        const oleada2Labels = ['dera_r2', 'nga_r2', 'agol_r2', 'cdau_r2', 'dera_g10_r2', 'dera_g3_r2', 'iaph_r2', 'cgpha_r2', 'obph_r2', 'osm_security_r2', 'gc_facilities_r2', 'dera_g12_37_r2', 'dera_g12_36_r2', 'dera_g12_38_r2', 'edu_centers_r2'] as const;
        type Oleada2Label = typeof oleada2Labels[number];
        interface Oleada2Candidate {
          source: Oleada2Label;
          x: number;
          y: number;
          confidence: number;
          matchedName: string;
          /** Solo para nga_r2: tipo NGA del feature (PROP-B lo usa para detectar zona extensa) */
          ngaType?: string;
        }
        const oleada2Candidates: Oleada2Candidate[] = [];

        for (let i = 0; i < oleada2Settled.length; i++) {
          const settled = oleada2Settled[i];
          const label = oleada2Labels[i];

          if (settled.status === 'rejected') {
            errors.push(`${label}: ${settled.reason instanceof Error ? settled.reason.message : String(settled.reason)}`);
            continue;
          }

          const val = settled.value;
          if (!val || val.confidence < 60) continue;

          // F9.01a-fix.1: Registrar éxito R2 → resetea contador de fallos consecutivos
          this.circuitBreaker.recordSuccess(label.toUpperCase());

          // CDAU_R2 devuelve matchedAddress; el resto matchedName
          const name = ('matchedName' in val && val.matchedName)
            ? val.matchedName
            : ('matchedAddress' in val && (val as { matchedAddress?: string }).matchedAddress)
              ? (val as { matchedAddress: string }).matchedAddress
              : options.name;

          oleada2Candidates.push({
            source: label,
            x: val.x,
            y: val.y,
            confidence: val.confidence,
            matchedName: name,
            // PROP-B: propagar ngaType solo cuando la fuente es nga_r2
            ngaType: label === 'nga_r2' && 'ngaType' in val
              ? (val as { ngaType?: string }).ngaType
              : undefined,
          });

          sourceResults.push({
            source: label as GeocodingSource,
            x: val.x,
            y: val.y,
            confidence: val.confidence,
            matchedName: name,
            responseTimeMs: Date.now() - startTime,
          });
        }

        // CB-A: Filtro espacial sobre candidatos de oleada 2
        if (options.codMun && oleada2Candidates.length > 0) {
          const checks2 = await Promise.all(
            oleada2Candidates.map(c => this.muniSpatial.check(c.x, c.y, options.codMun!))
          );
          const inside2 = oleada2Candidates.filter((_, i) => checks2[i].isInside);
          if (inside2.length > 0) {
            oleada2Candidates.splice(0, oleada2Candidates.length, ...inside2);
          } else {
            console.warn(
              `[MUNI-SPATIAL] Todos los candidatos oleada2 fuera del municipio INE ${options.codMun} → penalización aplicada`
            );
            oleada2Candidates.forEach((c, i) => {
              if (checks2[i].mode !== 'degraded') {
                c.confidence = Math.max(c.confidence - OUTSIDE_CONFIDENCE_PENALTY, 10);
              }
            });
          }
        }

        // ── CB-E + CB-G: TypologyVoter post-oleada2 ─────────────────────────────
        // Reutiliza resultados ya en memoria (oleada2Candidates), sin fetch nuevo.
        // Solo actúa cuando classification.type es GENERIC, RSU o LOW-conf OTRO,
        // para evitar degradar clasificaciones ya correctas (R-04 del plan).
        // CB-G añade señal NGA fuzzy desde nga_r2 candidate (zero fetches nuevos).
        const VOTER_ELIGIBLE_TYPES = new Set([
          InfrastructureType.GENERIC,
        ]);
        const shouldRunVoter =
          VOTER_ELIGIBLE_TYPES.has(classification.type) &&
          oleada2Candidates.length > 0;

        if (shouldRunVoter) {
          const voteResult = typologyVote({
            name: options.name,
            declaredType: options.declaredType,
            geocodabilityCategory: options.addressType as unknown as
              import('./orchestrator/AddressClassifier').GeocodabilityCategory | undefined,
          });

          // CB-G: señal NGA fuzzy — si oleada2 resolvió con nga_r2, aporta voto adicional
          const ngaCandidate = oleada2Candidates.find(c => c.source === 'nga_r2');
          const ngaVote = ngaCandidate?.matchedName
            ? extractNgaVote({
                searchName: options.name,
                ngaMatchedName: ngaCandidate.matchedName,
              })
            : null;

          // Fusión: si ngaVote coincide con voteResult, sube la confianza del voter
          const mergedConfidence = (ngaVote && ngaVote.type === voteResult.type)
            ? Math.min(100, Math.round((voteResult.confidence + ngaVote.confidence) / 2))
            : voteResult.confidence;

          const effectiveConfidence = Math.max(voteResult.confidence, mergedConfidence);

          if (effectiveConfidence >= 80 && voteResult.type !== InfrastructureType.GENERIC) {
            console.log(
              `[CB-E/G] TypologyVoter actualiza tipo: ${classification.type} → ${voteResult.type} ` +
              `(conf=${effectiveConfidence}` +
              (ngaVote ? `, nga_fuzzy=${ngaVote.confidence}` : '') +
              `, src=${voteResult.source})`
            );
            classification = {
              ...classification,
              type: voteResult.type,
            };
          }
        }
        // ── fin CB-E/G ───────────────────────────────────────────────────────────

        // Seleccionar mejor resultado por confianza
        if (oleada2Candidates.length > 0) {
          oleada2Candidates.sort((a, b) => b.confidence - a.confidence);
          const best = oleada2Candidates[0];
          geocodingResult = {
            x: best.x,
            y: best.y,
            confidence: best.confidence,
            source: best.source as GeocodingSource,
            matchedName: best.matchedName,
            municipality: options.municipality,
            province: options.province,
          };
          geocoderUsed = `oleada2:${best.source}`;
          console.log(
            `[FOPT-02b] Oleada 2 paralela: ${oleada2Candidates.length} fuente(s) válida(s), ` +
            `mejor=${best.source} (conf=${best.confidence})`
          );

          // ── PROP-B (NGA-REFINE-01) ──────────────────────────────────────────
          // Si el mejor candidato es nga_r2 con zona extensa, intentar afinarlo
          // con Catastro R2 V2 y CDAU R2 antes de aceptar el centroide NGA (±80 m–500 m).
          // Coste adicional estimado: < 50 ms (ambas fuentes offline/R2).
          // ─────────────────────────────────────────────────────────────────────
          const PROP_B_NGA_ZONE_TYPES = new Set([
            'Lugar / Paraje', 'Llanura', 'Depresión',
            'Relieve Litoral / Marítimo', 'Masa de Agua', 'Núcleo de Población',
          ]);

          if (
            best.source === 'nga_r2' &&
            best.ngaType !== undefined &&
            PROP_B_NGA_ZONE_TYPES.has(best.ngaType)
          ) {
            const codMunPropB = options.codMun ?? getCodigoINE(options.municipality ?? '');
            if (codMunPropB) {
              console.log(`[PROP-B] NGA zona extensa detectada (tipo='${best.ngaType}'), iniciando refinamiento…`);
              let propBResolved = false;

              // Intento 1: Catastro R2 V2 scoped al municipio
              try {
                const catRefine = await getCatastroGeocoderV2().geocode({
                  codINE: codMunPropB,
                  name: options.name,
                  minConfidence: 60,
                });
                if (catRefine && catRefine.confidence >= 60) {
                  geocodingResult = {
                    x: catRefine.x,
                    y: catRefine.y,
                    confidence: catRefine.confidence,
                    source: 'catastro_r2' as GeocodingSource,
                    matchedName: catRefine.matchedName ?? options.name ?? best.matchedName,
                    municipality: options.municipality,
                    province: options.province,
                  };
                  geocoderUsed = 'catastro_r2:nga_refine';
                  propBResolved = true;
                  console.log(`[PROP-B] ✅ NGA→Catastro refine: conf=${catRefine.confidence}`);
                }
              } catch (err) {
                console.warn('[PROP-B] Catastro refine error (non-blocking):', err);
              }

              // Intento 2: CDAU R2 si Catastro no resolvió con confianza suficiente
              if (!propBResolved) {
                try {
                  const cdauRefine = await geocodeWithCdauR2(
                    options.name ?? options.address ?? '',
                    codMunPropB,
                  );
                  if (cdauRefine && cdauRefine.confidence >= 55) {
                    geocodingResult = {
                      x: cdauRefine.x,
                      y: cdauRefine.y,
                      confidence: cdauRefine.confidence,
                      source: 'cdau_r2' as GeocodingSource,
                      matchedName: cdauRefine.matchedAddress ?? options.name ?? best.matchedName,
                      municipality: options.municipality,
                      province: options.province,
                    };
                    geocoderUsed = 'cdau_r2:nga_refine';
                    propBResolved = true;
                    console.log(`[PROP-B] ✅ NGA→CDAU refine: conf=${cdauRefine.confidence}`);
                  }
                } catch (err) {
                  console.warn('[PROP-B] CDAU refine error (non-blocking):', err);
                }
              }

              // Intento 3: mantener NGA si ambos fallan (ya penalizado por NGA-TYPE-01)
              if (!propBResolved) {
                geocoderUsed = 'nga_r2:zona_extensa';
                console.log(`[PROP-B] ⚠️ Ambos refinamientos fallaron — manteniendo NGA zona extensa`);
              }
            }
          }
          // ── /PROP-B ────────────────────────────────────────────────────────
        }
        // MJ-04: registrar si cdau_r2 resolvió (para skipCdauOnline post-bloque)
        hasCdauR2InOleada2 = oleada2Candidates.some(c => c.source === 'cdau_r2');
      }

      // ===== F023 FASE 2.3: CONSULTAS PARALELAS MULTI-FUENTE =====
      // Consulta WFS_SPECIALIZED + CARTOCIUDAD + CDAU en paralelo
      // Objetivo: alimentar validateEnhanced() con 2-3+ fuentes para validación cruzada
      attempts.push('parallel_query');

      // MJ-04: Calcular skip CDAU online ANTES de entrar en el paralelo.
      // Condición 1 — R2 ya resolvió: CDAU WFS online es duplicado (misma fuente, más lenta).
      // Condición 2 — municipio pequeño: WFS online devuelve 0 resultados sistemáticamente
      //   para municipios con < 3.000 portales (Colomera, Jubrique, Valle del Zalabí…).
      //   getPortalCount() es síncrono y O(1): consulta el LRU cache que oleada 2 pobló.
      //   Si por alguna razón el cache está vacío (0), la guardia NO actúa → sin regresión.
      const cdauPortalCount = options.codMun
        ? getCDAUR2Service().getPortalCount(options.codMun)
        : 0;
      const isSmallMunicipality = cdauPortalCount > 0 && cdauPortalCount < 3_000;
      const skipCdauOnline = hasCdauR2InOleada2 || isSmallMunicipality;

      if (skipCdauOnline) {
        const reason = hasCdauR2InOleada2 ? 'cdau_r2 ya resolvió' : `municipio pequeño (${cdauPortalCount} portales)`;
        console.log(`[MJ-04] Skip CDAU WFS online: ${reason} (codINE=${options.codMun ?? 'N/A'})`);
        attempts.push('cdau_online:skipped_mj04');
      }

      const currentBestConfidence = geocodingResult?.confidence ?? 0;
      const parallelResults = await this.queryParallelSources({
        name: options.name,
        municipality: options.municipality,
        province: options.province,
        address: options.address,
        type: classification.type,
        codMun: options.codMun,
        timeoutMs: 8000,
        currentConfidence: currentBestConfidence,
        skipCdauOnline,
        subtype: classification.subtype, // UPCLS3 eslabón 40 (γ híbrido): propaga sub-disc EMERGENCY al flujo paralelo
        hasDocumentCoords: options.hasDocumentCoords, // CENTOR: puerta del centroide municipal
      });

      // Añadir resultados paralelos a sourceResults para validación cruzada
      for (const result of parallelResults.results) {
        sourceResults.push(result);
        
        // Actualizar geocodingResult si es mejor que el actual
        if (!geocodingResult || result.confidence > geocodingResult.confidence) {
          // CENT76: si gana el centroide municipal del WFS especializado, se
          // conserva el resultado del productor (fuente, `matchType`, metadatos)
          // con la confianza validada, y su geocodificador; así lo reconocen
          // `esCentroideMunicipal`, la puntuación v2 y P1. Lo demás, como antes.
          const wfs = result.source === 'wfs_dera' ? parallelResults.wfsEspecializado : null;
          if (wfs && esCentroideMunicipal(wfs.geocoder, wfs.result.source)) {
            geocodingResult = { ...wfs.result, confidence: result.confidence };
            geocoderUsed = wfs.geocoder;
            continue;
          }
          geocodingResult = {
            x: result.x,
            y: result.y,
            confidence: result.confidence,
            source: result.source,
            matchedName: result.matchedName || options.name,
            municipality: options.municipality,
            province: options.province
          };
          geocoderUsed = `parallel:${result.source.toLowerCase()}`;
        }
      }

      // Registrar errores de consultas fallidas (para diagnóstico)
      for (const error of parallelResults.errors) {
        errors.push(`PARALLEL_${error.source}: ${error.error.message}`);
      }

      console.log(
        `[F023-2.3] Integración paralela: ${parallelResults.results.length} fuentes, ` +
        `sourceResults total: ${sourceResults.length}`
      );

      // ===== PROP-D-FIX: DERA R2 para InfrastructureType.GENERIC =====
      // GENERIC es el único tipo excluido de la guarda FUT-02 TAREA 4 (bloque inferior).
      // Esa guarda llama a trySpecializedGeocoder(), donde vive el case GENERIC con PROP-D.
      // Sin este bloque, GENERIC cae directo a M3 (centroide conf=22) sin intentar DERA R2.
      // BM.03 post PROP-C-FIX (20260409): 0 activaciones PROP-D confirmadas. Fix: bloque propio.
      if (!geocodingResult && classification.type === InfrastructureType.GENERIC
          && options.codMun && this.circuitBreaker.isAvailable('DERA_R2')) {
        attempts.push('prop-d-fix:generic:dera_r2');
        try {
          const { result: genericResult, geocoder: genericGeo } =
            await this.trySpecializedGeocoder(
              InfrastructureType.GENERIC,
              {
                name: options.name,
                municipality: options.municipality,
                province: options.province,
                codMun: options.codMun,
                maxResults: 5,
              },
              attempts,
              undefined,
              options.hasDocumentCoords
            );
          if (genericResult) {
            geocodingResult = genericResult;
            geocoderUsed = genericGeo;
          }
        } catch {
          // non-blocking — circuit breaker registrado dentro de trySpecializedGeocoder
        }
      }

      // ===== FUT-02 TAREA 4: FALLBACK TIPOLÓGICO MEJORADO =====
      // Si el especializado primario no encontró resultado, probar geocodificadores
      // WFS alternativos relacionados ANTES de saltar a NGA/IAID/Overpass
      const hasSpecializedResult = sourceResults.some(r => r.source.startsWith('wfs_'));
      
      if (!geocodingResult && !hasSpecializedResult && classification.type !== InfrastructureType.GENERIC) {
        attempts.push('fallback_typology');
        try {
          const wfsFallbackOptions: WFSSearchOptions = {
            name: options.name,
            municipality: options.municipality,
            province: options.province,
            codMun: options.codMun,   // P1-fix: necesario para AGOL + centroide en case SOCIAL
            maxResults: 5
          };

          // ARCH-01c fix (Opción A): llamada directa al geocodificador especializado
          // del tipo actual ANTES de iterar sobre fallbacks alternativos.
          // Esto garantiza que case SOCIAL (AGOL conf≥45 + centroide conf=22) se ejecute.
          // UPCLS3 eslabón 40: propagamos `classification.subtype` para que EMERGENCY
          // pueda enrutar a SOCORRO / HUMANITARIA si el classifier marcó sub-dominio.
          if (options.codMun) {
            const { result: specResult, geocoder: specGeo } =
              await this.trySpecializedGeocoder(
                classification.type,
                { ...wfsFallbackOptions, codMun: options.codMun },
                attempts,
                classification.subtype,
                options.hasDocumentCoords
              );
            if (specResult) {
              geocodingResult = specResult;
              geocoderUsed = specGeo;
            }
          }

          if (!geocodingResult) {
            const fallbackResult = await this.tryFallbackByTypology(
              classification.type,
              wfsFallbackOptions,
              attempts,
              errors,
              options.hasDocumentCoords
            );

            if (fallbackResult.result) {
              geocodingResult = fallbackResult.result;
              geocoderUsed = fallbackResult.geocoder;

              // PROP-E: validación tipológica cruzada post-fallback
              geocodingResult = applyWfsFallbackValidation(
                geocodingResult,
                classification.type,
                geocoderUsed,
              );

              // Añadir a sourceResults para validación cruzada
              sourceResults.push({
                source: 'wfs_dera' as GeocodingSource,
                x: fallbackResult.result.x,
                y: fallbackResult.result.y,
                confidence: fallbackResult.result.confidence,
                matchedName: fallbackResult.result.matchedName,
                responseTimeMs: Date.now() - startTime,
              });
            }
          }
        } catch (err) {
          errors.push(`FALLBACK_TYPOLOGY: ${err}`);
          console.warn('[FUT-02] Error en fallback tipológico:', err);
        }
      }

      // ===== CAT-12: NIVEL L4 - CATASTRO PTEL (referencias Pol/Parcela) =====
      // F1-UNIFY-MIGRATE+S3-CLEANUP (9-may-2026): legacy CatastroGeocoder eliminado.
      // Único geocoder catastro PTEL = CatastroGeocoderV2 (campo this.catastroGeocoder).
      // Patrón canónico R2-first heredado de L0 (chat 1/2).
      // Guard shouldTryCatastro() preservado: 5 patrones via hasCatastralPatternInFields()
      // (cláusula 1) + 4 tipologías rurales sin dirección postal (cláusula 2).
      // refcat extraída con extractRefcatFromText desde name/address: si presente,
      // habilita OVC fallback dentro de V2; si ausente, V2 devuelve OVC SKIP y
      // nos quedamos con el R2 hit (o null si R2 también miss).
      if (!geocodingResult && this.shouldTryCatastro(options, classification.type)) {
        attempts.push('catastro_ptel');
        const codMun = options.codMun ?? '';
        if (!codMun) {
          // Sin codMun no podemos invocar geocodeWithOVCFallback — saltamos
          // silenciosamente a la siguiente cascada.
          console.log('[CAT-12] ⏭️  Skip: codMun no resoluble para municipio');
        } else {
          try {
            const refcatFromText = this.extractRefcatFromText(
              options.name ?? options.address ?? '',
            ) ?? this.extractRefcatFromText(options.address ?? '');
            const cat12Result = await this.catastroGeocoder.geocodeWithOVCFallback(
              {
                codINE: codMun,
                name: options.name,
                municipality: options.municipality,
                // OBS-P1-PROVINCIA-FALLBACK-GRANADA: nunca inventar provincia.
                // Declarada → derivada del codMun (garantizado en esta rama) → ''.
                province:
                  options.province ||
                  getProvinciaPorCodigoINE(codMun) ||
                  '',
                ...(refcatFromText ? { refcat: refcatFromText } : {}),
              },
              codMun,
            );

            if (cat12Result && cat12Result.confidence >= 50) {
              geocodingResult = cat12Result;
              // El source distingue R2 ('catastro_r2_v2') vs OVC ('catastro_api')
              geocoderUsed = cat12Result.source === 'catastro_api'
                ? 'catastro_api:cat12_r2_fallback'
                : 'catastro_r2_v2:cat12';

              // Añadir a sourceResults para validación cruzada (mantener
              // 'catastro_ptel' por compatibilidad con CrossValidator)
              sourceResults.push({
                source: 'catastro_ptel',
                x: cat12Result.x,
                y: cat12Result.y,
                confidence: cat12Result.confidence,
                matchedName: cat12Result.matchedName,
                responseTimeMs: Date.now() - startTime,
              });

              console.log(
                `[CAT-12] ✅ ${cat12Result.source === 'catastro_api' ? 'OVC fallback' : 'R2'} hit ` +
                `vía ${geocoderUsed}: ${cat12Result.matchedName} ` +
                `(${cat12Result.confidence}% conf)`
              );
            }
          } catch (err) {
            errors.push(`CATASTRO_PTEL: ${err}`);
            console.warn('[CAT-12] ❌ Error en geocodeWithOVCFallback:', err);
          }
        }
      }

      // ===== Fix B: NOMBRE === MUNICIPIO → centroide municipal =====
      // Caso real: "Los Gallardos" como núcleo poblacional del mismo municipio.
      // El elemento llega sin coordenadas; el nombre no tiene sufijo topónimo
      // reconocible por looksLikeToponym, pero sí coincide con el municipio.
      // Usamos INER2 (centroide offline) con confianza 60.
      // CENTOR (12-sep-2026): con coordenada del documento no se fabrica el
      // centroide (`hasDocumentCoords`). Las dos copias literales que seguían a
      // este bloque (mismo guard `!geocodingResult`, mismo cuerpo) se podaron:
      // solo se ejecutaban si esta fallaba, y volvían a fallar igual
      // (`OBS-GEO-FIX-B-TRES-COPIAS`, `.ptel/p0_centor_20260912/v_fixb_diff.txt`).
      if (!geocodingResult && !options.hasDocumentCoords && options.name && options.municipality) {
        const nameLower = options.name.toLowerCase().trim();
        const munLower  = options.municipality.toLowerCase().trim();
        if (nameLower === munLower) {
          attempts.push('fix-b:centroide-municipio');
          try {
            const codMun = getCodigoINE(options.municipality) || '';
            if (codMun) {
              const ineService = getINER2Service();
              await ineService.initialize();
              const centroide = ineService.getCentroide(codMun);
              if (centroide) {
                geocodingResult = {
                  x: centroide.x,
                  y: centroide.y,
                  confidence: 60,
                  source: 'iner2:centroide-municipio',
                  matchedName: options.municipality,
                };
                geocoderUsed = 'iner2:centroide-municipio';
                console.log(
                  `[Fix-B] Centroide municipal "${options.municipality}" ` +
                  `(${codMun}): X=${centroide.x} Y=${centroide.y}`
                );
              }
            }
          } catch (err) {
            errors.push(`fix-b:centroide-municipio: ${err}`);
          }
        }
      }

      // ===== NIVEL 2: NGA - NOMENCLÁTOR GEOGRÁFICO (para topónimos) =====
      // T1: Cascada LOCAL → WFS para máxima eficiencia
      if (!geocodingResult && useNGA) {
        // Buscar si la dirección/nombre contiene un topónimo
        const toponymSearch = options.address || options.name;
        
        if (looksLikeToponym(toponymSearch)) {
          attempts.push('nga');
          
          // 2A: Intentar primero NGA LOCAL (233K topónimos offline, ~5ms)
          try {
            // NGALOC DOP-4 (a): el INE de cabecera manda sobre el texto del municipio.
            const codINE = options.codMun || getCodigoINE(options.municipality) || '';
            const localResult = await this.ngaLocalGeocoder.geocode(
              toponymSearch,
              codINE,
              { minConfidence: 60 }
            );
            
            if (localResult && localResult.confidence >= 60) {
              geocodingResult = localResult;
              geocoderUsed = 'nga:local';
            }
          } catch (err) {
            errors.push(`nga_local: ${err}`);
          }
          
          // 2B: Fallback a NGA WFS si local no encontró (200-500ms)
          if (!geocodingResult) {
            try {
              const ngaResult = await this.ngaGeocoder.geocodeWithTypeDetection({
                name: toponymSearch,
                municipality: options.municipality,
                province: options.province
              });
              
              if (ngaResult && ngaResult.confidence >= 55) {
                geocodingResult = ngaResult;
                geocoderUsed = 'nga:wfs';
              }
            } catch (err) {
              errors.push(`NGA_WFS: ${err}`);
            }
          }
        }
      }

      // ===== NIVEL L5: CATASTRO R2 V2 - PARCELAS OFFLINE (2.4M inmuebles) =====
      // F1.07: Usa CatastroGeocoderV2 (Flatbush + uFuzzy + refcatMap)
      // IEP-06: addressType='cadastral' activa el intento aunque haya resultado previo
      //         de baja confianza (<70), priorizando la fuente catastral.
      const _iep06CadastralActive = options.addressType === 'cadastral';
      // CATPP-BC: pol/parc poblados por el parser (IEP-08) → vía catastral directa viable.
      // Misma semántica OR que CatastroGeocoderV2.detectStrategy ('polygon_parcel').
      const _hasPolParc = Boolean(options.poligonoCatastral || options.parcelaCatastral);
      // CATPP-BC (parte B): true cuando L5 acepta un hit por vía catastral REAL
      // (polygon_parcel/refcat). Protege la parcela exacta DGC de ser desbancada
      // por la validación cruzada aguas abajo (que promedia con el pool de la
      // oleada, donde el centroide genérico domina por confianza — 3.er eslabón
      // del mismo defecto que motivó la OBS).
      let _catastroAutoritativo = false;
      // CATPP-BC (parte B, DOP-BC-2=a ampliada con confirmación del usuario):
      // con pol/parc poblados, NUNCA saltar Catastro — el hit catastral pre-empta
      // al centroide genérico de la oleada paralela; el fallo deja lo previo intacto
      // (guarda anti-degradación en la aceptación del resultado, más abajo).
      // pol/parc solo los puebla el parser IEP-08 ante patrón catastral explícito,
      // por lo que son evidencia catastral por sí mismos aunque el clasificador
      // haya etiquetado otra tipología (caso Charches: addressType='sports').
      const _skipCatastroR2 = geocodingResult !== null
        && !(_iep06CadastralActive && geocodingResult.confidence < 70)
        && !_hasPolParc;
      if (!_skipCatastroR2) {
        const codINE = getCodigoINE(options.municipality) || '';
        
        // Solo proceder si tenemos codINE válido (5 dígitos)
        if (codINE && codINE.length === 5) {
          // Extraer posible refcat de la dirección/nombre
          const refcatMatch = (options.address || options.name || '')
            .match(/\b([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5}(?:[0-9A-Z]{4}[A-Z]{2})?)\b/i);
          const refcat = refcatMatch?.[1];
          
          // Extraer vía/número de la dirección
          const addressText = options.address || '';
          const viaMatch = addressText.match(/(?:c\/|calle|avda|av\.|plaza|pza|paseo)\s*([^,\d]+)/i);
          const numeroMatch = addressText.match(/,?\s*(?:n[°º]?\s*)?(\d+)/);
          
          // Usar Catastro R2 si hay algún criterio de búsqueda
          // CATPP-BC (parte C): pol/parc cuentan como criterio (rústicas sin vía ni refcat)
          const hasSearchCriteria = refcat || viaMatch || options.name || _hasPolParc;
          
          if (hasSearchCriteria) {
            attempts.push('catastro_r2');
            try {
              // IEP-06: addressType='cadastral' baja el umbral mínimo para maximizar recall
              const _catastroMinConf = _iep06CadastralActive ? 40 : 55;
              const catastroR2Result = await this.catastroGeocoder.geocode({
                codINE,
                refcat,
                via: viaMatch?.[1]?.trim(),
                numero: numeroMatch?.[1],
                name: options.name,
                // CATPP-BC (parte C): activa estrategia 'polygon_parcel' en CatastroGeocoderV2
                polygon: options.poligonoCatastral,
                parcel: options.parcelaCatastral,
                minConfidence: _catastroMinConf,
                maxResults: 3,
              });
              
              if (catastroR2Result && catastroR2Result.confidence >= _catastroMinConf) {
                // CATPP-BC (parte B): guarda anti-degradación. Si había resultado
                // previo fiable (conf >= 70 — solo alcanzable aquí vía _hasPolParc),
                // únicamente reemplazar cuando el hit llegó por vía catastral REAL
                // (polygon_parcel/refcat). Un fuzzy por nombre no desbanca a un
                // resultado previo fiable; el camino IEP-06 (previo conf < 70)
                // conserva su comportamiento histórico de reemplazo incondicional.
                const _previoFiable = geocodingResult !== null && geocodingResult.confidence >= 70;
                const _viaCatastralReal = catastroR2Result.matchType === 'polygon_parcel'
                  || catastroR2Result.matchType === 'refcat';
                if (!_previoFiable || _viaCatastralReal) {
                  geocodingResult = catastroR2Result;
                  geocoderUsed = 'catastro_r2';
                  _catastroAutoritativo = _viaCatastralReal;
                }
              }
            } catch (err) {
              errors.push(`CATASTRO_R2: ${err}`);
            }
          }
        }
      }

      // ===== NIVEL 3: IAID — RETIRADO (IAID-DECISION 17-may-2026) =====
      // El antiguo NIVEL 3 invocaba IAIDGeocoder sobre la capa DERA `g12_06_Deportivo`,
      // que no existe en el endpoint (HTTP 400 «Feature type unknown» desde 21-nov-2025).
      // Tras auditoría empírica: la capa real deportiva es `g12_24_EquipamientoDeportivo`,
      // ya consumida por WFSSportsGeocoder en el flujo typed (oleada-1 G12). La cascada
      // deportiva queda cubierta por Sports + Overpass + CartoCiudad sin valor diferencial
      // perdido. Flag `useIAID` preservado como no-op (ver L948-957). Sucesoras:
      // OBS-FT-WFS-SPORTS-SCHEMA-DRIFT sev A, OBS-FT-IAID-FLAG-DEPRECATED-CLEANUP sev D.

      // ===== NIVEL 4: OVERPASS/OSM =====
      if (!geocodingResult && useOverpass) {
        attempts.push('overpass');
        try {
          // Determinar qué tipo de búsqueda OSM hacer
          if (isTelecomInfrastructure(options.name)) {
            // Buscar antenas específicamente
            const towers = await this.overpassGeocoder.findTelecomTowers(
              options.municipality,
              options.province
            );
            
            if (towers.length > 0) {
              // Buscar el topónimo de la dirección para match
              const address = options.address || '';
              const matchingTower = findMatchingTower(towers, address, options.name);
              
              if (matchingTower && matchingTower.lon !== undefined && matchingTower.lat !== undefined) {
                const coords = this.overpassGeocoder.toUTM30(matchingTower.lon, matchingTower.lat);
                geocodingResult = {
                  x: coords.x,
                  y: coords.y,
                  confidence: 70,
                  source: 'OpenStreetMap',
                  matchedName: matchingTower.name ?? options.name,
                  municipality: options.municipality,
                  province: options.province
                };
                geocoderUsed = 'overpass:telecom';
              }
            }
          } else {
            // Búsqueda genérica en OSM
            const osmResult = await this.overpassGeocoder.geocode({
              name: options.name,
              municipality: options.municipality,
              province: options.province,
              infrastructureType: classification.type
            });
            
            if (osmResult && osmResult.confidence >= 55) {
              geocodingResult = osmResult;
              geocoderUsed = 'overpass:general';
            }
          }
        } catch (err) {
          errors.push(`Overpass: ${err}`);
        }
      }

      // ===== NIVEL L8: CDAU R2 → Movido a FOPT-02b (oleada2_parallel) =====
      // CDAU_R2 ahora se consume dentro de Promise.allSettled con DERA/NGA/AGOL

      // ===== NIVEL 5: CDAU (Callejero Andalucía) - FALLBACK =====
      // F023 Fase 2.3: Skip si ya consultamos CDAU en paralelo
      const hasCDAUResult = sourceResults.some(r => r.source === 'cdau');
      if (!geocodingResult && options.useGenericFallback !== false && !hasCDAUResult) {
        if (!this.circuitBreaker.isAvailable('CDAU_ONLINE')) {
          servicesSkipped.push('CDAU_ONLINE');
          attempts.push('cdau:skipped_circuit_open');
        } else {
          attempts.push('cdau');
          try {
            const cdauResult = await this.cdauGeocoder.geocode({
              street: options.address || options.name,
              municipality: options.municipality,
              province: options.province
            });

            if (cdauResult.success && cdauResult.data.confidence >= 60) {
              this.circuitBreaker.recordSuccess('CDAU_ONLINE');
              geocodingResult = cdauResult.data;
              geocoderUsed = 'generic:cdau';
            }
          } catch (err) {
            this.circuitBreaker.recordFailure('CDAU_ONLINE');
            errors.push(`CDAU: ${err}`);
          }
        }
      }

      // ===== NIVEL 5b: NGA-PED — Pedanías plurinucleares (NGA R2 + Nominatim) =====
      // Cuando CDAU devuelve 0 resultados y el texto es un topónimo de pedanía
      // (distinto del municipio principal), intentar NGA R2 y, si falla, Nominatim
      // con la pedanía como contexto geográfico explícito.
      if (!geocodingResult && options.municipality && options.codMun) {
        const pedaniaCandidate = options.address || options.name || '';
        if (looksLikePedania(pedaniaCandidate, options.municipality)) {
          attempts.push('nga-ped');
          try {
            const ngaResult = await geocodeWithNgaR2(
              pedaniaCandidate,
              { codINE: options.codMun }
            );
            if (ngaResult && ngaResult.confidence >= 50) {
              geocodingResult = { ...ngaResult, source: 'nga-pedania' };
              geocoderUsed = 'generic:nga-pedania';
            }
          } catch (err) {
            errors.push(`NGA-PED: ${err}`);
          }

          if (!geocodingResult && this.circuitBreaker.isAvailable('NOMINATIM')) {
            attempts.push('nominatim-ped');
            try {
              // BM-M1: mismo fix que L7 — usar nominatimGeocoder.geocode() para activar circuit breaker en 429
              const nomPedRes = await this.nominatimGeocoder.geocode({
                query: pedaniaCandidate,
                municipality: options.municipality,
                province: options.province,
              });
              if (nomPedRes.success && nomPedRes.data.confidence >= 35) {
                this.circuitBreaker.recordSuccess('NOMINATIM');
                geocodingResult = { ...nomPedRes.data, source: 'nominatim-pedania' };
                geocoderUsed = 'generic:nominatim-pedania';
              } else if (!nomPedRes.success) {
                this.circuitBreaker.recordFailure('NOMINATIM');
                errors.push(`Nominatim-PED: ${nomPedRes.error?.message ?? 'sin resultado'}`);
              }
            } catch (err) {
              this.circuitBreaker.recordFailure('NOMINATIM');
              errors.push(`Nominatim-PED: ${err}`);
            }
          }
        }
      }

      // ===== PASO 1.6 — CB-C: AddressVerifier pre-CartoCiudad =====
      // Verifica la dirección postal contra CDAU R2 antes de invocar CartoCiudad.
      // Umbral ≥ 80 → retorno directo, skip CartoCiudad.
      // Umbral 60-79 → candidato adicional, CartoCiudad actúa igualmente.
      if (
        !geocodingResult &&
        options.address &&
        hasAddressPattern(options.address) &&
        options.codMun
      ) {
        try {
          const verifier = getAddressVerifier();
          const verif = await verifier.verify(options.address, options.codMun);
          if (verif.found && verif.confidence !== undefined) {
            if (verif.confidence >= 80) {
              // Retorno directo — skip CartoCiudad
              geocodingResult = {
                x: verif.x!,
                y: verif.y!,
                confidence: verif.confidence,
                address: verif.address,
                matchedName: options.name,
                municipality: options.municipality,
                province: options.province,
                source: 'cdau_r2',
              };
              geocoderUsed = 'address_verifier:cdau_r2';
              attempts.push('address_verifier:cdau_r2:high_conf');
            } else if (verif.confidence >= 60) {
              // Candidato adicional — CartoCiudad sigue actuando
              sourceResults.push({
                source: 'cdau',
                x: verif.x!,
                y: verif.y!,
                confidence: verif.confidence,
                responseTimeMs: 0,
              });
              attempts.push('address_verifier:cdau_r2:mid_conf');
            }
          }
        } catch (err) {
          // Degradado gracioso: nunca bloquea el flujo principal
          errors.push(`AddressVerifier: ${err}`);
        }
      }

      // ===== NIVEL 6: CARTOCIUDAD (Fallback universal) + VALIDACIÓN INE =====
      // F023 Fase 2.3: Skip si ya consultamos CartoCiudad en paralelo
      const hasCartoCiudadResult = sourceResults.some(r => r.source === 'cartociudad');
      if (!geocodingResult && options.useGenericFallback !== false && !hasCartoCiudadResult) {
        if (!this.circuitBreaker.isAvailable('CARTOCIUDAD')) {
          servicesSkipped.push('CARTOCIUDAD');
          attempts.push('cartociudad:skipped_circuit_open');
        } else {
          attempts.push('cartociudad');
          try {
            const address = options.address 
              ? `${options.address}, ${options.municipality}`
              : `${options.name}, ${options.municipality}`;
            
            // B-03: CartoCiudad solo procesa queries con morfología de dirección postal.
            // Sin tipo de vía ni número de portal devuelve la capital de provincia (falso positivo).
            if (!hasAddressPattern(address)) {
              attempts[attempts.length - 1] = 'cartociudad:skipped_no_address_pattern';
              errors.push(`CartoCiudad: skip — sin patrón de dirección: "${address}"`);
            } else {
            const cartoCiudadResult = await this.cartoCiudadGeocoder.geocode({
              address,
              municipality: options.municipality,
              province: options.province,
              // IEP-06: addressType='road' → hint callejero para mejor match en vías
              ...(_iep06RoadHint ? { type: 'callejero' } : {}),
            });

            if (cartoCiudadResult.success) {
              this.circuitBreaker.recordSuccess('CARTOCIUDAD');
              // ===== VALIDACIÓN POR CÓDIGO INE =====
              // Obtener código INE esperado para el municipio
              const codigoINEEsperado = getCodigoINE(options.municipality);
              
              // Validar que el resultado corresponde al municipio correcto
              // Esto previene errores como Colomera(Granada) vs Colomers(Girona)
              const validacionINE = validarResultadoCartoCiudad(
                {
                  muni: cartoCiudadResult.data.municipality,
                  muniCode: cartoCiudadResult.data.muniCode,
                  province: cartoCiudadResult.data.province,
                  lat: cartoCiudadResult.data.y,
                  lng: cartoCiudadResult.data.x
                },
                options.municipality,
                options.province,
                codigoINEEsperado || undefined,
                true // logearRechazos
              );
              
              if (validacionINE.valido) {
                geocodingResult = cartoCiudadResult.data;
                geocoderUsed = 'generic:cartociudad';
                
                if (cartoCiudadResult.data.confidence < 70) {
                  errors.push('CartoCiudad: match de baja confianza');
                }
              } else {
                // Resultado rechazado por validación INE
                errors.push(`CartoCiudad: ${validacionINE.error}`);
                console.warn(`[GeocodingOrchestrator] Resultado CartoCiudad rechazado por validación INE: ${validacionINE.error}`);
                // El resultado NO se acepta, continuará al siguiente nivel (Nominatim)
              }
            }
            } // end hasAddressPattern (B-03)
          } catch (err) {
            this.circuitBreaker.recordFailure('CARTOCIUDAD');
            errors.push(`CartoCiudad: ${err}`);
          }
        }
      }

      // ===== NIVEL 7: NOMINATIM OSM (último recurso) =====
      if (!geocodingResult && useOverpass) {
        if (!this.circuitBreaker.isAvailable('NOMINATIM')) {
          servicesSkipped.push('NOMINATIM');
          attempts.push('nominatim:skipped_circuit_open');
        } else {
          attempts.push('nominatim');
          try {
            const searchText = options.address || options.name;
            // BM-M1: usar nominatimGeocoder.geocode() en lugar de overpassGeocoder.geocodeWithNominatim()
            // El método anterior tragaba los 429 internamente (devolvía null), impidiendo que
            // recordFailure('NOMINATIM') se llamase → circuit breaker nunca se abría.
            const nomRes = await this.nominatimGeocoder.geocode({
              query: searchText,
              municipality: options.municipality,
              province: options.province,
            });

            if (nomRes.success && nomRes.data.confidence >= 40) {
              this.circuitBreaker.recordSuccess('NOMINATIM');
              geocodingResult = { ...nomRes.data, source: 'nominatim' };
              geocoderUsed = 'nominatim';
            } else if (!nomRes.success) {
              // 429, timeout o sin resultados → registrar fallo para activar circuit breaker
              this.circuitBreaker.recordFailure('NOMINATIM');
              errors.push(`Nominatim: ${nomRes.error?.message ?? 'sin resultado'}`);
            }
          } catch (err) {
            this.circuitBreaker.recordFailure('NOMINATIM');
            errors.push(`Nominatim: ${err}`);
          }
        }
      }

      const processingTime = Date.now() - startTime;

      // F9.01a-fix.1: Log resumen circuit breaker
      if (servicesSkipped.length > 0) {
        console.warn(`[CircuitBreaker] Servicios saltados: ${servicesSkipped.join(', ')} | Estado: ${this.circuitBreaker.getSummary()}`);
      }

      // ===== TRIANGULACIÓN ACUMULATIVA R2 (T3) =====
      // Post-oleada-2, pre-validateEnhanced. Eleva el score si ≥ 3 fuentes R2
      // convergen en radio < 25 m. Cap en 92 para no superar matches oficiales.
      if (geocodingResult && sourceResults.length >= 3) {
        const triValidator = getCrossValidator();
        const accScore = triValidator.computeAccumulatedScore(
          sourceResults,
          25,  // radio 25 m
          3    // mínimo 3 fuentes convergentes
        );
        if (accScore !== null && accScore > geocodingResult.confidence) {
          geocodingResult = { ...geocodingResult, confidence: accScore };
          console.log(
            `[TRIANG] Score elevado por triangulación R2: ${accScore} ` +
            `(${sourceResults.length} fuentes, radio 25m)`
          );
        }
      }

      // ===== VALIDACIÓN CRUZADA MULTI-FUENTE (v3.0 Enhanced) =====
      const crossValidate = options.crossValidate !== false; // Default: true
      
      if (crossValidate && sourceResults.length > 0) {
        const validator = getCrossValidator();
        // F023 Fase 2: Usar validateEnhanced() con algoritmos robustos
        // - huberCentroid: centroide robusto (reduce outliers)
        // - analyzeResultClusters: detección automática discordancias
        // - detectDiscrepancy: umbrales específicos por tipología
        // - generateRecommendation: USE_RESULT / MANUAL_REVIEW / REJECT
        const validationResult = validator.validateEnhanced(
          sourceResults,
          classification.type
        );
        
        // Usar coordenadas validadas si están disponibles
        // CATPP-BC (parte B): NO desbancar un hit catastral autoritativo
        // (polygon_parcel/refcat = parcela exacta DGC); promediarlo con el pool
        // de la oleada (centroides genéricos) es mezclar clases de precisión.
        // CENT76: un centroide municipal no es una ubicación que la validación
        // cruzada pueda confirmar; con una sola fuente le daba concordancia 1 y
        // subía su confianza de 35 a 76. Se conserva tal cual (fuente, `matchType`).
        const _centroideMunicipal = esCentroideMunicipal(geocoderUsed, geocodingResult?.source);
        // XVUNA: con una sola fuente no hay concordancia que medir, pero el
        // compuesto la contaba como perfecta (+40) y, a las fuentes que no
        // reconoce, les aplicaba la autoridad de Nominatim: subía lo flojo
        // (55 → 73) y bajaba lo bueno (92 → 86). La coordenada se adopta como hasta ahora;
        // la confianza es la de esa fuente, el mismo criterio que ya sigue
        // `validateWithSecondarySources` («SINGLE_SOURCE honesto»).
        const _confUnaFuente = validationResult.status === 'SINGLE_SOURCE'
          ? validationResult.sourceResults[0]?.confidence
          : undefined;
        const _confianzaValidada = _confUnaFuente ?? validationResult.compositeScore;
        if (validationResult.coordinates && !_catastroAutoritativo && !_centroideMunicipal) {
          geocodingResult = {
            x: validationResult.coordinates.x,
            y: validationResult.coordinates.y,
            confidence: _confianzaValidada,
            source: validationResult.primarySource || 'cross_validated',
            matchedName: geocodingResult?.matchedName || options.name,
            municipality: options.municipality,
            province: options.province,
            // CTRN: la validación cruzada reconstruye el objeto y hasta hoy se
            // llevaba por delante la parcela que la cascada hubiera atado. La
            // coordenada sí cambia aquí —es su cometido—, la parcela no tiene
            // por qué desaparecer con ella.
            ...(geocodingResult?.catastroParcel
              ? { catastroParcel: geocodingResult.catastroParcel }
              : {}),
          };
        }
        
        // TRF-06: Aplicar validación espacial antes de retornar
        const crossValidatedResult: OrchestrationResult = {
          geocoding: geocodingResult,
          classification,
          geocoderUsed: sourceResults.length > 1 && !_catastroAutoritativo && !_centroideMunicipal
            ? 'cross_validated' : geocoderUsed,
          processingTime,
          errors,
          attempts,
          originalName: options.name,
          servicesSkipped: servicesSkipped.length > 0 ? servicesSkipped : undefined,
          // Campos de validación cruzada
          // XVC1: con hit catastral autoritativo (parcela DGC exacta vía
          // polygon_parcel/refcat), las CONCLUSIONES de la XVAL contra el pool de
          // centroides no aplican (single-source/conflict no deben pedir revisión
          // manual sobre la fuente más exacta de la cascada). Se conservan
          // discrepancyMeters/sourcesConsulted/crossValidationDetail como señal
          // INFORMATIVA (transparencia: la distancia al pool es un dato útil;
          // lo erróneo era la conclusión).
          crossValidationScore: _confianzaValidada,
          crossValidationDetail: validationResult,
          validationStatus: _catastroAutoritativo
            ? 'CADASTRAL_AUTHORITATIVE'
            : validationResult.status,
          discrepancyMeters: validationResult.discrepancyMeters,
          requiresManualReview: _catastroAutoritativo
            ? false
            : validationResult.requiresManualReview,
          reviewReason: _catastroAutoritativo
            ? 'Parcela catastral DGC autoritativa (polígono/parcela o refcat); validación cruzada solo informativa'
            : validationResult.reviewReason,
          scoreBreakdown: validationResult.scoreBreakdown,
          sourcesConsulted: sourceResults.map(s => ({
            source: s.source,
            x: s.x,
            y: s.y,
            confidence: s.confidence,
            responseTimeMs: s.responseTimeMs,
          })),
          // F025: Información de extracción de dirección
          ...(addressExtractionResult && originalAddress ? {
            addressExtraction: {
              originalAddress,
              normalizedAddress: addressExtractionResult.address,
              confidence: addressExtractionResult.confidence,
              transformations: addressExtractionResult.transformations || [],
            }
          } : {}),
          // F3.07: Clasificación de geocodificabilidad
          geocodabilityCategory: geocodabilityResult.category,
          geocodabilityReason: geocodabilityResult.reason,
        };
        return this.finalizeResult(crossValidatedResult, options.municipality, options.codMun, options);
      }

      // Cuando crossValidate=true pero sourceResults vacío, incluir estructura vacía
      if (crossValidate) {
        // TRF-06: Aplicar validación espacial antes de retornar
        const noSourcesResult: OrchestrationResult = {
          geocoding: geocodingResult,
          classification,
          geocoderUsed,
          processingTime,
          errors,
          attempts,
          originalName: options.name,
          servicesSkipped: servicesSkipped.length > 0 ? servicesSkipped : undefined,
          // Campos de validación cruzada (vacíos por falta de fuentes)
          // XVC1: 'NO_RESULTS' sobre un hit catastral autoritativo es engañoso
          // («Sin resultado» en el dashboard siendo parcela DGC exacta).
          crossValidationScore: geocodingResult ? geocodingResult.confidence : 0,
          validationStatus: _catastroAutoritativo ? 'CADASTRAL_AUTHORITATIVE' : 'NO_RESULTS',
          discrepancyMeters: null,
          requiresManualReview: _catastroAutoritativo ? false : !geocodingResult,
          reviewReason: _catastroAutoritativo
            ? 'Parcela catastral DGC autoritativa (polígono/parcela o refcat); sin fuentes adicionales para validación cruzada'
            : (sourceResults.length === 0 ? 'Sin fuentes disponibles para validación cruzada' : undefined),
          sourcesConsulted: [],
          // F025: Información de extracción de dirección
          ...(addressExtractionResult && originalAddress ? {
            addressExtraction: {
              originalAddress,
              normalizedAddress: addressExtractionResult.address,
              confidence: addressExtractionResult.confidence,
              transformations: addressExtractionResult.transformations || [],
            }
          } : {}),
          // F3.07: Clasificación de geocodificabilidad
          geocodabilityCategory: geocodabilityResult.category,
          geocodabilityReason: geocodabilityResult.reason,
        };

        // Issue #14-B: Centroide fallback universal — cascada total sin fuentes y sin geocodingResult
        // CENTOR: no actúa si el documento ya trae coordenada (`hasDocumentCoords`).
        if (options.codMun && !geocodingResult && !options.hasDocumentCoords) {
          try {
            const { INER2Service } = await import('./specialized/INER2Service');
            const ineService = INER2Service.getInstance();
            // INECOLD (19-sep-2026): sin esperar la carga, `getCentroide` devuelve null
            // si el INE R2 aún no está en memoria y la fila se queda sin centroide según
            // el orden de carga (`OBS-GEO-M3-CENTROIDE-SIN-INITIALIZE-…`). `initialize()`
            // es no-op si ya cargó y comparte la carga en curso (ver `esperarINE`).
            await this.esperarINE(ineService);
            const centroide = ineService.getCentroide(options.codMun);
            if (centroide) {
              const centroidResult: OrchestrationResult = {
                ...noSourcesResult,
                geocoding: {
                  x: centroide.x,
                  y: centroide.y,
                  confidence: 20,
                  source: 'centroid',
                  matchedName: options.municipality ?? options.codMun,
                },
              };
              return this.finalizeResult(centroidResult, options.municipality, options.codMun, options);
            }
          } catch {
            // INER2Service no disponible o error → continuar con noSourcesResult
          }
        }

        return this.finalizeResult(noSourcesResult, options.municipality, options.codMun, options);
      }

      // Sin validación cruzada: comportamiento legacy
      // TRF-06: Aplicar validación espacial antes de retornar
      const legacyResult: OrchestrationResult = {
        geocoding: geocodingResult,
        classification,
        geocoderUsed,
        processingTime,
        errors,
        attempts,
        originalName: options.name,
        servicesSkipped: servicesSkipped.length > 0 ? servicesSkipped : undefined,
        // F025: Información de extracción de dirección
        ...(addressExtractionResult && originalAddress ? {
          addressExtraction: {
            originalAddress,
            normalizedAddress: addressExtractionResult.address,
            confidence: addressExtractionResult.confidence,
            transformations: addressExtractionResult.transformations || [],
          }
        } : {}),
        // F3.07: Clasificación de geocodificabilidad
        geocodabilityCategory: geocodabilityResult.category,
        geocodabilityReason: geocodabilityResult.reason,
      };
      return this.finalizeResult(legacyResult, options.municipality, options.codMun, options);

    } catch (error) {
      const processingTime = Date.now() - startTime;
      errors.push(`Error crítico: ${error}`);

      return enrichWithScoringV2({
        geocoding: null,
        classification: {
          type: InfrastructureType.GENERIC,
          confidence: ClassificationConfidence.NONE,
          keywords: [] as string[]
        },
        geocoderUsed: 'none',
        processingTime,
        originalName: options.name,
        errors,
        attempts
      });
    }
  }

  /**
   * CAT-12: Determina si se debe intentar geocodificación vía Catastro PTEL
   * 
   * Criterios:
   * 1. La dirección/nombre contiene patrones catastrales (Pol/Parcela, SIGPAC, ref completa)
   * 2. Es infraestructura con uso catastral (Y=Sanidad, E=Cultural, P=Público, K=Deportivo...)
   * 3. Es infraestructura rural aislada (HYDRAULIC, INDUSTRIAL sin dirección postal)
   * 
   * @param options - Opciones de geocodificación con nombre, dirección, municipio
   * @param type - Tipo de infraestructura clasificada
   * @returns true si debe intentarse Catastro PTEL
   */
  private shouldTryCatastro(
    options: OrchestrationOptions, 
    type: InfrastructureType
  ): boolean {
    // 1. Verificar si hay patrón catastral explícito en nombre/dirección
    const hasPattern = hasCatastralPatternInFields(
      options.name, 
      options.address
    );
    
    if (hasPattern) {
      console.log(`[CAT-12] Patrón catastral detectado en: "${options.address || options.name}"`);
      return true;
    }
    
    // 2. Tipologías con alta probabilidad de ubicación rural (Pol/Parcela)
    const ruralTypes = [
      InfrastructureType.HYDRAULIC,    // Depósitos, pozos, balsas
      InfrastructureType.INDUSTRIAL,   // Naves aisladas, canteras
      InfrastructureType.ENERGY,       // Parques solares/eólicos
      InfrastructureType.RSU,          // Vertederos, puntos limpios
    ];
    
    // Solo para tipos rurales SIN dirección postal estructurada
    if (ruralTypes.includes(type)) {
      const address = options.address || '';
      // Si no tiene número de portal ni calle clara, probar Catastro
      const hasStreetNumber = /\d+/.test(address) && /calle|c\/|avda|plaza|paseo/i.test(address);
      
      if (!hasStreetNumber) {
        console.log(`[CAT-12] Infraestructura rural sin dirección postal: ${type}`);
        return true;
      }
    }
    
    return false;
  }

  /**
   * Intenta geocodificación con geocodificador especializado según tipo.
   *
   * @param subtype  Sub-discriminador opcional para `InfrastructureType.EMERGENCY`
   *                 (γ híbrido cluster UPCLS3 eslabón 40):
   *                 `'SOCORRO'`      → enruta a `WFSPuestoSocorroGeocoder` (DERA G12_37)
   *                 `'HUMANITARIA'`  → enruta a `WFSOrganizacionesHumanitariasGeocoder` (DERA G12_36)
   *                 `undefined`      → cascada security genérica (comportamiento legacy)
   *                 Procede del campo homónimo de `ClassificationResult` upstream.
   */
  private async trySpecializedGeocoder(
    type: InfrastructureType,
    options: WFSSearchOptions,
    attempts: string[],
    subtype?: 'SOCORRO' | 'HUMANITARIA',
    /** CENTOR: el documento trae X/Y → ni social ni M3 fabrican centroide municipal. */
    hasDocumentCoords = false
  ): Promise<{ result: GeocodingResult | null; geocoder: string }> {
    
    let result: GeocodingResult | null = null;
    let geocoder = 'none';

    switch (type) {
      case InfrastructureType.HEALTH:
        attempts.push('specialized:health');
        result = await this.healthGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:health' : geocoder;
        break;

      case InfrastructureType.EDUCATION:
        // WECB 26-may: redirigido de WFSEducationGeocoder (CKAN datastore_search
        // muerto desde el commit 418d52d5, 23-nov-2025 — HTTP «Resource not
        // found») a EduCentersR2Service (~6000+ centros, R2 /edu-centers/,
        // EPSG:25830). Sin codMun el servicio R2 devuelve null igual que el
        // geocoder CKAN roto → cae al fallback genérico. Cierra
        // OBS-FT-WFS-EDUCATION-CKAN-BROKEN (HX-FALLBACK-ENMASCARA-BUG).
        attempts.push('specialized:education:r2');
        result = options.codMun
          ? await getEduCentersR2Service().geocode({
              codINE: options.codMun,
              name: options.name,
            })
          : null;
        geocoder = result ? 'specialized:education:r2' : geocoder;
        break;

      case InfrastructureType.CULTURAL:
        attempts.push('specialized:cultural');
        result = await this.culturalGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:cultural' : geocoder;
        break;

      case InfrastructureType.POLICE:
      case InfrastructureType.FIRE:
        attempts.push('specialized:security');
        result = await this.securityGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:security' : geocoder;
        break;

      // ===== F2 oleada-2 cluster UPCLS3 eslabón 40 (γ híbrido) =====
      // MAREMOTO sale del bucket SEGURIDAD: es su propio InfrastructureType y
      // se enruta directamente a `WFSMaremotosGeocoder` (DERA G12 · g12_38).
      case InfrastructureType.MAREMOTO:
        attempts.push('specialized:maremoto');
        result = await this.maremotosGeocoder.geocodeLegacy({
          name: options.name,
          municipality: options.municipality,
          province: options.province,
          codMun: options.codMun,
        });
        geocoder = result ? 'specialized:maremoto' : geocoder;
        break;

      // EMERGENCY con sub-discriminación (γ híbrido UPCLS3):
      // - `subtype === 'SOCORRO'`     → WFSPuestoSocorroGeocoder (DERA G12_37)
      // - `subtype === 'HUMANITARIA'` → WFSOrganizacionesHumanitariasGeocoder (DERA G12_36)
      // - sin subtype                 → cascada security genérica (comportamiento legacy)
      // El parámetro `subtype` lo propaga el caller upstream desde
      // `classification.subtype` (asignado por `InfrastructureClassifier`).
      case InfrastructureType.EMERGENCY: {
        if (subtype === 'SOCORRO') {
          attempts.push('specialized:puestoSocorro');
          result = await this.puestoSocorroGeocoder.geocodeLegacy({
            name: options.name,
            municipality: options.municipality,
            province: options.province,
            codMun: options.codMun,
          });
          geocoder = result ? 'specialized:puestoSocorro' : geocoder;
        } else if (subtype === 'HUMANITARIA') {
          attempts.push('specialized:organizacionHumanitaria');
          result = await this.organizacionesHumanitariasGeocoder.geocodeLegacy({
            name: options.name,
            municipality: options.municipality,
            province: options.province,
            codMun: options.codMun,
          });
          geocoder = result ? 'specialized:organizacionHumanitaria' : geocoder;
        } else {
          attempts.push('specialized:security');
          result = await this.securityGeocoder.geocodeWithAutoLayer(options);
          geocoder = result ? 'specialized:security' : geocoder;
        }
        break;
      }

      case InfrastructureType.HYDRAULIC:
        // F3-CAUCES: Detectar cauces hidrográficos (ríos, ramblas, arroyos, barrancos)
        // Son geometrías LineString, requieren geocodificador especializado
        if (isWatercourse(options.name)) {
          attempts.push('specialized:waterways');
          // Obtener código INE del municipio para consulta WFS
          const codMun = options.codMun;
          if (codMun) {
            result = await this.waterwaysGeocoder.geocodeWithAutoLayer({
              name: options.name,
              codMun,
            });
            geocoder = result ? 'specialized:waterways' : geocoder;
          }
          
          // Fallback a NGA si DERA Waterways no tiene resultado
          if (!result) {
            attempts.push('specialized:waterways_nga_fallback');
            result = await this.ngaGeocoder.geocodeWithTypeDetection({
              name: options.name,
              municipality: options.municipality,
              province: options.province
            });
            geocoder = result ? 'specialized:waterways_nga' : geocoder;
          }
          break;
        }
        
        // Infraestructuras hidráulicas puntuales (depósitos, EDAR, captaciones)
        attempts.push('specialized:hydraulic');
        result = await this.hydraulicGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:hydraulic' : geocoder;
        
        // F3-HYDRAULIC: Fallback a HydraulicMunicipalGeocoder (NGA→OSM→Catastro)
        // Para depósitos, pozos, manantiales municipales no cubiertos por REDIAM
        if (!result && options.municipality) {
          attempts.push('specialized:hydraulic_municipal');
          result = await this.hydraulicMunicipalGeocoder.geocode({
            name: options.name,
            municipality: options.municipality,
            province: options.province
          });
          geocoder = result ? 'specialized:hydraulic_municipal' : geocoder;
        }
        break;

      case InfrastructureType.ENERGY:
        attempts.push('specialized:energy');
        result = await this.energyGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:energy' : geocoder;
        break;

      case InfrastructureType.SPORTS:
        attempts.push('specialized:sports');
        result = await this.sportsGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:sports' : geocoder;
        break;

      case InfrastructureType.TELECOM:
        // F3.TELECOM: Torres comunicación con cascada DERA G9 + OSM + NGA
        attempts.push('specialized:telecom');
        result = await this.torresComGeocoder.geocodeWithCascade({
          name: options.name,
          municipality: options.municipality,
          province: options.province
        });
        // CENT76: el L3 (centroide municipal DERA) se declara con el mismo nombre
        // que en el lote de P2 (`convertDistributedResult`), que ya reconoce
        // `esCentroideMunicipal`; con `specialized:telecom` pasaba por una ubicación.
        geocoder = result
          ? (result.matchType === 'centroid_fallback_L3'
              ? 'specialized:telecom:centroid_fallback'
              : 'specialized:telecom')
          : geocoder;
        break;

      case InfrastructureType.VIAL:
        // VIAL deprecado WTEP-DEPRECATE 17-may-2026: transportGeocoder
        // eliminado (endpoint DERA_g9_transporte_com HTTP 404 permanente
        // desde 19-mar-2026 commit 88d2cc432 `knownDead`). Cae al fallback
        // genérico CartoCiudad/Catastro tras este break.
        // Ver .ptel/AUDIT_WTEP_20260517.md.
        attempts.push('vial:deprecated');
        break;

      case InfrastructureType.INDUSTRIAL:
        attempts.push('specialized:industrial');
        result = await this.industrialGeocoder.geocodeWithAutoLayer(options);
        geocoder = result ? 'specialized:industrial' : geocoder;
        break;

      case InfrastructureType.SOCIAL: {
        // L3-1: Albergues, refugios, naves feriales, locales sociales.
        // Cascada tipológica: EDU (colegios usados como albergue) →
        // SPORTS (pabellones/polideportivos) → CULTURAL (locales sociales).
        // Sin case previo → caía a default:break sin aprovechar ningún geocodificador.
        const _eduTokens = /\b(ies|ceip|cpr|colegio|instituto|escuela|centro\s+educativo)\b/i;
        const _sportTokens = /\b(pabellón|polideportivo|estadio|piscina|frontón|pista\s+dep)\b/i;

        if (_eduTokens.test(options.name ?? '')) {
          // WECB 26-may: rama EDU redirigida a EduCentersR2Service
          // (ver case EDUCATION). Sin codMun → null → continúa la cascada
          // SOCIAL (SPORTS → CULTURAL → AGOL → centroide).
          attempts.push('specialized:social:edu:r2');
          result = options.codMun
            ? await getEduCentersR2Service().geocode({
                codINE: options.codMun,
                name: options.name,
              })
            : null;
          geocoder = result ? 'specialized:social:edu:r2' : geocoder;
          if (result) break;
        }

        if (!result && _sportTokens.test(options.name ?? '')) {
          attempts.push('specialized:social:sports');
          result = await this.sportsGeocoder.geocodeWithAutoLayer(options);
          geocoder = result ? 'specialized:social:sports' : geocoder;
          if (result) break;
        }

        if (!result) {
          attempts.push('specialized:social:cultural');
          result = await this.culturalGeocoder.geocodeWithAutoLayer(options);
          geocoder = result ? 'specialized:social:cultural' : geocoder;
        }

        // P1 Paso 4: AGOL R2 — búsqueda fuzzy por nombre sin dirección postal
        if (!result && options.codMun) {
          attempts.push('specialized:social:agol');
          const agolResult = await geocodeWithAgolR2(options.name ?? '', {
            codINE: options.codMun,
          });
          if (agolResult && agolResult.confidence >= 45) {
            result = agolResult;
            geocoder = 'specialized:social:agol';
          }
        }

        // P1 Paso 5: centroide municipal — último recurso, conf=22
        // CENTOR: no actúa si el documento ya trae coordenada (`hasDocumentCoords`).
        if (!result && options.codMun && !hasDocumentCoords) {
          attempts.push('specialized:social:centroide');
          try {
            const ineService = getINER2Service();
            // INECOLD (19-sep-2026): sin esperar la carga, `getCentroide` devuelve null
            // si el INE R2 aún no está en memoria y la fila se queda sin centroide según
            // el orden de carga (`OBS-GEO-M3-CENTROIDE-SIN-INITIALIZE-…`). `initialize()`
            // es no-op si ya cargó y comparte la carga en curso (ver `esperarINE`).
            await this.esperarINE(ineService);
            const centroide = ineService.getCentroide(options.codMun);
            if (centroide) {
              result = {
                x: centroide.x,
                y: centroide.y,
                confidence: 22,
                source: 'iner2:centroide-municipio' as GeocodingSource,
                matchedName: options.municipality ?? options.name ?? '',
                municipality: options.municipality,
                province: options.province,
              };
              geocoder = 'specialized:social:centroide';
            }
          } catch {
            // centroide es opcional, no bloquear
          }
        }
        break;
      }

      case InfrastructureType.FUEL:
        // F2.T8: Gasolineras, electrolineras, GLP, GNC, GNL, hidrógeno.
        // Fuente única: MITECO (post-S4-FUEL-DEPRECATE 10-may, ver MAPEO-AUDIT §3.4 —
        // el snapshot Catastro R2 no contiene gasolineras: 0 hits con keywords
        // gasolin/combustib/hidrocarbu en 143 destino_cod sobre ~131 213 parcelas
        // auditadas en 3 capitales). Si MITECO falla, cae al M3 centroide universal.
        attempts.push('specialized:fuel:miteco');
        const mitecoResult = await this.gasolinerasGeocoder.geocode({
          name: options.name,
          municipality: options.municipality,
          province: options.province
        });
        // Extraer datos si success, null si failure (TypedResult → GeocodingResult)
        if (mitecoResult.success && mitecoResult.data) {
          result = mitecoResult.data;
          geocoder = 'specialized:fuel:miteco';
        }
        break;

      case InfrastructureType.GENERIC:
      default: {
        // PROP-D (DISAMV-REFINE-01): intento DERA R2 antes de M3 para tipos GENERIC.
        // INDUSTRIAL, FUEL, TELECOM, SOCIAL ya tienen geocodificadores propios — no tocar.
        // GENERIC era el único tipo sin geocodificador especializado → caía directo a M3 (conf=22).
        // Umbral conf≥55 (más alto que CDAU-R2 conf≥45) para reducir falsos positivos en nombres ambiguos.
        // Evidencia BM.03 corpus v4: 113 infras GENERIC terminaron en centroide conf=20.
        if (options.codMun && options.name && this.circuitBreaker.isAvailable('DERA_R2')) {
          attempts.push('prop-d:generic:dera_r2');
          try {
            const deraResult = await getDERAR2Geocoder().geocode({
              name: options.name,
              codINE: options.codMun,
              tipologia: 'OTRO',
            });
            if (deraResult && deraResult.confidence >= 55) {
              result = deraResult;
              geocoder = 'prop-d:generic:dera_r2';
              console.log(`[PROP-D] ✅ GENERIC DERA R2: "${deraResult.matchedName}" conf=${deraResult.confidence}`);
            }
          } catch {
            this.circuitBreaker.recordFailure('DERA_R2');
            console.warn('[PROP-D] DERA R2 error para GENERIC (non-blocking) — circuit breaker notificado');
          }
        }
        break;
      }
    }

    // ===== M3: CENTROIDE MUNICIPAL FALLBACK UNIVERSAL =====
    // Cuando TODOS los geocodificadores tipológicos fallan (result === null),
    // asignar el centroide del municipio como último recurso con conf=22.
    // - No contamina T04/T05: solo actúa cuando no hay coordenada del documento
    //   (`hasDocumentCoords`; hasta CENTOR 12-sep-2026 esta línea describía una
    //   protección que no existía — `OBS-GEO-M3-CENTROIDE-IGNORA-COORD-DOCUMENTO`).
    // - SOCIAL queda excluido implícitamente: tiene su propio centroide interno
    //   (specialized:social:centroide) que asigna result antes de llegar aquí.
    // - Sin codMun no hay centroide municipal posible → no actúa.
    if (!result && options.codMun && !hasDocumentCoords) {
      attempts.push('specialized:universal:centroide');
      try {
        const ineService = getINER2Service();
        // INECOLD (19-sep-2026): sin esperar la carga, `getCentroide` devuelve null
        // si el INE R2 aún no está en memoria y la fila se queda sin centroide según
        // el orden de carga (`OBS-GEO-M3-CENTROIDE-SIN-INITIALIZE-…`). `initialize()`
        // es no-op si ya cargó y comparte la carga en curso (ver `esperarINE`).
        await this.esperarINE(ineService);
        const centroide = ineService.getCentroide(options.codMun);
        if (centroide) {
          result = {
            x: centroide.x,
            y: centroide.y,
            confidence: 22,
            source: 'iner2:centroide-municipio' as GeocodingSource,
            matchedName: options.municipality ?? options.name ?? '',
            municipality: options.municipality,
            province: options.province,
          };
          geocoder = 'specialized:universal:centroide';
          console.log(
            `[M3] Centroide universal fallback ${options.codMun}: X=${centroide.x} Y=${centroide.y} ` +
            `(tipo=${options.name?.substring(0, 30) ?? '?'})`
          );
        }
      } catch {
        // centroide es opcional, no bloquear el pipeline
      }
    }
    // ===== FIN M3 =====

    return { result, geocoder };
  }

  /**
   * Intenta geocodificadores WFS alternativos según tipología.
   * 
   * FUT-02 Tarea 4: Fallback tipológico mejorado
   * 
   * Cuando el geocodificador primario no encuentra resultado,
   * este método prueba geocodificadores relacionados antes de
   * saltar a los genéricos (CDAU, CartoCiudad).
   * 
   * Los resultados de fallback tienen confianza reducida (-10%)
   * para indicar que no provienen del geocodificador primario.
   * 
   * @param originalType - Tipo original que falló
   * @param options - Opciones de búsqueda WFS
   * @param attempts - Array de intentos (se modifica in-place)
   * @param errors - Array de errores (se modifica in-place)
   * @returns Resultado de geocodificación con confianza penalizada
   */
  private async tryFallbackByTypology(
    originalType: InfrastructureType,
    options: WFSSearchOptions,
    attempts: string[],
    errors: string[],
    /** CENTOR: se propaga a `trySpecializedGeocoder` (puerta del centroide). */
    hasDocumentCoords = false
  ): Promise<{ result: GeocodingResult | null; geocoder: string }> {
    // Obtener fallbacks para este tipo
    const fallbackTypes = TYPOLOGY_FALLBACKS[originalType];
    
    if (!fallbackTypes || fallbackTypes.length === 0) {
      return { result: null, geocoder: 'none' };
    }

    console.log(
      `[FUT-02] Fallback tipológico: ${originalType} → [${fallbackTypes.join(', ')}]`
    );

    // Probar cada fallback en orden
    for (const fallbackType of fallbackTypes) {
      attempts.push(`fallback:${fallbackType.toLowerCase()}`);
      
      try {
        const { result, geocoder } = await this.trySpecializedGeocoder(
          fallbackType,
          options,
          [], // No añadir a attempts duplicados
          undefined,
          hasDocumentCoords
        );

        if (result && result.confidence >= 50) {
          // Aplicar penalización de confianza
          const penalizedConfidence = Math.max(
            result.confidence - FALLBACK_CONFIDENCE_PENALTY,
            40 // Mínimo 40% para fallbacks
          );

          console.log(
            `[FUT-02] ✅ Fallback exitoso: ${originalType} → ${fallbackType} ` +
            `(confianza: ${result.confidence} → ${penalizedConfidence})`
          );

          return {
            result: {
              ...result,
              confidence: penalizedConfidence,
              source: `FALLBACK_${result.source}`,
              metadata: {
                ...result.metadata,
                originalType,
                fallbackType,
                penaltyApplied: FALLBACK_CONFIDENCE_PENALTY
              }
            },
            geocoder: `fallback:${geocoder}`
          };
        }
      } catch (err) {
        errors.push(`FALLBACK_${fallbackType}: ${err}`);
        console.warn(`[FUT-02] Error en fallback ${fallbackType}:`, err);
      }
    }

    // Ningún fallback encontró resultado
    return { result: null, geocoder: 'none' };
  }

  // mapTypeToLocalCategories: importado de ./orchestrator

  // looksLikeToponym: importado de ./orchestrator

  // isSportsFacility: importado de ./orchestrator

  // isTelecomInfrastructure: importado de ./orchestrator

  // findMatchingTower: importado de ./orchestrator

  // extractOperator: importado de ./orchestrator

  // ========================================================================
  // F9.01a-fix.2: HEALTH CHECK PRE-BATCH
  // ========================================================================

  /**
   * Ejecuta health check contra servicios online antes del batch.
   * Si un servicio falla, abre su CB inmediatamente (ahorra ~16 s
   * de timeout en las 2 primeras infras).
   *
   * Invocable desde UI antes de geocodeBatch().
   */
  public async runPreBatchHealthCheck(
    options?: { timeout?: number }
  ): Promise<HealthCheckResult[]> {
    console.log('[HealthCheck] Iniciando verificación pre-batch de servicios online...');
    const results = await this.healthChecker.checkServices(this.circuitBreaker, options);
    const failed = results.filter(r => !r.available);
    if (failed.length > 0) {
      console.warn(
        `[HealthCheck] Servicios degradados: ${failed.map(f => `${f.service}(${f.error})`).join(', ')}`
      );
    } else {
      console.log(`[HealthCheck] Todos los servicios online disponibles (${results.map(r => `${r.service}:${r.latencyMs}ms`).join(', ')})`);
    }
    return results;
  }

  /**
   * Geocodifica una entrada como punto de encuentro ante maremotos
   * (WAVE-1 DERA G12 capa `g12_38` · 715 features litorales).
   *
   * Desde el cluster UPCLS3 (eslabón 40 · 20-may-2026), el flujo automático
   * vía `InfrastructureClassifier` upstream **YA enruta** a este
   * geocodificador cuando `classification.type === InfrastructureType.MAREMOTO`
   * (cierre de `OBS-FT-MAREMOTOS-UPSTREAM-CLASSIFIER`). Este método público se
   * mantiene para invocación explícita por consumidores que ya conocen la
   * tipología sin pasar por el classifier (p. ej. flujos batch con
   * tipología declarada).
   *
   * Retorno tipado como `GeocodingResult | null` para coherencia con el
   * resto del flujo cascada del orchestrator (precedente: `securityGeocoder.geocodeLegacy`
   * usado en flujo POLICE/FIRE legacy).
   */
  public async geocodeMaremoto(
    options: MaremotosSearchOptions
  ): Promise<GeocodingResult | null> {
    return this.maremotosGeocoder.geocodeLegacy(options);
  }

  /**
   * Geocodifica un puesto de socorro vía `WFSPuestoSocorroGeocoder`
   * (DERA G12 · g12_37 · 533 features litorales · WAVE-2 cluster F2 SEG
   * ampliado oleada-2 rumbo 2 BBDD enriquecida).
   *
   * Desde el cluster UPCLS3 (eslabón 40 · 20-may-2026), el flujo automático
   * vía `InfrastructureClassifier` upstream **YA enruta** a este
   * geocodificador cuando `classification.type === EMERGENCY` y
   * `classification.subtype === 'SOCORRO'` (γ híbrido · cierre de
   * `OBS-FT-WAVE2-UPSTREAM-CLASSIFIER-SOCORRO`). Este método público se
   * mantiene para invocación explícita por consumidores que ya conocen la
   * sub-tipología sin pasar por el classifier.
   *
   * Retorno tipado como `GeocodingResult | null` análogo a `geocodeMaremoto`.
   */
  public async geocodePuestoSocorro(
    options: PuestoSocorroSearchOptions
  ): Promise<GeocodingResult | null> {
    return this.puestoSocorroGeocoder.geocodeLegacy(options);
  }

  /**
   * Geocodifica una organización humanitaria vía
   * `WFSOrganizacionesHumanitariasGeocoder` (DERA G12 · g12_36 · 107
   * features dataset completo · WAVE-2 cluster F2 SEG ampliado oleada-2
   * rumbo 2 BBDD enriquecida).
   *
   * Desde el cluster UPCLS3 (eslabón 40 · 20-may-2026), el flujo automático
   * vía `InfrastructureClassifier` upstream **YA enruta** a este
   * geocodificador cuando `classification.type === EMERGENCY` y
   * `classification.subtype === 'HUMANITARIA'` (γ híbrido · cierre de
   * `OBS-FT-WAVE2-UPSTREAM-CLASSIFIER-HUMANITARIAS`). Este método público se
   * mantiene para invocación explícita por consumidores que ya conocen la
   * sub-tipología sin pasar por el classifier (p. ej. flujos batch que
   * distinguen «Cruz Roja» de «Cáritas» antes de geocodificar).
   *
   * Retorno tipado como `GeocodingResult | null` análogo a `geocodeMaremoto`.
   */
  public async geocodeOrganizacionHumanitaria(
    options: OrganizacionesHumanitariasSearchOptions
  ): Promise<GeocodingResult | null> {
    return this.organizacionesHumanitariasGeocoder.geocodeLegacy(options);
  }

  /**
   * Geocodifica múltiples infraestructuras en batch
   * 
   * F3.T3.3: Agrupa infraestructuras TELECOM por municipio para 
   * distribución inteligente de torres OSM.
   */
  public async geocodeBatch(
    infrastructures: OrchestrationOptions[],
    onProgress?: (current: number, total: number) => void
  ): Promise<OrchestrationResult[]> {
    const BATCH_SIZE = 5;
    const results: OrchestrationResult[] = new Array(infrastructures.length);
    
    // ===== F3.T3.3: DETECTAR Y AGRUPAR TELECOM POR MUNICIPIO =====
    const telecomGroups = new Map<string, Array<OrchestrationOptions & { _originalIndex: number }>>();
    const nonTelecomInfras: Array<OrchestrationOptions & { _originalIndex: number }> = [];
    
    for (let i = 0; i < infrastructures.length; i++) {
      const infra = infrastructures[i];
      const classification = this.classifier.classify(infra.name);
      
      if (classification.type === InfrastructureType.TELECOM) {
        const key = `${infra.municipality}_${infra.province}`.toLowerCase();
        const group = telecomGroups.get(key) || [];
        group.push({ ...infra, _originalIndex: i });
        telecomGroups.set(key, group);
      } else {
        nonTelecomInfras.push({ ...infra, _originalIndex: i });
      }
    }
    
    let processedCount = 0;
    
    // ===== PROCESAR GRUPOS TELECOM CON DISTRIBUCIÓN INTELIGENTE =====
    for (const [key, telecomInfras] of telecomGroups.entries()) {
      // Solo usar distribución si hay ≥2 infraestructuras
      if (telecomInfras.length >= 2) {
        console.log(
          `[F3.T3.3] Procesando grupo TELECOM: ${telecomInfras.length} infraestructuras en ${key}`
        );
        
        const telecomResults = await this.geocodeTelecomBatch(
          telecomInfras,
          telecomInfras[0].municipality,
          telecomInfras[0].province,
          telecomInfras[0].codMun
        );
        
        // Insertar resultados en posiciones originales
        for (const { result, originalIndex } of telecomResults) {
          results[originalIndex] = result;
          processedCount++;
          onProgress?.(processedCount, infrastructures.length);
        }
      } else {
        // Solo 1 TELECOM en este municipio → procesar individualmente
        nonTelecomInfras.push(...telecomInfras);
      }
    }
    
    // ===== PROCESAR NO-TELECOM SECUENCIALMENTE =====
    for (let i = 0; i < nonTelecomInfras.length; i += BATCH_SIZE) {
      const batch = nonTelecomInfras.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map(opts => this.geocode(opts))
      );
      
      // Insertar resultados en posiciones originales
      for (let j = 0; j < batchResults.length; j++) {
        results[batch[j]._originalIndex] = batchResults[j];
        processedCount++;
      }
      
      onProgress?.(processedCount, infrastructures.length);

      if (i + BATCH_SIZE < nonTelecomInfras.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return results;
  }

  // =========================================================================
  // F3.T3.3: DISTRIBUCIÓN INTELIGENTE DE MÚLTIPLES ANTENAS TELECOM
  // =========================================================================

  /**
   * F3.T3.3: Geocodifica múltiples infraestructuras TELECOM del mismo municipio
   * con distribución inteligente de torres OSM.
   * 
   * Ventajas sobre geocodeBatch individual:
   * - Una sola llamada Overpass por municipio (vs N llamadas)
   * - Distribución round-robin evita asignar misma torre a múltiples antenas
   * - Detección automática de ubicaciones compartidas
   * - Badges UI informativos (L1_DISTRIBUTED, L1_SHARED)
   */
  private async geocodeTelecomBatch(
    telecomInfras: Array<OrchestrationOptions & { _originalIndex: number }>,
    municipality: string,
    province: string,
    codMun?: string
  ): Promise<Array<{ result: OrchestrationResult; originalIndex: number }>> {
    const startTime = Date.now();
    
    // Convertir a TelecomInfrastructure para el geocodificador
    const telecomInputs: TelecomInfrastructure[] = telecomInfras.map((infra, idx) => ({
      id: `telecom_${idx}_${infra._originalIndex}`,
      name: infra.name,
      address: infra.address,
      operator: extractOperator(infra.name) || undefined,
    }));
    
    console.log(
      `[F3.T3.3] geocodeTelecomBatch: ${telecomInputs.length} infraestructuras en ${municipality}`
    );
    
    // Llamar a distribución inteligente
    const distributedResults = await this.torresComGeocoder.geocodeMobileDistributed(
      telecomInputs,
      municipality,
      province,
      codMun
    );
    
    const processingTime = Date.now() - startTime;
    
    // Convertir a OrchestrationResult[]
    const results: Array<{ result: OrchestrationResult; originalIndex: number }> = [];
    
    for (let i = 0; i < distributedResults.length; i++) {
      const distResult = distributedResults[i];
      const originalOpts = telecomInfras[i];
      
      const orchResult = this.convertDistributedResult(
        distResult,
        originalOpts,
        processingTime / telecomInputs.length
      );
      
      results.push({
        result: orchResult,
        originalIndex: originalOpts._originalIndex
      });
    }
    
    // Log resumen
    const shared = distributedResults.filter(r => r.sharedLocation).length;
    const distributed = distributedResults.filter(r => 
      r.metadata?.approximationLevel === 'L1_DISTRIBUTED'
    ).length;
    console.log(
      `[F3.T3.3] Resultados: ${distributed} distribuidos, ${shared} compartidos (${processingTime}ms)`
    );
    
    return results;
  }

  /**
   * Convierte DistributedGeocodingResult a OrchestrationResult
   */
  private convertDistributedResult(
    distResult: DistributedGeocodingResult,
    originalOpts: OrchestrationOptions,
    processingTimeMs: number
  ): OrchestrationResult {
    const classification = this.classifier.classify(originalOpts.name);
    
    // Determinar geocoder usado basado en metadata
    let geocoderUsed = 'specialized:telecom:distributed';
    const approxLevel = distResult.metadata?.approximationLevel as string | undefined;
    if (approxLevel === 'L0_ADDRESS_MATCH') {
      geocoderUsed = 'specialized:telecom:address_match';
    } else if (distResult.sharedLocation) {
      geocoderUsed = 'specialized:telecom:shared';
    } else if (approxLevel === 'L3_CENTROID_FALLBACK') {
      geocoderUsed = 'specialized:telecom:centroid_fallback';
    }

    // CENTOR (12-sep-2026): el centroide L3 del distribuidor no vale como
    // resultado si el documento ya trae coordenada (`hasDocumentCoords`).
    // Se devuelve sin geocoding, como cualquier otro productor de centroide.
    if (approxLevel === 'L3_CENTROID_FALLBACK' && originalOpts.hasDocumentCoords) {
      return {
        geocoding: null,
        classification,
        geocoderUsed: 'specialized:telecom:distributed',
        processingTime: processingTimeMs,
        errors: [],
        attempts: ['specialized:telecom:distributed'],
        originalName: originalOpts.name,
        validationStatus: 'UNCERTAIN',
        discrepancyMeters: null,
        requiresManualReview: false,
        sourcesConsulted: [],
      };
    }
    
    // Construir GeocodingResult
    // F3.T3.3: sharedWith en nivel superior para SharedLocationBadge UI
    const sharedWithArray = distResult.sharedWith;
    const hasSharedWith = Array.isArray(sharedWithArray) && sharedWithArray.length > 0;
    
    const geocoding: GeocodingResult = {
      x: distResult.x,
      y: distResult.y,
      confidence: distResult.confidence,
      source: distResult.source,
      matchedName: distResult.matchedName,
      municipality: originalOpts.municipality,
      province: originalOpts.province,
      muniCode: originalOpts.codMun,
      metadata: {
        ...distResult.metadata,
        // F3.T3.3: Nivel superior para TelecomIndicators.tsx
        approximationLevel: approxLevel,
        osmId: distResult.osmId,
        sharedWith: hasSharedWith ? sharedWithArray : undefined,
        sharedCount: hasSharedWith ? sharedWithArray.length + 1 : undefined,
        // Detalle completo en telecomDistribution
        telecomDistribution: {
          sharedLocation: distResult.sharedLocation || false,
          sharedWith: distResult.sharedWith,
          assignmentOrder: distResult.assignmentOrder,
          approximationLevel: approxLevel,
          towerType: distResult.towerType,
          osmId: distResult.osmId,
        }
      }
    };
    
    // Validación status basada en confianza
    let validationStatus = 'LIKELY_VALID';
    if (distResult.confidence >= 70) {
      validationStatus = 'CONFIRMED';
    } else if (distResult.confidence < 50) {
      validationStatus = 'UNCERTAIN';
    }
    
    return {
      geocoding,
      classification,
      geocoderUsed,
      processingTime: processingTimeMs,
      errors: [],
      attempts: ['specialized:telecom:distributed'],
      originalName: originalOpts.name,
      crossValidationScore: distResult.confidence,
      validationStatus,
      discrepancyMeters: null,
      requiresManualReview: distResult.sharedLocation || distResult.confidence < 50,
      reviewReason: distResult.sharedLocation 
        ? `Ubicación compartida con: ${distResult.sharedWith?.join(', ') || 'otras infraestructuras'}`
        : distResult.confidence < 50 
          ? 'Baja confianza en geocodificación TELECOM'
          : undefined,
      sourcesConsulted: [{
        source: distResult.dataSource || 'OSM_OVERPASS',
        x: distResult.x,
        y: distResult.y,
        confidence: distResult.confidence,
        responseTimeMs: processingTimeMs,
      }],
    };
  }

  /**
   * Obtiene estadísticas de clasificación para un conjunto de nombres
   */
  public analyzeDataset(names: string[]): {
    byType: Record<string, number>;
    byConfidence: Record<string, number>;
    totalSpecializedCoverage: number;
    estimatedGeocodingSuccess: number;
  } {
    const classifications = names.map(name => this.classifier.classify(name));

    const byType: Record<string, number> = {};
    const byConfidence: Record<string, number> = {};
    let specializedCount = 0;

    classifications.forEach(c => {
      byType[c.type] = (byType[c.type] || 0) + 1;
      byConfidence[c.confidence] = (byConfidence[c.confidence] || 0) + 1;

      if (c.type !== InfrastructureType.GENERIC) {
        specializedCount++;
      }
    });

    const totalSpecializedCoverage = (specializedCount / names.length) * 100;
    
    // Con los nuevos geocodificadores, el éxito estimado es mayor:
    // - Especializados: ~85% éxito
    // - Genéricos con cascada completa: ~75% éxito
    const estimatedGeocodingSuccess = 
      (specializedCount * 0.85 + (names.length - specializedCount) * 0.75) / names.length * 100;

    return {
      byType,
      byConfidence,
      totalSpecializedCoverage,
      estimatedGeocodingSuccess
    };
  }

  // =========================================================================
  // P2.10: VALIDACIÓN CRUZADA CON FUENTES SECUNDARIAS
  // =========================================================================

  /**
   * P2.10b: Valida resultado primario contra fuentes secundarias.
   * 
   * PRINCIPIO: Singleton ≠ fuente única
   * Aunque solo haya 1 candidato, podemos validar sus coordenadas
   * contra NGA, Catastro R2, CartoCiudad para detectar errores.
   * 
   * PRIORIDAD: Calidad > Velocidad
   * 200-500ms extra es aceptable para detectar errores críticos
   * (ej: hospital ubicado 85km de su ubicación real).
   * 
   * FALLBACK: Si todas las fuentes secundarias fallan, devuelve
   * SINGLE_SOURCE con confianza real (no hardcoded 95).
   * 
   * @param primaryResult - Resultado del geocodificador primario
   * @param primarySource - Fuente del resultado primario (LOCAL_DERA, WFS_SPECIALIZED, etc.)
   * @param options - Opciones de orquestación original
   * @param classification - Clasificación tipológica
   * @returns Resultado de validación cruzada real
   */
  private async validateWithSecondarySources(
    primaryResult: GeocodingResult,
    primarySource: GeocodingSource,
    options: OrchestrationOptions,
    classification: ClassificationResult
  ): Promise<{
    crossValidationScore: number;
    validationStatus: ValidationStatus;
    discrepancyMeters: number | null;
    sourcesConsulted: SourceResult[];
    requiresManualReview: boolean;
    reviewReason?: string;
    coordinates?: { x: number; y: number };
  }> {
    const startTime = Date.now();
    const sourceResults: SourceResult[] = [];
    const errors: string[] = [];

    // 1. Agregar resultado primario
    sourceResults.push({
      source: primarySource,
      x: primaryResult.x,
      y: primaryResult.y,
      confidence: primaryResult.confidence,
      matchedName: primaryResult.matchedName,
      responseTimeMs: Date.now() - startTime,
    });

    // 2. Preparar consultas secundarias en paralelo
    const secondaryPromises: Array<Promise<SourceResult | null>> = [];
    const codINE = options.codMun || getCodigoINE(options.municipality) || '';

    // 2A. NGA Local (topónimos offline - ~5ms)
    if (this.ngaLocalGeocoder) {
      secondaryPromises.push(
        this.queryNGAForValidation(options.name, codINE, startTime)
          .catch(err => { errors.push(`nga_local: ${err}`); return null; })
      );
    }

    // 2B. Catastro R2 (2.4M parcelas - si hay dirección)
    if (this.catastroGeocoder && (options.address || options.name)) {
      secondaryPromises.push(
        this.queryCatastroR2ForValidation(options, codINE, startTime)
          .catch(err => { errors.push(`CATASTRO_R2: ${err}`); return null; })
      );
    }

    // 2C. CartoCiudad (IGN online - 200-500ms)
    if (this.cartoCiudadGeocoder) {
      secondaryPromises.push(
        this.queryCartoCiudadForValidation(options, startTime)
          .catch(err => { errors.push(`CARTOCIUDAD: ${err}`); return null; })
      );
    }

    // 3. Ejecutar consultas en paralelo con timeout
    const timeoutMs = 3000; // 3s máximo para validación secundaria
    const timeoutPromise = new Promise<(SourceResult | null)[]>(resolve => 
      setTimeout(() => resolve([]), timeoutMs)
    );

    const secondaryResults = await Promise.race([
      Promise.all(secondaryPromises),
      timeoutPromise
    ]);

    // 4. Agregar resultados secundarios válidos
    for (const result of secondaryResults) {
      if (result && result.x !== 0 && result.y !== 0 && result.confidence > 0) {
        sourceResults.push(result);
      }
    }

    // 5. Ejecutar validación cruzada
    if (sourceResults.length >= 2) {
      // Tenemos múltiples fuentes → validación cruzada real
      const validator = getCrossValidator();
      const validationResult = validator.validateEnhanced(
        sourceResults,
        classification.type
      );

      return {
        crossValidationScore: validationResult.compositeScore,
        validationStatus: validationResult.status,
        discrepancyMeters: validationResult.discrepancyMeters,
        sourcesConsulted: sourceResults,
        requiresManualReview: validationResult.requiresManualReview,
        reviewReason: validationResult.reviewReason,
        coordinates: validationResult.coordinates ?? undefined,
      };
    }

    // 6. FALLBACK: Solo 1 fuente disponible → SINGLE_SOURCE honesto
    // (No inventamos score=95, usamos la confianza real del geocodificador)
    console.log(
      `[P2.10] ⚠️ Validación SINGLE_SOURCE: ${primarySource} ` +
      `(${sourceResults.length} fuente, secundarias fallidas: ${errors.length})`
    );

    return {
      crossValidationScore: primaryResult.confidence,  // Real, no hardcoded
      validationStatus: 'SINGLE_SOURCE' as ValidationStatus,
      discrepancyMeters: null,
      sourcesConsulted: sourceResults,
      requiresManualReview: primaryResult.confidence < 85,
      reviewReason: primaryResult.confidence < 85 
        ? 'Fuente única sin validación cruzada' 
        : undefined,
    };
  }

  /** INECOLD: instante del último fallo de carga del INE R2 (0 = ninguno). */
  private ineFalloEn = 0;

  /**
   * INECOLD (19-sep-2026): espera la carga del INE R2 antes de pedir un centroide.
   * Si la carga falla se recuerda 60 s: con R2 caído, cada fila pagaría los
   * reintentos de `fetchWithBackoff` (≈2,5 s) en cada uno de los tres centroides.
   */
  private async esperarINE(ineService: { initialize: () => Promise<boolean> }): Promise<void> {
    if (this.ineFalloEn && Date.now() - this.ineFalloEn < 60_000) return;
    try {
      const ok = await ineService.initialize();
      this.ineFalloEn = ok === false ? Date.now() : 0;
    } catch {
      this.ineFalloEn = Date.now();
    }
  }

  /**
   * P2.10b Helper: Consulta NGA Local para validación secundaria
   */
  private async queryNGAForValidation(
    name: string,
    codINE: string,
    startTime: number
  ): Promise<SourceResult | null> {
    const result = await this.ngaLocalGeocoder.geocode(name, codINE, { 
      minConfidence: 50 
    });
    
    if (result && result.confidence >= 50) {
      return {
        source: 'nomenclator' as GeocodingSource,
        x: result.x,
        y: result.y,
        confidence: result.confidence,
        matchedName: result.matchedName,
        responseTimeMs: Date.now() - startTime,
      };
    }
    return null;
  }

  /**
   * P2.10b Helper: Consulta Catastro R2 para validación secundaria
   */
  private async queryCatastroR2ForValidation(
    options: OrchestrationOptions,
    codINE: string,
    startTime: number
  ): Promise<SourceResult | null> {
    const result = await this.catastroGeocoder.geocode({
      codINE,
      via: options.address,
      name: options.name,
      minConfidence: 50,
      maxResults: 1,
    });
    
    if (result && result.confidence >= 50) {
      return {
        source: 'catastro_ptel',
        x: result.x,
        y: result.y,
        confidence: result.confidence,
        matchedName: result.matchedName,
        responseTimeMs: Date.now() - startTime,
      };
    }
    return null;
  }

  /**
   * P2.10b Helper: Consulta CartoCiudad para validación secundaria
   */
  private async queryCartoCiudadForValidation(
    options: OrchestrationOptions,
    startTime: number
  ): Promise<SourceResult | null> {
    const address = options.address 
      ? `${options.address}, ${options.municipality}`
      : `${options.name}, ${options.municipality}`;
    
    const result = await this.cartoCiudadGeocoder.geocode({
      address,
      municipality: options.municipality,
      province: options.province,
    });
    
    if (result.success && result.data) {
      // Guard cross-muniCode (OBS-FT-XVAL-CARTO-CROSS-MUNI-DRIFT-GENERIC):
      // descartar respuestas con muniCode_carto != codINE esperado.
      const codigoINEEsperado = options.codMun || getCodigoINE(options.municipality) || undefined;
      const acepta = acceptCartoCiudadResult(
        {
          muni: result.data.municipality,
          muniCode: result.data.muniCode,
          province: result.data.province,
          lat: result.data.y,
          lng: result.data.x,
        },
        options.municipality,
        options.province,
        codigoINEEsperado
      );
      if (!acepta) {
        return null;
      }

      return {
        source: 'cartociudad' as GeocodingSource,
        x: result.data.x,
        y: result.data.y,
        confidence: result.data.confidence || 70,
        matchedName: result.data.address,
        responseTimeMs: Date.now() - startTime,
      };
    }
    return null;
  }

  // =========================================================================
  // F023 FASE 2.2: CONSULTAS PARALELAS MULTI-FUENTE
  // =========================================================================

  /**
   * Ejecuta consultas a múltiples geocodificadores en paralelo.
   * 
   * Fuentes consultadas:
   * - WFS Especializado (según tipología)
   * - CartoCiudad (IGN)
   * - CDAU (Callejero Andalucía)
   * 
   * @returns Array de resultados exitosos con metadatos de fuente
   */
  private async queryParallelSources(
    options: {
      name: string;
      municipality: string;
      province?: string;
      address?: string;
      type: InfrastructureType;
      codMun?: string;
      timeoutMs?: number;
      /** OPT-03: confianza actual de Waves 1+2 para activar fuentes extra */
      currentConfidence?: number;
      /**
       * MJ-04: Si true, omite CDAU WFS online en el paralelo.
       * Se activa cuando oleada 2 ya resolvió con cdau_r2 O el municipio
       * tiene < 3.000 portales CDAU (municipio pequeño → WFS online inútil).
       */
      skipCdauOnline?: boolean;
      /**
       * UPCLS3 eslabón 40 (γ híbrido): sub-disc para `InfrastructureType.EMERGENCY`.
       * Procede de `classification.subtype` upstream y se reenvía a
       * `trySpecializedGeocoder` interno para que el WFS paralelo enrute a
       * SOCORRO (G12_37) o HUMANITARIA (G12_36) cuando corresponda.
       */
      subtype?: 'SOCORRO' | 'HUMANITARIA';
      /** CENTOR: puerta del centroide municipal en el WFS paralelo. */
      hasDocumentCoords?: boolean;
    }
  ): Promise<{
    results: SourceResult[];
    errors: Array<{ source: string; error: GeocodingError }>;
    totalTimeMs: number;
    /** CENT76: resultado completo del WFS especializado, si lo hubo. */
    wfsEspecializado?: { result: GeocodingResult; geocoder: string } | null;
  }> {
    const startTime = Date.now();
    const timeoutMs = options.timeoutMs ?? 8000; // 8s default
    const errors: Array<{ source: string; error: GeocodingError }> = [];

    // Preparar opciones de búsqueda WFS
    const wfsOptions: WFSSearchOptions = {
      name: options.name,
      municipality: options.municipality,
      province: options.province,
      maxResults: 5
    };

    // F9.01a-fix.2: AbortController por servicio (en vez de global único)
    // Permite abortar selectivamente si CB abre durante el paralelo
    const serviceAborts = new Map<string, AbortController>();
    const globalTimeout = setTimeout(() => {
      for (const ctrl of serviceAborts.values()) ctrl.abort();
    }, timeoutMs);

    /** Envuelve promise con abort signal por servicio */
    const withServiceAbort = <T>(serviceKey: string, promise: Promise<T>): Promise<T> => {
      const ctrl = new AbortController();
      serviceAborts.set(serviceKey, ctrl);
      return Promise.race([
        promise,
        new Promise<never>((_, reject) => {
          ctrl.signal.addEventListener('abort', () =>
            reject(new DOMException(`${serviceKey} aborted`, 'AbortError')), { once: true });
        })
      ]);
    };

    // ===== DEFINIR PROMESAS PARALELAS =====
    // F9.01a-fix.1: circuit breaker guards para servicios online
    const queries: Array<{ source: GeocodingSource; promise: Promise<GeocodingResult | null> }> = [];
    /** CENT76: resultado completo del WFS especializado (ver su promesa). */
    const wfsEspecializado: { valor: { result: GeocodingResult; geocoder: string } | null } = { valor: null };

    // 1. WFS Especializado
    if (this.circuitBreaker.isAvailable('WFS_SPECIALIZED')) {
      queries.push({
        source: 'wfs_dera' as GeocodingSource,
        promise: withServiceAbort('WFS_SPECIALIZED',
          this.trySpecializedGeocoder(options.type, wfsOptions, [], options.subtype, options.hasDocumentCoords).then(r => {
            // CENT76: se guarda el resultado entero; el `SourceResult` de abajo
            // solo lleva coordenada y confianza, y sin `matchType`, fuente ni
            // geocodificador un centroide municipal deja de reconocerse.
            wfsEspecializado.valor = r.result ? { result: r.result, geocoder: r.geocoder } : null;
            return r.result;
          })
        )
      });
    }

    // 2. CartoCiudad (TD-009: extraer datos de TypedResult)
    if (this.circuitBreaker.isAvailable('CARTOCIUDAD')) {
      queries.push({
        source: 'cartociudad' as GeocodingSource,
        promise: withServiceAbort('CARTOCIUDAD',
          this.cartoCiudadGeocoder.geocode({
            address: options.address 
              ? `${options.address}, ${options.municipality}`
              : `${options.name}, ${options.municipality}`,
            municipality: options.municipality,
            province: options.province
          }).then(r => {
            // Guard cross-muniCode (OBS-FT-XVAL-CARTO-CROSS-MUNI-DRIFT-GENERIC):
            // descartar respuestas con muniCode_carto != codINE esperado antes
            // de competir en la carrera multi-fuente.
            if (r.success && r.data) {
              const codigoINEEsperado = options.codMun || getCodigoINE(options.municipality) || undefined;
              const acepta = acceptCartoCiudadResult(
                {
                  muni: r.data.municipality,
                  muniCode: r.data.muniCode,
                  province: r.data.province,
                  lat: r.data.y,
                  lng: r.data.x,
                },
                options.municipality,
                options.province,
                codigoINEEsperado
              );
              if (!acepta) {
                return null;
              }
            }
            return this.extractLegacyResult(r);
          })
        )
      });
    }

    // 3. CDAU online (TD-009: extraer datos de TypedResult)
    // MJ-04: omitir si el caller indicó skipCdauOnline (R2 ya resolvió o municipio pequeño)
    if (!options.skipCdauOnline && this.circuitBreaker.isAvailable('CDAU_ONLINE')) {
      queries.push({
        source: 'cdau' as GeocodingSource,
        promise: withServiceAbort('CDAU_ONLINE',
          this.cdauGeocoder.geocode({
            street: options.address || options.name,
            municipality: options.municipality,
            province: options.province
          }).then(r => this.extractLegacyResult(r))
        )
      });
    }

    // ===== OPT-03: WAVE 3 EXPANSION (6 fuentes) =====
    // Fuentes extra solo si Waves 1+2 no alcanzaron ≥75% confianza
    const WAVE3_CONFIDENCE_THRESHOLD = 75;
    if ((options.currentConfidence ?? 0) < WAVE3_CONFIDENCE_THRESHOLD) {
      console.log(
        `[OPT-03] Wave 3 expandido: conf=${options.currentConfidence ?? 0}% < ${WAVE3_CONFIDENCE_THRESHOLD}%, activando IDEE+Nominatim+Catastro`
      );

      // 4. IDEE - API OGC Features del IGN (L4)
      // FASE-A-C2: envuelto en withServiceAbort (oleada homogénea) + registro
      // del breaker sobre el desenlace REAL del servicio en la promesa interna
      // (inmune a aborts: el AbortError de la race no cuenta como fallo de IDEE).
      if (this.circuitBreaker.isAvailable('IDEE')) {
        queries.push({
          source: 'idee' as GeocodingSource,
          promise: withServiceAbort('IDEE',
            this.ideeGeocoder.geocode({
              query: options.name,
              municipality: options.municipality,
              province: options.province
            }).then(
              r => {
                if (r.success) this.circuitBreaker.recordSuccess('IDEE');
                else this.circuitBreaker.recordFailure('IDEE');
                return this.extractLegacyResult(r);
              },
              err => {
                this.circuitBreaker.recordFailure('IDEE');
                throw err;
              }
            )
          )
        });
      }

      // 5. Nominatim - OpenStreetMap (L5, último recurso)
      // FASE-A-C2: mismo tratamiento que IDEE — envoltorio + registro simétrico
      // (la rama Wave 3 no registraba pese a hacerlo los niveles L7/pedanía).
      if (this.circuitBreaker.isAvailable('NOMINATIM')) {
        queries.push({
          source: 'nominatim' as GeocodingSource,
          promise: withServiceAbort('NOMINATIM',
            this.nominatimGeocoder.geocode({
              query: options.address
                ? `${options.address}, ${options.municipality}`
                : `${options.name}, ${options.municipality}`,
              municipality: options.municipality,
              province: options.province
            }).then(
              r => {
                if (r.success) this.circuitBreaker.recordSuccess('NOMINATIM');
                else this.circuitBreaker.recordFailure('NOMINATIM');
                return this.extractLegacyResult(r);
              },
              err => {
                this.circuitBreaker.recordFailure('NOMINATIM');
                throw err;
              }
            )
          )
        });
      }

      // NOTA: CatastroService NO incluido - requiere referencia catastral,
      // no soporta búsqueda genérica. Catastro ya cubierto en Wave 2 (CatastroR2).
    }

    // ===== EJECUTAR EN PARALELO =====
    const settlements = await Promise.allSettled(
      queries.map(q => q.promise)
    );

    clearTimeout(globalTimeout);

    // ===== PROCESAR RESULTADOS =====
    const results: SourceResult[] = [];
    const endTime = Date.now();

    for (let i = 0; i < settlements.length; i++) {
      const settlement = settlements[i];
      const queryInfo = queries[i];

      if (settlement.status === 'fulfilled' && settlement.value) {
        const geocodingResult = settlement.value;
        
        // Validar coordenadas dentro de Andalucía
        if (!isValidAndaluciaCoordinate(geocodingResult.x, geocodingResult.y)) {
          continue; // Skip resultado fuera de Andalucía
        }
        
        // ===== B05: VALIDACIÓN INE + LÍMITES MUNICIPALES =====
        // Detecta errores como Colomera (18051) vs Colomers (17055) = 750 km error
        let validatedConfidence = geocodingResult.confidence;
        
        if (options.codMun) {
          const b05Errors: string[] = [];
          const validation = await validateGeocodingResult(
            geocodingResult,
            options.codMun,
            b05Errors
          );
          
          if (!validation.isValid) {
            // Resultado rechazado por validación B05
            console.warn(
              `[B05] ❌ ${queryInfo.source} rechazado: ${validation.reason}`
            );
            errors.push({ 
              source: queryInfo.source, 
              error: wrapError(
                new Error(`B05: ${validation.reason || 'Validación INE/límites fallida'}`),
                { source: queryInfo.source, query: options.name, municipio: options.municipality }
              )
            });
            continue; // Skip este resultado
          }
          
          // Usar confianza ajustada (posiblemente degradada por proximidad a límite)
          validatedConfidence = validation.confidence;
          
          if (validatedConfidence < geocodingResult.confidence) {
            console.log(
              `[B05] ⚠️ ${queryInfo.source}: confianza degradada ${geocodingResult.confidence}% → ${validatedConfidence}%`
            );
          }
        }
        
        // F9.01a-fix.1: Registrar éxito servicio online
        const cbKey = queryInfo.source === 'cdau' ? 'CDAU_ONLINE' : queryInfo.source;
        this.circuitBreaker.recordSuccess(cbKey);

        results.push({
          source: queryInfo.source,
          x: geocodingResult.x,
          y: geocodingResult.y,
          confidence: validatedConfidence,
          matchedName: geocodingResult.matchedName,
          responseTimeMs: endTime - startTime
        });
      } else if (settlement.status === 'rejected') {
        // F9.01a-fix.1: Registrar fallo servicio online
        const cbKey = queryInfo.source === 'cdau' ? 'CDAU_ONLINE' : queryInfo.source;
        this.circuitBreaker.recordFailure(cbKey);

        const wrappedError = wrapError(settlement.reason, {
          source: queryInfo.source,
          query: options.name,
          municipio: options.municipality
        });
        errors.push({ source: queryInfo.source, error: wrappedError });
      }
    }

    console.log(
      `[F023-2.2] Consultas paralelas: ${results.length}/${queries.length} exitosas ` +
      `(${endTime - startTime}ms)`
    );

    return {
      results,
      errors,
      totalTimeMs: endTime - startTime,
      wfsEspecializado: wfsEspecializado.valor,
    };
  }

  // isValidAndaluciaCoordinate: importado de ./orchestrator

  /**
   * Limpia cachés de todos los geocodificadores
   */
  public clearAllCaches(): void {
    // Especializados WFS
    this.healthGeocoder.clearCache();
    // WECB 26-may: caché de centros educativos migrada a EduCentersR2Service
    // (WFSEducationGeocoder deprecado — CKAN muerto).
    getEduCentersR2Service().clearCache();
    this.culturalGeocoder.clearCache();
    this.securityGeocoder.clearCache();
    this.hydraulicGeocoder.clearCache();
    this.energyGeocoder.clearCache();
    this.sportsGeocoder.clearCache();
    this.industrialGeocoder.clearCache();
    this.maremotosGeocoder.clearCache();
    this.puestoSocorroGeocoder.clearCache();
    this.organizacionesHumanitariasGeocoder.clearCache();
    
    // Nuevos geocodificadores
    this.ngaGeocoder.clearCache();
    this.overpassGeocoder.clearCache();
    this.torresComGeocoder.clearCache();
    this.gasolinerasGeocoder.clearCache();
    this.waterwaysGeocoder.clearCache();
    
    // OPT-03: Wave 3 geocoders
    this.ideeGeocoder.clearCache();
    this.nominatimGeocoder.clearCache();
    
    // Genéricos
    this.cdauGeocoder.clearCache();
    this.cartoCiudadGeocoder.clearCache();
    
    console.log('✅ Cachés de todos los geocodificadores limpiados');
  }

  /**
   * Obtiene estadísticas de todos los geocodificadores
   */
  public getAllStats() {
    return {
      specialized: {
        health: this.healthGeocoder.getStats(),
        // WECB 26-may: `education` retirada — WFSEducationGeocoder deprecado.
        // EduCentersR2Service no expone getStats() equivalente.
        cultural: this.culturalGeocoder.getStats(),
        security: this.securityGeocoder.getStats(),
        hydraulic: this.hydraulicGeocoder.getStats(),
        energy: this.energyGeocoder.getStats(),
        sports: this.sportsGeocoder.getStats(),
        industrial: this.industrialGeocoder.getStats(),
        maremotos: this.maremotosGeocoder.getStats(),
        puestoSocorro: this.puestoSocorroGeocoder.getStats(),
        organizacionesHumanitarias: this.organizacionesHumanitariasGeocoder.getStats()
      },
      phaseB: {
        nga: this.ngaGeocoder.getStats(),
        overpass: { cacheSize: 0, endpoint: 'overpass-api.de', layer: 'osm' },
        torresCom: this.torresComGeocoder.getStats(),
        gasolineras: this.gasolinerasGeocoder.getStats(),
        waterways: this.waterwaysGeocoder.getStats(),
      },
      wave3: {
        idee: this.ideeGeocoder.getStats(),
        nominatim: this.nominatimGeocoder.getStats(),
      },
      generic: {
        cdau: this.cdauGeocoder.getStats(),
        cartociudad: this.cartoCiudadGeocoder.getStats()
      }
    };
  }

  /**
   * Resetea estadísticas de todos los geocodificadores
   */
  public resetAllStats(): void {
    this.cdauGeocoder.resetStats();
    this.cartoCiudadGeocoder.resetStats();
    console.log('✅ Estadísticas reseteadas');
  }
}
